// ============================================================================
// VEDANGJYOTISHAM v5.13 — INTEGRATED PERSONALITY & LIFE PROFILE GENERATOR
// Deep Astrological Synthesis Engine: Cross-Factor Interaction & Multi-Kundali Differentiation
// ============================================================================

import { CompleteKundali, PlanetPosition, HouseInfo } from '../types/kundali';
import { calculateShadbala, getBirthUtcDate, getSavByBhava } from './vedicEngine';

export interface ProfileSection {
  id: string;
  title: string;
  iconName: string;
  content: string;
}

export interface IntegratedProfileResult {
  disclosure: string;
  sections: ProfileSection[];
  wordCount: number;
  activeDashaSummary: string;
  remedyVerdict: {
    required: boolean;
    text: string;
  };
  personalitySignature?: PersonalitySignature;
}

// ----------------------------------------------------------------------------
// Personality Signature & Life Area Deep Dive Architecture
// ----------------------------------------------------------------------------

export interface PersonalitySignature {
  lagnaArchetype: {
    sign: string;
    lord: string;
    element: ElementType;
    presenceEn: string;
    presenceHi: string;
    archetypeEn: string;
    archetypeHi: string;
  };
  mindArchetype: {
    nakshatra: string;
    pada: number;
    instinctEn: string;
    instinctHi: string;
    cognitiveStyleEn: string;
    cognitiveStyleHi: string;
  };
  emotionalCore: {
    moonSign: string;
    element: ElementType;
    receptivityEn: string;
    receptivityHi: string;
  };
  dominantExecutiveEngine: {
    planet: string;
    rupas: number;
    strengthOveruseEn: string;
    strengthOveruseHi: string;
  };
  fundamentalVulnerability: {
    planet: string;
    rupas: number;
    blindspotEn: string;
    blindspotHi: string;
  };
  corePolarityDynamic: {
    polarityEn: string;
    polarityHi: string;
  };
  maturationArc: {
    arcEn: string;
    arcHi: string;
  };
  nativeAge: number | null;
  isYoungChild: boolean;
}

export type LifeArea =
  | 'career'
  | 'wealth'
  | 'marriage'
  | 'progeny'
  | 'health'
  | 'property'
  | 'education'
  | 'foreignTravel'
  | 'spirituality';

export interface LifeAreaFactorMap {
  area: LifeArea;
  nameEn: string;
  nameHi: string;
  primaryBhavas: number[];
  secondaryBhavas: number[];
  primaryKarakas: string[];
  secondaryKarakas: string[];
  relevantDivisionalCharts: string[];
  relevantYogaGroups: string[];
  timingFactors: {
    dashaKeyHouses: number[];
    dashaKeyPlanets: string[];
    transitTriggers: string[];
  };
  personalitySynthesisFocus: {
    inheritsLagnaTraitEn: string;
    inheritsLagnaTraitHi: string;
    inheritsMoonTraitEn: string;
    inheritsMoonTraitHi: string;
    inheritsDominantEngineTraitEn: string;
    inheritsDominantEngineTraitHi: string;
    appliesWeakestVulnerabilityEn: string;
    appliesWeakestVulnerabilityHi: string;
  };
}

export interface ExtractedLifeAreaFactors {
  area: LifeArea;
  factorMap: LifeAreaFactorMap;
  primaryHousesData: Array<{
    houseNumber: number;
    sign: string;
    signLord: string;
    savBindus: number;
    occupants: string[];
  }>;
  secondaryHousesData: Array<{
    houseNumber: number;
    sign: string;
    signLord: string;
    savBindus: number;
    occupants: string[];
  }>;
  karakaStrengths: Array<{
    planet: string;
    rupas: number;
    isStrong: boolean;
  }>;
  activeDashaRelation: {
    mahadashaLord: string;
    antardashaLord?: string;
    isPrimaryKaraka: boolean;
    activatesHouse: boolean;
    activatedHouseNumbers: number[];
  };
  inheritedPersonalityContext: {
    lagnaSign: string;
    lagnaLord: string;
    moonSign: string;
    moonNakshatra: string;
    dominantPlanet: string;
    weakestPlanet: string;
    vulnerabilityFocusEn: string;
    vulnerabilityFocusHi: string;
  };
}

export interface LifeAreaDeepDiveResult {
  area: LifeArea;
  title: string;
  astrologicalLens: string;
  baselineCapacity: string;
  activeTimingInfluence: string;
  riskMitigation: string;
  actionRoadmap: {
    directives: string[];
    remedialFocus: string;
  };
  extractedFactors?: ExtractedLifeAreaFactors;
}

// ----------------------------------------------------------------------------
// Astrological Knowledge Representations for True Multi-Factor Synthesis
// ----------------------------------------------------------------------------

type ElementType = 'Fire' | 'Earth' | 'Air' | 'Water';
type ModalityType = 'Movable' | 'Fixed' | 'Dual';

interface SignSynthesisProfile {
  element: ElementType;
  modality: ModalityType;
  ruler: string;
  archetypeEn: string;
  archetypeHi: string;
  presenceEn: string;
  presenceHi: string;
  decisionCoreEn: string;
  decisionCoreHi: string;
  emotionalCoreEn: string;
  emotionalCoreHi: string;
  workDriveEn: string;
  workDriveHi: string;
  socialStyleEn: string;
  socialStyleHi: string;
}

const SIGN_PROFILES: Record<string, SignSynthesisProfile> = {
  Aries: {
    element: 'Fire',
    modality: 'Movable',
    ruler: 'Mars',
    archetypeEn: 'pioneering initiator',
    archetypeHi: 'पहल करने वाले और ऊर्जावान पथप्रदर्शक',
    presenceEn: 'direct, alert, dynamic, and quick to take initiative without waiting for others to pave the way',
    presenceHi: 'ऊर्जावान, तत्पर, स्पष्टवादी और बिना किसी झिझक के स्वाभाविक पहल करने वाले',
    decisionCoreEn: 'action-oriented and instinctive, preferring to tackle challenges head-on and course-correct on the go',
    decisionCoreHi: 'तुरंत कदम उठाने और प्रत्यक्ष अनुभव से सीखकर परिस्थितियों का सामना करने वाली',
    emotionalCoreEn: 'passionate, candid, and quick to clear the air, preferring transparent directness over simmering tension',
    emotionalCoreHi: 'उत्साही, खरी और मन में बात दबाए बिना तुरंत बात स्पष्ट कर देने वाली',
    workDriveEn: 'high-velocity execution, independent autonomy, and an appetite for pioneering projects that break new ground',
    workDriveHi: 'तेज गति से काम निपटाने, स्वतंत्रता से काम करने और नई चुनौतियों का नेतृत्व करने वाली',
    socialStyleEn: 'spirited, authentic, and outspoken, with little tolerance for artificial social pretense',
    socialStyleHi: 'स्पष्ट, मुखर और दिखावे से पूरी तरह दूर रहने वाली'
  },
  Taurus: {
    element: 'Earth',
    modality: 'Fixed',
    ruler: 'Venus',
    archetypeEn: 'grounded builder',
    archetypeHi: 'धैर्यवान, स्थिर और सुदृढ़ निर्माणकर्ता',
    presenceEn: 'calm, patient, unhurried, and radiating a quiet, dependable stability that anchors those around you',
    presenceHi: 'धैर्यवान, शांत, गंभीर और आसपास के लोगों को सुरक्षा व स्थिरता का अहसास कराने वाले',
    decisionCoreEn: 'methodical and deeply practical, weighing tangible utility and long-term durability before committing',
    decisionCoreHi: 'सुविचारित और व्यावहारिक, जो हर निर्णय के ठोस परिणामों और स्थायित्व को पहले परखती है',
    emotionalCoreEn: 'deeply loyal, steady, and protective, processing feelings slowly and cherishing lasting security',
    emotionalCoreHi: 'अटूट निष्ठावान, गंभीर और शांत, जो भावनाओं को गहराई से महसूस करती है और सुरक्षा को महत्व देती है',
    workDriveEn: 'sustained persistence, methodical craftsmanship, and building assets that compound in value over decades',
    workDriveHi: 'निरंतर लगन, व्यवस्थित कार्यकुशलता और समय के साथ मजबूत होने वाली स्थायी उपलब्धियों के निर्माण वाली',
    socialStyleEn: 'warm, trustworthy, and selective, creating a peaceful, hospitable atmosphere for true confidants',
    socialStyleHi: 'स्नेहपूर्ण, भरोसेमंद और अपने करीबी दायरे में शांति व सौहार्द पसंद करने वाली'
  },
  Gemini: {
    element: 'Air',
    modality: 'Dual',
    ruler: 'Mercury',
    archetypeEn: 'versatile intellectual',
    archetypeHi: 'जिज्ञासु, बहुआयामी और वाकपटु विचारक',
    presenceEn: 'mentally alert, articulate, curious, and effortlessly connecting disparate ideas and people',
    presenceHi: 'मानसिक रूप से सजग, वाकपटु, विचारशील और नए विचारों को तेजी से ग्रहण करने वाले',
    decisionCoreEn: 'multi-perspectival and adaptable, examining problems from multiple angles before finding the most agile solution',
    decisionCoreHi: 'लचीली और बहुआयामी, जो विभिन्न दृष्टिकोणों और तथ्यों का विश्लेषण करके सबसे चतुर समाधान ढूंढती है',
    emotionalCoreEn: 'inquisitive, reflective, and needing mental engagement and honest conversation to feel emotionally safe',
    emotionalCoreHi: 'विचारशील और संवाद-प्रिय, जिसे मानसिक तालमेल और खुली बातचीत से सुकून मिलता है',
    workDriveEn: 'rapid synthesis of information, strategic communication, and flourishing in varied, fast-moving assignments',
    workDriveHi: 'सूचनाओं के त्वरित विश्लेषण, संवाद कौशल और विविधतापूर्ण गतिशील कार्यों में उत्कृष्ट प्रदर्शन वाली',
    socialStyleEn: 'witty, engaging, and lively, bridging different social circles through conversational flair',
    socialStyleHi: 'रोचक, मिलनसार और अपनी बातचीत से विभिन्न प्रकार के लोगों को जोड़ने वाली'
  },
  Cancer: {
    element: 'Water',
    modality: 'Movable',
    ruler: 'Moon',
    archetypeEn: 'intuitive protector',
    archetypeHi: 'संवेदनशील, अंतर्ज्ञानी और रक्षात्मक संरक्षक',
    presenceEn: 'perceptive, empathetic, dignified, and possessing a deep emotional radar that immediately reads the room',
    presenceHi: 'गहरी समझ वाले, संवेदनशील, मर्यादित और माहौल के भीतरी स्पंदन को तुरंत भांप लेने वाले',
    decisionCoreEn: 'guided by seasoned intuitive instincts and a deep concern for the emotional and practical welfare of loved ones',
    decisionCoreHi: 'तीव्र अंतर्ज्ञान और अपनों की वास्तविक भलाई व सुरक्षा को केंद्र में रखकर निर्णय लेने वाली',
    emotionalCoreEn: 'deeply reflective, rich in memory, and needing a safe, harmonious sanctum to recharge from worldly friction',
    emotionalCoreHi: 'अत्यंत संवेदनशील, गहरी यादों से युक्त और बाहरी तनाव से खुद को दोबारा ऊर्जावान बनाने के लिए शांत एकांत चाहने वाली',
    workDriveEn: 'protective stewardship, building loyal teams, and excelling where empathy and strategic foresight merge',
    workDriveHi: 'समर्पण भाव, समर्पित टीम निर्माण और जहां मानवीय संवेदना व रणनीतिक सूझबूझ का मेल हो वहां सफलता पाने वाली',
    socialStyleEn: 'caring and selective, keeping a close-knit circle where trust is earned and preserved with fierce loyalty',
    socialStyleHi: 'स्नेहशील और चुनिंदा, जो केवल उन्हीं को करीब आने देती है जिन्होंने अपनी विश्वसनीयता सिद्ध की हो'
  },
  Leo: {
    element: 'Fire',
    modality: 'Fixed',
    ruler: 'Sun',
    archetypeEn: 'noble sovereign',
    archetypeHi: 'स्वाभिमानी, गरिमापूर्ण और स्वाभाविक नेता',
    presenceEn: 'commanding, warm, noble, and carrying an unmistakable aura of natural authority and personal dignity',
    presenceHi: 'प्रभावशाली, उदार, स्वाभिमानी और स्वाभाविक गरिमा व नेतृत्व की चमक से युक्त',
    decisionCoreEn: 'values-driven, principled, and confident, holding yourself to high standards of personal honor',
    decisionCoreHi: 'उच्च नैतिक मूल्यों और सिद्धांतों पर आधारित, जो स्वयं जिम्मेदारी लेने में पूरा आत्मविश्वास रखती है',
    emotionalCoreEn: 'generous, proud, and wholehearted, seeking sincere respect and mutual admiration in personal bonds',
    emotionalCoreHi: 'उदार, आत्मसम्मान से भरपूर और रिश्तों में निष्कपट सम्मान व सराहना चाहने वाली',
    workDriveEn: 'visionary leadership, high-visibility responsibility, and inspiring others by setting an exemplary standard',
    workDriveHi: 'दूरदर्शी नेतृत्व, बड़ी जिम्मेदारियों का निर्वहन और अपने आचरण से दूसरों के लिए मिसाल पेश करने वाली',
    socialStyleEn: 'magnanimous, encouraging, and protective, acting as a natural benefactor to your community',
    socialStyleHi: 'उदार, उत्साहवर्धक और अपने सहयोगियों व परिचितों को संबल व मार्गदर्शन देने वाली'
  },
  Virgo: {
    element: 'Earth',
    modality: 'Dual',
    ruler: 'Mercury',
    archetypeEn: 'master craftsman',
    archetypeHi: 'सटीक, कर्तव्यनिष्ठ और सूक्ष्म विश्लेषक',
    presenceEn: 'observant, modest, precise, and immediately noting the operational details that others overlook',
    presenceHi: 'सूक्ष्मदर्शी, कर्तव्यनिष्ठ, विनम्र और उन बारीकियों को तुरंत पहचान लेने वाले जिन्हें दूसरे अनदेखा कर देते हैं',
    decisionCoreEn: 'evidence-based, systematic, and diagnostic, seeking practical optimization and error-free execution',
    decisionCoreHi: 'तथ्यों पर आधारित, क्रमबद्ध और व्यावहारिक, जो हर काम में सटीकता और उपयोगिता को सर्वोच्च रखती है',
    emotionalCoreEn: 'thoughtful, reserved, and expressing care through tangible acts of service and helpful problem-solving',
    emotionalCoreHi: 'विचारशील, गंभीर और बातों के बजाय व्यावहारिक मदद और समस्याओं को हल करके परवाह जताने वाली',
    workDriveEn: 'exacting quality control, analytical rigor, and turning chaotic workflows into streamlined, reliable systems',
    workDriveHi: 'उच्च गुणवत्ता मानक, विश्लेषणात्मक दक्षता और बिखरी हुई व्यवस्था को सुव्यवस्थित रूप देने वाली',
    socialStyleEn: 'helpful, polite, and considerate, valuing substantive conversations over noisy social gatherings',
    socialStyleHi: 'सहयोगी, शिष्ट और सतही भीड़-भाड़ के मुकाबले अर्थपूर्ण बातचीत को प्राथमिकता देने वाली'
  },
  Libra: {
    element: 'Air',
    modality: 'Movable',
    ruler: 'Venus',
    archetypeEn: 'diplomatic harmonizer',
    archetypeHi: 'संतुलित, न्यायप्रिय और सामंजस्य स्थापित करने वाले',
    presenceEn: 'graceful, courteous, poised, and instinctively seeking fair equilibrium in every interpersonal encounter',
    presenceHi: 'सौम्य, सुरुचिपूर्ण, संतुलित और हर बातचीत में सहज संतुलन व निष्पक्षता बनाए रखने वाले',
    decisionCoreEn: 'judicious and consensus-oriented, carefully weighing opposing viewpoints to arrive at a just compromise',
    decisionCoreHi: 'न्यायप्रिय और संतुलित, जो दोनों पक्षों को गंभीरता से तौलकर सबसे न्यायसंगत मार्ग चुनती है',
    emotionalCoreEn: 'relational, refined, and yearning for aesthetic harmony and peaceful partnership without harsh conflict',
    emotionalCoreHi: 'शांतिप्रिय, सुरुचिपूर्ण और रिश्तों में बिना किसी कटुता के सच्चा सामंजस्य व आत्मीयता चाहने वाली',
    workDriveEn: 'strategic mediation, partnership building, creative design, and refining collective working relationships',
    workDriveHi: 'रणनीतिक मध्यस्थता, मजबूत साझेदारियों का निर्माण और कार्यस्थल पर सहयोग का सौहार्दपूर्ण माहौल बनाने वाली',
    socialStyleEn: 'charming, tactful, and diplomatic, with an innate gift for easing tension and building bridges',
    socialStyleHi: 'मिलनसार, शिष्ट और तनावपूर्ण माहौल को भी अपनी सौम्यता से शांत कर देने वाली'
  },
  Scorpio: {
    element: 'Water',
    modality: 'Fixed',
    ruler: 'Mars',
    archetypeEn: 'strategic transformer',
    archetypeHi: 'गहन, एकाग्र, दूरदर्शी और रणनीतिकार',
    presenceEn: 'penetrating, quiet, intense, and possessing an unyielding inner focus that commands unspoken respect',
    presenceHi: 'गंभीर, एकाग्र, शांत और बिना किसी दिखावे के भी अपने गहरे प्रभाव का अहसास कराने वाले',
    decisionCoreEn: 'deeply strategic and piercing, looking straight past external facade to uncover core motivations and hidden dynamics',
    decisionCoreHi: 'गहन और रणनीतिक, जो सतह के दिखावे को चीरकर असली कारणों और छिपी मंशाओं को तुरंत भांप लेती है',
    emotionalCoreEn: 'profoundly passionate, guarded, and fiercely loyal, protecting emotional vulnerabilities behind strong inner walls',
    emotionalCoreHi: 'अत्यंत संवेदनशील लेकिन आत्म-नियंत्रित, जो अपनी भावनाओं की रक्षा करती है और अटूट निष्ठा निभाती है',
    workDriveEn: 'relentless investigative depth, crisis management, and mastering complex undertakings through total dedication',
    workDriveHi: 'गहन शोध व अन्वेषण क्षमता, संकट के समय अचूक निर्णय और पूरी एकाग्रता से कठिन लक्ष्यों को साधने वाली',
    socialStyleEn: 'discerning, private, and fiercely loyal to an intimate circle while keeping superficial contacts at a polite distance',
    socialStyleHi: 'सतर्क, निजी और गिने-चुने लोगों के प्रति समर्पित, जबकि औपचारिक संपर्कों से मर्यादित दूरी बनाए रखने वाली'
  },
  Sagittarius: {
    element: 'Fire',
    modality: 'Dual',
    ruler: 'Jupiter',
    archetypeEn: 'visionary philosopher',
    archetypeHi: 'दूरदर्शी, आशावादी और ज्ञान के अन्वेषक',
    presenceEn: 'optimistic, candid, broad-minded, and radiating an inspiring enthusiasm for life, wisdom, and expansive horizons',
    presenceHi: 'आशावादी, खुले विचारों वाले, निष्कपट और जीवन के प्रति एक सकारात्मक व प्रेरक उत्साह जगाने वाले',
    decisionCoreEn: 'principle-led and forward-looking, prioritizing ethical purpose, broad philosophical vision, and long-term meaning',
    decisionCoreHi: 'सिद्धांतवादी और दूरगामी सोच से युक्त, जो नैतिक मूल्यों और जीवन के व्यापक उद्देश्य को सबसे आगे रखती है',
    emotionalCoreEn: 'generous, freedom-loving, and resilient, bouncing back from setbacks with renewed faith and purpose',
    emotionalCoreHi: 'उदार, स्वतंत्र चेतना वाली और कठिन समय से भी नए आत्मविश्वास के साथ बाहर निकलने वाली',
    workDriveEn: 'strategic expansion, guiding and mentoring others, enterprise leadership, and pursuing meaningful macro goals',
    workDriveHi: 'व्यापक रणनीति निर्माण, दूसरों को मार्गदर्शन देने और बड़े व अर्थपूर्ण लक्ष्यों को साकार करने वाली',
    socialStyleEn: 'honest, warm, and inspiring, sharing wisdom and humor with an open, welcoming heart',
    socialStyleHi: 'सच्ची, उत्साही और बिना किसी संकोच के ज्ञान, सकारात्मकता और हास्य बांटने वाली'
  },
  Capricorn: {
    element: 'Earth',
    modality: 'Movable',
    ruler: 'Saturn',
    archetypeEn: 'architect of legacy',
    archetypeHi: 'कर्मठ, अनुशासित और सुदृढ़ निर्माता',
    presenceEn: 'composed, pragmatic, self-disciplined, and conveying the gravitas of a seasoned leader who respects time and duty',
    presenceHi: 'संयत, व्यावहारिक, अनुशासित और कर्तव्य व समय के महत्व को समझने वाले एक परिपक्व मार्गदर्शक जैसे',
    decisionCoreEn: 'rigorously realistic and strategic, building plans designed to withstand severe real-world stress testing',
    decisionCoreHi: 'कठोर व्यावहारिक धरातल पर आधारित, जो हर परिस्थिति में टिकने वाली ठोस और आजमाई हुई योजनाओं पर चलती है',
    emotionalCoreEn: 'self-contained, deeply accountable, and measuring emotional depth through steadfast devotion and practical care',
    emotionalCoreHi: 'आत्म-संयमी, जिम्मेदार और शब्दों के दिखावे के बजाय जीवनभर के कर्तव्य व समर्पण से प्यार जताने वाली',
    workDriveEn: 'unrivaled endurance, institutional mastery, methodical progression, and climbing steadily to authoritative heights',
    workDriveHi: 'अथक धैर्य, संगठनात्मक कुशलता और लगातार प्रयासों से अपने क्षेत्र में सर्वोच्च प्रतिष्ठा हासिल करने वाली',
    socialStyleEn: 'measured, respectful, and reliable, valuing demonstrated competence and earned integrity above empty flattery',
    socialStyleHi: 'मर्यादित, गंभीर और चापलूसी के बजाय वास्तविक काबिलियत व ईमानदारी का सम्मान करने वाली'
  },
  Aquarius: {
    element: 'Air',
    modality: 'Fixed',
    ruler: 'Saturn',
    archetypeEn: 'progressive reformer',
    archetypeHi: 'नवाचारी, निष्पक्ष और मौलिक विचारक',
    presenceEn: 'independent, observant, original, and assessing human affairs through an objective, future-oriented lens',
    presenceHi: 'स्वतंत्र सोच वाले, सूक्ष्म निरीक्षक, मौलिक और सामाजिक परिस्थितियों को निष्पक्ष दृष्टि से देखने वाले',
    decisionCoreEn: 'unconventional, rational, and systemic, questioning outdated norms to build smarter, more equitable frameworks',
    decisionCoreHi: 'तार्किक, मौलिक और व्यवस्था सुधारक, जो पुरानी रूढ़ियों को त्यागकर अधिक उपयोगी और न्यायसंगत तरीके बनाती है',
    emotionalCoreEn: 'egalitarian, intellectually broad, and protecting personal autonomy while holding deep concern for collective welfare',
    emotionalCoreHi: 'समतावादी, बौद्धिक रूप से स्वतंत्र और अपनी निजता की रक्षा करते हुए भी सबके भले की चिंता करने वाली',
    workDriveEn: 'system innovation, conceptual breakthroughs, collaborative networks, and championing progressive initiatives',
    workDriveHi: 'प्रणालीगत सुधार, नए विचारों का क्रियान्वयन और आधुनिक तकनीक व प्रगतिशील पहलों को आगे बढ़ाने वाली',
    socialStyleEn: 'friendly yet detached, connecting effortlessly across diverse walks of life while keeping emotional boundaries',
    socialStyleHi: 'सहज मिलनसार लेकिन आंतरिक रूप से तटस्थ, जो हर वर्ग के लोगों से बिना किसी पक्षपात के जुड़ती है'
  },
  Pisces: {
    element: 'Water',
    modality: 'Dual',
    ruler: 'Jupiter',
    archetypeEn: 'compassionate sage',
    archetypeHi: 'संवेदनशील, अंतर्ज्ञानी और करुणाशील विचारक',
    presenceEn: 'gentle, reflective, empathetic, and tuned into the subtle, unseen currents that underlie human behavior',
    presenceHi: 'सौम्य, विचारशील, करुणावान और लोगों के अनकहे मनोभावों को सहज ही समझ लेने वाले',
    decisionCoreEn: 'holistic, adaptive, and intuitive, integrating emotional wisdom, universal ethics, and deep lateral thinking',
    decisionCoreHi: 'समग्र, सहज और अंतर्ज्ञानी, जो केवल सूखे तर्कों के बजाय भावनात्मक सच्चाई और नैतिक विवेक को जोड़कर निर्णय लेती है',
    emotionalCoreEn: 'boundlessly compassionate, imaginative, and deeply porous to the moods and energies of your environment',
    emotionalCoreHi: 'अत्यंत संवेदनशील, कल्पनाशील और अपने परिवेश की भावनाओं व ऊर्जा को बहुत गहराई से महसूस करने वाली',
    workDriveEn: 'creative inspiration, humanitarian service, healing arts, and solving intractable problems through intuitive synthesis',
    workDriveHi: 'रचनात्मक प्रेरणा, जनकल्याणकारी कार्य और अपनी गहरी समझ से जटिल समस्याओं का मानवीय समाधान निकालने वाली',
    socialStyleEn: 'understanding, non-judgmental, and offering quiet, unconditional solace to those seeking emotional shelter',
    socialStyleHi: 'सहानुभूतिपूर्ण, पूर्वाग्रह-मुक्त और जरूरतमंदों को बिना किसी शर्त के शांत संबल प्रदान करने वाली'
  }
};

// Nakshatra Core Nuances (27 Astrological Lunar Mansions)
interface NakshatraNuance {
  archetypeEn: string;
  archetypeHi: string;
  instinctEn: string;
  instinctHi: string;
  learningToneEn: string;
  learningToneHi: string;
}

const NAKSHATRA_DATA: Record<string, NakshatraNuance> = {
  Ashwini: {
    archetypeEn: 'swift healer and pioneering explorer',
    archetypeHi: 'तेज गति से पहल करने वाले और स्वाभाविक उपचारक',
    instinctEn: 'a spontaneous impulse to act swiftly, solve sudden crises, and clear roadblocks with energetic optimism',
    instinctHi: 'संकट के समय तुरंत सक्रिय होकर तेजी से समाधान निकालने और नई राह बनाने की स्वाभाविक प्रवृत्ति',
    learningToneEn: 'quick, visual, and learning best through direct, dynamic trial and hands-on experimentation',
    learningToneHi: 'तेज, व्यावहारिक और प्रत्यक्ष अनुभव के जरिए सबसे तेजी से सीखने वाली शैली'
  },
  Bharani: {
    archetypeEn: 'resilient transformer bearing heavy responsibilities',
    archetypeHi: 'गंभीर जिम्मेदारियों को वहन करने वाले और परिवर्तनकारी',
    instinctEn: 'deep emotional endurance, an innate capacity to hold immense pressure, and a fierce commitment to core convictions',
    instinctHi: 'गहरा मानसिक धैर्य, भारी दबाव में भी अडिग रहने और अपने सिद्धांतों पर डटे रहने की आंतरिक शक्ति',
    learningToneEn: 'intense, thorough, and demanding complete mastery over fundamental principles',
    learningToneHi: 'गहन, विस्तृत और बुनियादी नियमों को पूरी तरह समझकर आत्मसात करने वाली शैली'
  },
  Krittika: {
    archetypeEn: 'piercing discernment and courageous truth-seeker',
    archetypeHi: 'सूक्ष्म विवेक और सत्य को निर्भीकता से प्रकट करने वाले',
    instinctEn: 'a razor-sharp intellect that cuts through deception, tests assumptions, and defends righteousness with unwavering heat',
    instinctHi: 'तीक्ष्ण बुद्धि जो दिखावे को हटाकर सीधे सच को परखती है और निष्पक्षता की रक्षा करती है',
    learningToneEn: 'structured, analytical, and thriving when challenged with demanding, rigorous problems',
    learningToneHi: 'अनुशासित, तर्कपूर्ण और चुनौतीपूर्ण समस्याओं को हल करने में निखरने वाली शैली'
  },
  Rohini: {
    archetypeEn: 'creative manifestor and aesthetic cultivator',
    archetypeHi: 'रचनात्मक, सौंदर्यप्रिय और समृद्धि के निर्माता',
    instinctEn: 'an eye for grace, beauty, and emotional warmth, with a natural ability to attract resources and nurture enduring growth',
    instinctHi: 'सुंदरता, सौम्यता और भावनात्मक संबल की गहरी समझ, जो अपने आसपास समृद्धि और स्थिरता लाती है',
    learningToneEn: 'sensory, artistic, and flourishing in peaceful, aesthetically pleasing environments',
    learningToneHi: 'रचनात्मक, कलात्मक और शांत व सुरुचिपूर्ण माहौल में सबसे अच्छा प्रदर्शन करने वाली शैली'
  },
  Mrigashira: {
    archetypeEn: 'gentle seeker and inquisitive researcher',
    archetypeHi: 'जिज्ञासु अन्वेषक और ज्ञान के खोजी',
    instinctEn: 'perpetual curiosity, a gentle questing spirit that asks insightful questions, and an instinct for peaceful negotiation',
    instinctHi: 'सदा नई जानकारी की खोज, सूक्ष्म प्रश्न पूछने की आदत और संवाद से हल निकालने की स्वाभाविक कला',
    learningToneEn: 'exploratory, investigative, and thriving when given varied subjects to cross-examine',
    learningToneHi: 'अन्वेषणात्मक, तुलनात्मक और विभिन्न विषयों को जोड़कर समझने वाली बहुआयामी शैली'
  },
  Ardra: {
    archetypeEn: 'analytical clarity born through breakthrough and turbulence',
    archetypeHi: 'बौद्धिक स्पष्टता और मुश्किलों को अवसर में बदलने वाले',
    instinctEn: 'intellectual resilience, seeing through surface chaos to rebuild stronger systems with technological and psychological acuity',
    instinctHi: 'मानसिक मजबूती, जो संकट के बीच से भी नए अवसर और व्यवस्थाएं बनाने की क्षमता रखती है',
    learningToneEn: 'conceptual, deep-diving, and questioning every rule to discover underlying truths',
    learningToneHi: 'वैचारिक, गहराई तक जाने वाली और हर नियम के पीछे के वैज्ञानिक कारण को समझने वाली शैली'
  },
  Punarvasu: {
    archetypeEn: 'restorative guardian and benevolent nurturer',
    archetypeHi: 'पुनर्स्थापक, उदार और सबको संबल देने वाले',
    instinctEn: 'a renewable spring of optimism, the ability to bounce back from any setback, and a welcoming, generous spirit',
    instinctHi: 'सदा नया सवेरा देखने की आशावादिता, असफलताओं से सीखकर दोबारा उठ खड़े होने और उदारता की शक्ति',
    learningToneEn: 'broad, philosophical, and absorbing ideas best when contextualized in ethical frameworks',
    learningToneHi: 'व्यापक, सिद्धांतवादी और जीवन मूल्यों से जुड़ी बातों को सबसे आसानी से ग्रहण करने वाली शैली'
  },
  Pushya: {
    archetypeEn: 'ethical pillar and devoted institutional builder',
    archetypeHi: 'कर्तव्यनिष्ठ, सदाचारी और भरोसेमंद स्तंभ',
    instinctEn: 'an instinct for selfless service, nurturing communal stability, and carrying out duties with unwavering moral rectitude',
    instinctHi: 'निस्वार्थ सेवाभाव, समाज में स्थिरता बनाए रखने और अपने दायित्वों को पूरी निष्ठा से निभाने की प्रवृत्ति',
    learningToneEn: 'systematic, disciplined, and mastering traditional wisdom step by methodical step',
    learningToneHi: 'क्रमबद्ध, अनुशासित और प्रामाणिक ज्ञान को एक-एक कदम करके पूरी गहराई से सीखने वाली शैली'
  },
  Ashlesha: {
    archetypeEn: 'deep psychological strategist and intuitive shield',
    archetypeHi: 'गहन मनोवैज्ञानिक समझ और सतर्क रणनीतिकार',
    instinctEn: 'penetrating psychological radar that senses hidden motives, protecting loved ones through strategic foresight and quiet caution',
    instinctHi: 'गहरी मनोवैज्ञानिक समझ जो अनकही बातों को भांप लेती है और समझदारी से हर स्थिति को नियंत्रित करती है',
    learningToneEn: 'astute, concentrated, and excelling at strategic analysis and intuitive pattern recognition',
    learningToneHi: 'एकाग्र, सूक्ष्म और छिपे हुए पैटर्न्स व रणनीतियों को तेजी से पकड़ने वाली शैली'
  },
  Magha: {
    archetypeEn: 'dignified leader rooted in ancestral honor and self-respect',
    archetypeHi: 'स्वाभिमानी, गरिमापूर्ण और परंपराओं का सम्मान करने वाले',
    instinctEn: 'an instinctive sense of self-respect, honoring legacy and tradition, and leading with natural aristocratic composure',
    instinctHi: 'आत्मसम्मान की गहरी भावना, मर्यादा का पालन और अपने आचरण से स्वाभाविक आदर प्राप्त करने की शक्ति',
    learningToneEn: 'authoritative, holistic, and excelling when given responsibility and respect',
    learningToneHi: 'गरिमापूर्ण, समग्र और जब उचित सम्मान व जिम्मेदारी मिले तो सर्वश्रेष्ठ प्रदर्शन करने वाली शैली'
  },
  PurvaPhalguni: {
    archetypeEn: 'creative artist and generous relationship cultivator',
    archetypeHi: 'कलात्मक, स्नेही और संबंधों को संजोने वाले',
    instinctEn: 'celebrating life, cultivating warmth and affection, and bringing creative flair and gracious hospitality into every space',
    instinctHi: 'जीवन में उल्लास, आत्मीयता और अपनी रचनात्मक प्रतिभा से माहौल को सुखद बनाने की स्वाभाविक इच्छा',
    learningToneEn: 'relational, visual, and learning best through collaborative and creative mediums',
    learningToneHi: 'सहयोगात्मक, दृश्य-माध्यमों और रचनात्मक गतिविधियों के जरिए तेजी से सीखने वाली शैली'
  },
  UttaraPhalguni: {
    archetypeEn: 'dutiful patron and steadfast ally',
    archetypeHi: 'सच्चे सहयोगी, कर्तव्यनिष्ठ और भरोसेमंद मित्र',
    instinctEn: 'unshakable loyalty, fulfilling promises at personal cost, and organizing social support with calm, noble generosity',
    instinctHi: 'वचनों को निभाने की पक्की आदत, संकट में साथ खड़े रहने और निष्पक्ष सहयोग करने की भावना',
    learningToneEn: 'structured, purposeful, and thriving in disciplined, values-based environments',
    learningToneHi: 'व्यवस्थित, उद्देश्यपूर्ण और नैतिक मूल्यों पर आधारित शिक्षा में उत्कृष्टता पाने वाली शैली'
  },
  Hasta: {
    archetypeEn: 'skillful artisan and master problem-solver',
    archetypeHi: 'कार्यकुशल, व्यावहारिक और समस्याओं के चतुर समाधानकर्ता',
    instinctEn: 'resourceful dexterity, practical intelligence that fixes breakdowns quickly, and a clever sense of humor that diffuses stress',
    instinctHi: 'व्यावहारिक सूझबूझ, बिगड़े काम को कुशलता से संभालने और हास्य-विनोद से माहौल हल्का करने की क्षमता',
    learningToneEn: 'hands-on, applied, and mastering techniques through precise tactile and analytical practice',
    learningToneHi: 'प्रायोगिक, व्यावहारिक और स्वयं करके बारीकियों को सिद्धहस्त करने वाली सीखने की शैली'
  },
  Chitra: {
    archetypeEn: 'architectural designer and sparkling visionary',
    archetypeHi: 'सृजनशील वास्तुकार और सुरुचिपूर्ण निर्माता',
    instinctEn: 'an instinct for visual symmetry, elegance, structural perfection, and expressing unique individuality in everything you create',
    instinctHi: 'सुंदरता, संतुलन, तकनीकी बारीकियों और अपनी अनूठी छाप छोड़ने की सशक्त आंतरिक प्रेरणा',
    learningToneEn: 'visual, structural, and excelling at spatial, design, and mechanical conceptualization',
    learningToneHi: 'दृश्य-आधारित, तकनीकी और संरचनात्मक व कलात्मक विषयों में तुरंत महारत हासिल करने वाली शैली'
  },
  Swati: {
    archetypeEn: 'independent traveler and diplomatic bridge-builder',
    archetypeHi: 'स्वतंत्र विचारक, लचीले और कुशल वार्ताकार',
    instinctEn: 'valuing personal freedom, adapting gracefully to shifting winds, and negotiating fair outcomes across diverse communities',
    instinctHi: 'स्वतंत्रता की चाह, बदलती परिस्थितियों में ढलने और विभिन्न पक्षों में सामंजस्य बैठाने की कला',
    learningToneEn: 'self-paced, multidisciplinary, and flourishing through wide independent reading and research',
    learningToneHi: 'स्व-अध्ययन, बहुविषयक और स्वतंत्र रूप से खोजबीन करके सीखने वाली अनुकूलनीय शैली'
  },
  Vishakha: {
    archetypeEn: 'triumphant conqueror of chosen summits',
    archetypeHi: 'दृढ़ संकल्पी और अपने लक्ष्यों को हर हाल में साधने वाले',
    instinctEn: 'single-minded focus on chosen objectives, fierce competitive stamina, and refusing to rest until the summit is reached',
    instinctHi: 'लक्ष्य के प्रति अटूट एकाग्रता, कठिन परिश्रम और जब तक विजय न मिले तब तक निरंतर आगे बढ़ने की लगन',
    learningToneEn: 'goal-oriented, intense, and thriving under structured milestones and healthy competition',
    learningToneHi: 'लक्ष्य-उन्मुख, गहन और स्पष्ट समय-सीमा व सकारात्मक प्रतिस्पर्धा में निखरने वाली शैली'
  },
  Anuradha: {
    archetypeEn: 'devoted friend and harmonious bridge-builder across differences',
    archetypeHi: 'सच्चे मित्र, निष्ठावान और दिलों को जोड़ने वाले',
    instinctEn: 'heart-centered devotion, navigating international or diverse settings with ease, and remaining loyal through every test of time',
    instinctHi: 'हृदय की गहराई से निष्ठा निभाना, अलग-अलग पृष्ठभूमि के लोगों से अपनापन बनाना और समय की हर परीक्षा में खरा उतरना',
    learningToneEn: 'cooperative, research-driven, and excelling in supportive group dynamics and foreign or cross-disciplinary subjects',
    learningToneHi: 'सहयोगात्मक, शोधपरक और समूह अध्ययन व नए क्षेत्रों के ज्ञान को गहराई से समझने वाली शैली'
  },
  Jyeshtha: {
    archetypeEn: 'master strategist and protective elder',
    archetypeHi: 'वरिष्ठ संरक्षक, रणनीतिकार और सुरक्षा कवच',
    instinctEn: 'natural seniority, holding commanding authority in emergencies, and defending vulnerable dependents with fierce courage',
    instinctHi: 'स्वाभाविक वरिष्ठता, कठिन परिस्थितियों में सटीक कमान संभालना और अपनों की रक्षा में ढाल बनकर खड़े रहना',
    learningToneEn: 'independent, deep-level, and mastering complex managerial and defensive principles',
    learningToneHi: 'स्वतंत्र, गंभीर और उच्च-स्तरीय रणनीतिक व प्रबंधन से जुड़े विषयों में पारंगत होने वाली शैली'
  },
  Mula: {
    archetypeEn: 'investigator of foundational roots and dismantler of illusions',
    archetypeHi: 'मूल कारणों के खोजी और सत्य के अन्वेषक',
    instinctEn: 'fearlessly digging to the very root of any problem, stripping away superficial fluff, and building anew on solid truth',
    instinctHi: 'समस्या की जड़ तक पहुंचकर असली कारण को समझने और खोखली बातों को हटाकर ठोस सच्चाई पर निर्माण करने की शक्ति',
    learningToneEn: 'philosophical, diagnostic, and demanding to know the "why" and foundational source of everything',
    learningToneHi: 'दार्शनिक, खोजी और हर बात के मूल कारण तथा वैज्ञानिक आधार को समझे बिना संतुष्ट न होने वाली शैली'
  },
  PurvaAshadha: {
    archetypeEn: 'invincible orator and uplifting inspirer',
    archetypeHi: 'प्रभावशाली वक्ता और सकारात्मक ऊर्जा के संचारक',
    instinctEn: 'unshakeable self-belief, emotional invulnerability, and an innate capacity to uplift and revitalize disheartened colleagues',
    instinctHi: 'अटूट आत्मविश्वास, ऊर्जावान विचार और निराश हो चुके लोगों में भी नया उत्साह फूंक देने की क्षमता',
    learningToneEn: 'expressive, rhetorical, and flourishing when presenting, debating, and synthesizing grand concepts',
    learningToneHi: 'अभिव्यक्तिपूर्ण, तार्किक और संवाद, वाद-विवाद व बड़े सिद्धांतों को प्रस्तुत करने में दक्ष शैली'
  },
  UttaraAshadha: {
    archetypeEn: 'universal champion of enduring integrity and victory',
    archetypeHi: 'सदाचारी, शांत विजेता और अडिग कर्तव्यनिष्ठ',
    instinctEn: 'quiet humility paired with unshakeable moral stamina, winning lasting respect through patient, selfless contribution',
    instinctHi: 'शालीन विनम्रता और अडिग नैतिक बल, जो बिना किसी शोर-शराबे के अपने ठोस काम से स्थायी सम्मान दिलाती है',
    learningToneEn: 'methodical, ethical, and mastering challenging long-term disciplines through steady devotion',
    learningToneHi: 'गंभीर, नैतिक और दीर्घकालिक कठिन विषयों को निरंतर अभ्यास से सिद्ध करने वाली शैली'
  },
  Shravana: {
    archetypeEn: 'attentive listener and keeper of timeless knowledge',
    archetypeHi: 'गंभीर श्रोता और ज्ञान को सहेजने वाले विद्वान',
    instinctEn: 'listening with profound patience, registering nuances in what is unsaid, and preserving practical wisdom to guide others',
    instinctHi: 'धैर्यपूर्वक दूसरों की बात सुनना, बिना कहे भावों को समझना और अनुभवों से सीखकर सही सलाह देना',
    learningToneEn: 'auditory, conversational, and learning exceptionally well through lectures, dialogue, and oral traditions',
    learningToneHi: 'श्रवण-आधारित, संवादप्रिय और व्याख्यानों व अनुभवी लोगों से सुनकर सबसे जल्दी सीखने वाली शैली'
  },
  Dhanishta: {
    archetypeEn: 'rhythmic leader and organizer of collective prosperity',
    archetypeHi: 'संगठक, ऊर्जावान और सामूहिक प्रगति के सूत्रधार',
    instinctEn: 'impeccable sense of timing, bringing harmony to scattered teams, and turning creative vision into tangible prosperity',
    instinctHi: 'समय की सटीक पहचान, बिखरी हुई शक्तियों को एकजुट करना और योजनाओं को वास्तविक सफलता में बदलना',
    learningToneEn: 'rhythmic, structured, and excelling at economics, management, and expressive arts',
    learningToneHi: 'लयात्मक, सुव्यवस्थित और प्रबंधन, अर्थशास्त्र व रचनात्मक कलाओं में सहज रुचि रखने वाली शैली'
  },
  Shatabhisha: {
    archetypeEn: 'visionary diagnostician and private healer',
    archetypeHi: 'रहस्यों के ज्ञाता, सूक्ष्म विश्लेषक और उपचारक',
    instinctEn: 'piercing diagnostic insight, guarding private boundaries, and formulating comprehensive solutions that address hidden causes',
    instinctHi: 'सटीक नैदानिक दृष्टि, अपनी निजता की रक्षा करना और समस्याओं के अनदेखे कारणों का संपूर्ण समाधान निकालना',
    learningToneEn: 'scientific, independent, and excelling in technological, research, and holistic diagnostic fields',
    learningToneHi: 'वैज्ञानिक, स्वतंत्र और तकनीक, शोध व गूढ़ विषयों के अध्ययन में गहरी रुचि रखने वाली शैली'
  },
  PurvaBhadrapada: {
    archetypeEn: 'transformative ascetic and dedicated idealist',
    archetypeHi: 'तपस्वी, गहरे आदर्शवादी और आत्म-अनुशासित',
    instinctEn: 'intense philosophical conviction, sacrificing personal comfort for higher principles, and confronting dark truths with courage',
    instinctHi: 'गहरा दार्शनिक संकल्प, उच्च सिद्धांतों के लिए व्यक्तिगत स्वार्थ को त्यागना और जीवन के गंभीर पक्षों को समझना',
    learningToneEn: 'profound, intense, and thriving in deep philosophical, esoteric, and investigative subjects',
    learningToneHi: 'गंभीर, एकाग्र और गूढ़ दर्शन व अनुसंधानपरक विषयों को गहराई से समझने वाली शैली'
  },
  UttaraBhadrapada: {
    archetypeEn: 'steady anchor of boundless wisdom and calm compassion',
    archetypeHi: 'गहरे विवेक के धनी, शांत और करुणा के संबल',
    instinctEn: 'profound emotional stability, an oceanic capacity for forgiveness, and providing steady shelter to anyone in distress',
    instinctHi: 'गहरी मानसिक स्थिरता, क्षमाशीलता और संकट में घिरे लोगों को एक शांत व सुरक्षित सहारा प्रदान करना',
    learningToneEn: 'contemplative, holistic, and excelling at deep theoretical, spiritual, and systemic knowledge',
    learningToneHi: 'मननशील, समग्र और आध्यात्मिक व गहन सैद्धांतिक ज्ञान को सहजता से आत्मसात करने वाली शैली'
  },
  Revati: {
    archetypeEn: 'gentle shepherd and compassionate journeyer',
    archetypeHi: 'करुणाशील मार्गदर्शक, स्नेही और सबकी रक्षा करने वाले',
    instinctEn: 'boundless empathy, safeguarding the weak and helpless, and navigating life with an intuitive faith in universal goodness',
    instinctHi: 'अपार करुणा, कमजोरों की निस्वार्थ सहायता और जीवन की अच्छाइयों में अटूट विश्वास के साथ आगे बढ़ना',
    learningToneEn: 'intuitive, imaginative, and thriving in nurturing, creative, and human-centered fields',
    learningToneHi: 'सहज, कल्पनाशील और मानवीय संवेदनाओं व कला से जुड़े क्षेत्रों में श्रेष्ठ प्रदर्शन करने वाली शैली'
  }
};

// Fallback for unexpected nakshatra names
const DEFAULT_NAKSHATRA: NakshatraNuance = {
  archetypeEn: 'reflective observer and sincere seeker',
  archetypeHi: 'सुलझे हुए विचारक और निष्ठावान कर्मठ',
  instinctEn: 'steady perseverance and thoughtful awareness across life shifts',
  instinctHi: 'धैर्य और सजगता के साथ परिस्थितियों को संभालने की क्षमता',
  learningToneEn: 'thorough, practical, and grounded in common sense',
  learningToneHi: 'व्यावहारिक और ठोस समझ पर आधारित'
};

// ----------------------------------------------------------------------------
// Multi-Factor Dynamic Synthesis Helpers
// ----------------------------------------------------------------------------

interface SynthesizedChartFactors {
  lagnaSign: string;
  lagnaProfile: SignSynthesisProfile;
  lagnaLord: string;
  lagnaLordHouse: number;
  lagnaLordDignity: string;
  moonSign: string;
  moonProfile: SignSynthesisProfile;
  moonNakshatraName: string;
  moonNakshatra: NakshatraNuance;
  moonPada: number;
  sunSign: string;
  sunProfile: SignSynthesisProfile;
  sunHouse: number;
  strongestPlanetName: string;
  strongestPlanetRupas: number;
  weakestPlanetName: string;
  weakestPlanetRupas: number;
  tenthLordName: string;
  fifthLordName: string;
  ninthLordName: string;
  seventhLordName: string;
  savLagnaBindus: number;
  activeMahadasha: string;
  activeAntardasha: string;
  activeDashaDates: string;
  elementMix: {
    lagna: ElementType;
    moon: ElementType;
    sun: ElementType;
    isHarmonious: boolean;
    isPolaryTense: boolean;
  };
  nativeAge: number | null;
  isYoungChild: boolean;
  isYouth: boolean;
}

function extractAndWeightFactors(kundali: CompleteKundali): SynthesizedChartFactors {
  const b = kundali.birthDetails || ({} as any);
  const houses: HouseInfo[] = kundali.houses || [];
  const planets: PlanetPosition[] = kundali.planets || [];
  const dashas = kundali.dashas || [];

  const lagnaHouse = houses.find(h => h.houseNumber === 1) || { sign: 'Aries', signLord: 'Mars' };
  const lagnaSign = lagnaHouse.sign || 'Aries';
  const lagnaLord = lagnaHouse.signLord || 'Mars';
  const lagnaLordPlanet = planets.find(p => p.name === (lagnaLord as any));

  const moon = planets.find(p => p.name === 'Moon');
  const sun = planets.find(p => p.name === 'Sun');

  const moonSign = moon?.sign || 'Taurus';
  const nakshatraName = kundali.panchang?.nakshatra || moon?.nakshatra || 'Rohini';
  const moonPada = moon?.pada || 1;
  const sunSign = sun?.sign || 'Aries';

  const tenthHouse = houses.find(h => h.houseNumber === 10);
  const fifthHouse = houses.find(h => h.houseNumber === 5);
  const ninthHouse = houses.find(h => h.houseNumber === 9);
  const seventhHouse = houses.find(h => h.houseNumber === 7);

  const shadbala = kundali.shadbala || calculateShadbala(planets, houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });
  const sortedPlanets = [...shadbala.planets].sort((a, b) => b.totalRupas - a.totalRupas);
  const strongest = sortedPlanets[0] || { planet: 'Mars', totalRupas: 7.5 };
  const weakest = sortedPlanets[sortedPlanets.length - 1] || { planet: 'Mercury', totalRupas: 4.8 };

  const currentDasha = dashas.find(d => d.isCurrent) || dashas[0];
  const currentSubDasha = currentDasha?.antardashas?.find(a => a.isCurrent);
  const dashaDates = currentSubDasha 
    ? `${currentSubDasha.startDate} to ${currentSubDasha.endDate}`
    : currentDasha ? `${currentDasha.startDate} to ${currentDasha.endDate}` : '';

  const savLagnaBindus = getSavByBhava(kundali.ashtakvarga, 1, kundali.houses);

  const lagnaProfile = SIGN_PROFILES[lagnaSign] || SIGN_PROFILES['Aries'];
  const moonProfile = SIGN_PROFILES[moonSign] || SIGN_PROFILES['Taurus'];
  const sunProfile = SIGN_PROFILES[sunSign] || SIGN_PROFILES['Aries'];
  const moonNakshatra = NAKSHATRA_DATA[nakshatraName] || DEFAULT_NAKSHATRA;

  const now = new Date();
  let nativeAge: number | null = null;
  if (b.date) {
    const bDate = new Date(b.date);
    if (!isNaN(bDate.getTime())) {
      let age = now.getFullYear() - bDate.getFullYear();
      const m = now.getMonth() - bDate.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < bDate.getDate())) age--;
      if (age >= 0) nativeAge = age;
    }
  }

  const isYoungChild = nativeAge !== null && nativeAge < 11;
  const isYouth = nativeAge !== null && nativeAge >= 11 && nativeAge <= 18;

  const lEl = lagnaProfile.element;
  const mEl = moonProfile.element;
  const sEl = sunProfile.element;

  const isHarmonious = (lEl === mEl) || 
    (lEl === 'Fire' && mEl === 'Air') || (lEl === 'Air' && mEl === 'Fire') ||
    (lEl === 'Earth' && mEl === 'Water') || (lEl === 'Water' && mEl === 'Earth');

  const isPolaryTense = (lEl === 'Fire' && mEl === 'Water') || (lEl === 'Water' && mEl === 'Fire') ||
    (lEl === 'Earth' && mEl === 'Air') || (lEl === 'Air' && mEl === 'Earth');

  return {
    lagnaSign,
    lagnaProfile,
    lagnaLord,
    lagnaLordHouse: lagnaLordPlanet?.house || 1,
    lagnaLordDignity: lagnaLordPlanet?.dignity || 'Neutral',
    moonSign,
    moonProfile,
    moonNakshatraName: nakshatraName,
    moonNakshatra,
    moonPada,
    sunSign,
    sunProfile,
    sunHouse: sun?.house || 1,
    strongestPlanetName: strongest.planet,
    strongestPlanetRupas: strongest.totalRupas,
    weakestPlanetName: weakest.planet,
    weakestPlanetRupas: weakest.totalRupas,
    tenthLordName: tenthHouse?.signLord || 'Mars',
    fifthLordName: fifthHouse?.signLord || 'Jupiter',
    ninthLordName: ninthHouse?.signLord || 'Sun',
    seventhLordName: seventhHouse?.signLord || 'Venus',
    savLagnaBindus,
    activeMahadasha: currentDasha?.planet || 'Sun',
    activeAntardasha: currentSubDasha?.planet || currentDasha?.planet || 'Moon',
    activeDashaDates: dashaDates,
    elementMix: {
      lagna: lEl,
      moon: mEl,
      sun: sEl,
      isHarmonious,
      isPolaryTense
    },
    nativeAge,
    isYoungChild,
    isYouth
  };
}

// ----------------------------------------------------------------------------
// Section Generators: Pure Multi-Factor Synthesis, Zero Static Boilerplate
// ----------------------------------------------------------------------------

function generateSection1(f: SynthesizedChartFactors, isHi: boolean): string {
  // Synthesizes: Lagna Sign + Lagna Modality + Lagna Lord placement + Moon Nakshatra instinct + Elemental & Planetary Matrix
  const lEl = f.elementMix.lagna;
  const dominantPlanet = f.strongestPlanetName;

  // Determine dynamic genesis matrix category without templated opening hooks
  let dynamicOpeningEn = '';
  let dynamicOpeningHi = '';

  if (lEl === 'Earth' || dominantPlanet === 'Saturn' || dominantPlanet === 'Mercury') {
    dynamicOpeningEn = `Your baseline psychological architecture is anchored in observant grounding, quiet assessment, and architectural thinking. Rooted in an unhurried reliability, you approach new challenges by evaluating their structural integrity and enduring utility rather than superficial hype. Your presence expresses ${f.lagnaProfile.presenceEn}.`;
    dynamicOpeningHi = `आपकी मूल मनोवैज्ञानिक संरचना शांत अवलोकन, वस्तुस्थिति की गहरी समझ और व्यवस्थित संरचनात्मक सोच पर आधारित है। एक अडिग और धैर्यवान स्वभाव के साथ, आप किसी भी नई चुनौती या परिस्थिति को तात्कालिक दिखावे के बजाय उसकी दीर्घकालिक उपयोगिता और ठोस धरातल पर परखते हैं। आपका स्वभाव ${f.lagnaProfile.presenceHi} है।`;
  } else if (lEl === 'Fire' || dominantPlanet === 'Mars' || dominantPlanet === 'Sun') {
    dynamicOpeningEn = `Your fundamental psychological architecture is driven by high agency, natural command, and decisive clarity. With a low tolerance for ambiguity and an instinctive drive toward direct ownership, you meet new landscapes with proactive initiative and strategic boldness. Your presence expresses ${f.lagnaProfile.presenceEn}.`;
    dynamicOpeningHi = `आपकी मूल मनोवैज्ञानिक संरचना प्रखर कार्य-ऊर्जा, स्वाभाविक नेतृत्व और अचूक निर्णय-क्षमता से संचालित है। अस्पष्टता को सहन न करने और परिणामों की सीधी जिम्मेदारी लेने की स्वाभाविक प्रेरणा के साथ, आप नई परिस्थितियों में आगे बढ़कर पहल करते हैं। आपका स्वभाव ${f.lagnaProfile.presenceHi} है।`;
  } else if (lEl === 'Air' || dominantPlanet === 'Venus') {
    dynamicOpeningEn = `Your primary psychological architecture is shaped by perceptive social agility, diplomatic discernment, and intellectual curiosity. Endowed with an agile ability to bridge opposing viewpoints and synthesize varied streams of information, you navigate new environments with articulate finesse. Your presence expresses ${f.lagnaProfile.presenceEn}.`;
    dynamicOpeningHi = `आपकी मूल मनोवैज्ञानिक संरचना सूक्ष्म सामाजिक चपलता, कूटनीतिक सूझबूझ और निरंतर बौद्धिक जिज्ञासा से समृद्ध है। परस्पर विरोधी विचारों में सामंजस्य बैठाने और जानकारियों का त्वरित विश्लेषण करने की क्षमता के साथ, आप नए परिवेश में अपनी वाकपटुता और संतुलन से प्रभाव छोड़ते हैं। आपका स्वभाव ${f.lagnaProfile.presenceHi} है।`;
  } else { // Water / Moon / Jupiter
    dynamicOpeningEn = `Your core psychological architecture is defined by reflective depth, intuitive subtext reading, and a deeply anchored principled quietude. You perceive the unstated emotional undercurrents of any room before words are spoken, evaluating situations through an unshakeable moral compass. Your presence expresses ${f.lagnaProfile.presenceEn}.`;
    dynamicOpeningHi = `आपकी मूल मनोवैज्ञानिक संरचना गंभीर चिंतनशीलता, अनकहे भावों को भांपने के अचूक अंतर्ज्ञान और मर्यादापूर्ण शांति से संपन्न है। किसी भी बातचीत या माहौल में शब्दों से पहले उसकी आंतरिक भावना को समझने और नैतिक मूल्यों पर टिके रहने के गुण के साथ, आपका स्वभाव ${f.lagnaProfile.presenceHi} है।`;
  }

  if (isHi) {
    return `${dynamicOpeningHi} आपकी मूल प्रकृति किसी बनावटी दिखावे पर नहीं, बल्कि अपनी वास्तविक आंतरिक गरिमा और ${f.lagnaLord} के स्थिर मार्गदर्शन पर टिकी है।

आपकी सोच और आचरण में ${f.moonNakshatra.instinctHi} का बहुत गहरा प्रभाव है। जब आपके आसपास के लोग जल्दबाजी में प्रतिक्रिया देने लगते हैं, तब आपके भीतर का यह स्वभाव आपको शांत रहकर वस्तुस्थिति को सही तरीके से समझने में मदद करता है। आप दिखावे या सतही बातों के मुकाबले इस बात को कहीं ज्यादा महत्व देते हैं कि कोई काम कितना ठोस, मर्यादित और दीर्घकालिक है।

दैनिक जीवन में इसका सबसे बड़ा असर यह होता है कि आप अपने आत्मसम्मान और व्यक्तिगत सिद्धांतों के साथ कभी समझौता नहीं करते। बाहर से आपका व्यवहार चाहे जितना भी सरल और शिष्ट रहे, लेकिन भीतर से आपके पास एक बहुत स्पष्ट सीमा-रेखा होती है जिसे आप किसी को लांघने नहीं देते।`;
  }

  return `${dynamicOpeningEn} You do not rely on artificial pretense; your presence is defined by an authentic, unforced self-respect anchored in ${f.lagnaLord}'s governance.

In daily interactions, this is reinforced by ${f.moonNakshatra.instinctEn}. In moments when others around you become reactive or lose their patience, you have a natural ability to hold your ground, observe the underlying dynamics, and act with measured composure. You have little interest in superficial posturing; what truly matters to you is whether an effort is genuine, sensible, and built to last.

In practical daily life, this translates into a quiet inner firmness. While you are courteous and collaborative on ordinary matters, you maintain clear boundaries around your integrity. Once you decide where you stand on an essential principle, you do not get easily swayed by peer pressure or passing trends.`;
}

function generateSection2(f: SynthesizedChartFactors, isHi: boolean): string {
  // Synthesizes: Moon Sign + Moon Nakshatra learning style + 5th house/intellectual ruler
  if (isHi) {
    return `आपकी सोचने और निर्णय लेने की शैली ${f.lagnaProfile.decisionCoreHi} है। आप केवल किताबी सिद्धांतों, बड़ी-बड़ी बातों या हवाई दावों से संतुष्ट नहीं होते; आप हमेशा यह परखना चाहते हैं कि कोई विचार व्यावहारिक जीवन में कितना उपयोगी और कारगर सिद्ध होगा।

सीखने और समस्याओं को सुलझाने के मामले में आपका दृष्टिकोण ${f.moonNakshatra.learningToneHi} है। जब आपके सामने कोई जटिल उलझन आती है, तो आप घबराने के बजाय उसके बुनियादी कारणों को समझते हैं। आप तथ्यों को ध्यान से परखते हैं, लेकिन साथ ही लोगों की नीयत और अनकही बातों को भी बहुत तेजी से भांप लेते हैं।

आप रटने या केवल दूसरों की नकल करने से बचते हैं। जब तक किसी बात का मूल सिद्धांत आपकी समझ में नहीं बैठता, तब तक आप उसे पूरी तरह स्वीकार नहीं करते। लेकिन एक बार जब कोई विषय आपकी समझ में आ जाता है, तो आप उसे किसी भी नई चुनौती में बहुत सहजता और आत्मविश्वास के साथ लागू कर लेते हैं।`;
  }

  return `When it comes to how your mind operates, your approach is ${f.lagnaProfile.decisionCoreEn}. You are not someone who gets swept away by exciting hype or theories that have no real-world grounding; your instinctive test is always how an idea actually functions in practice.

In your intellectual and problem-solving habits, your style is ${f.moonNakshatra.learningToneEn}. When a complicated problem lands before you, you tend to examine the core mechanics rather than panicking. You weigh concrete evidence carefully, while simultaneously registering subtle shifts in human behavior, reading between the lines of what people say and what they actually intend.

You thrive when you grasp the fundamental principle behind a subject rather than following a rigid script. Once the foundation makes sense to you, you can improvise and handle unexpected curveballs with remarkable clarity and common sense.`;
}

function generateSection3(f: SynthesizedChartFactors, isHi: boolean): string {
  // Synthesizes: Moon Sign emotional core + Element interaction (Fire/Water/Earth/Air)
  if (isHi) {
    return `आपकी बाहरी उपस्थिति के पीछे एक ${f.moonProfile.emotionalCoreHi} वाला आंतरिक संसार है। आप अपनी भावनाओं का सरेआम ढिंढोरा पीटना या छोटी-छोटी बातों पर तुरंत अशांत होना पसंद नहीं करते; आप अपनी खुशियों और अपने संशयों दोनों को पहले अपने भीतर संभालते और समझते हैं।

जब जीवन में कोई भावनात्मक तनाव या अप्रत्याशित उलझन आती है, तो आपकी पहली स्वाभाविक आवश्यकता कुछ समय अपने साथ शांत रहने की होती है। यह एकांत किसी से भागना नहीं है, बल्कि मानसिक स्पष्टता और भावनात्मक ऊर्जा को दोबारा जुटाने का आपका सबसे भरोसेमंद तरीका है। थोड़ी देर शांत रहकर जब आप बातों को सुलझा लेते हैं, तो आप पहले से ज्यादा मजबूती के साथ वापस आते हैं।

रिश्तों में आप दिखावे के बजाय सच्चाई और भरोसे को सबसे ऊपर रखते हैं। आप हर किसी के सामने आसानी से अपना दिल नहीं खोलते, लेकिन जिन चुने हुए लोगों पर आप सच्चा भरोसा करते हैं, उनके लिए आपकी निष्ठा, परवाह और आत्मीयता अटूट होती है।`;
  }

  return `Beneath your outer demeanor lies an emotional core that is ${f.moonProfile.emotionalCoreEn}. You are not someone who wears every passing feeling on your sleeve or creates unnecessary drama; instead, you process your feelings internally, preferring to understand your own thoughts before discussing them with others.

When unexpected friction or personal stress arises, your natural instinct is usually to step back into your own space for a while. This quiet reflection is essential for you—it is how you filter out emotional noise, regain your equilibrium, and decide what truly matters. Once you have sorted out your thoughts in private, you return to the situation with renewed clarity and steady composure.

In personal bonds, you value emotional honesty, consistency, and loyalty above all else. You do not hand out your trust lightly, but for the select few people who have proven their dependability, your loyalty is steady, protective, and lifelong.`;
}

function generateSection4(f: SynthesizedChartFactors, isHi: boolean): string {
  // Synthesizes: 7th house lord + Moon social style + Lagna social style
  if (isHi) {
    return `बातचीत और रिश्तों में आपकी शैली ${f.lagnaProfile.socialStyleHi} है। आप व्यर्थ की गपशप, दूसरों की अनावश्यक आलोचना या बिना सिर-पैर की बहसों में अपनी ऊर्जा बर्बाद करना पसंद नहीं करते। आप तभी अपनी राय रखते हैं जब आपके पास कहने के लिए कोई ठोस, उपयोगी या सकारात्मक बात हो।

जब कभी किसी करीबी या सहयोगी से कोई मतभेद हो जाए, तो आपकी स्वाभाविक शैली चीख-पुकार करने के बजाय शांत रहकर मुद्दे की बात पर चर्चा करने की होती है। आप रिश्तों में बराबरी और परस्पर सम्मान को बहुत महत्व देते हैं। यदि कोई आपके सम्मान को ठेस पहुंचाए, तो आप झगड़ने के बजाय धीरे-धीरे एक गरिमापूर्ण दूरी बना लेते हैं।

सामाजिक दायरे के मामले में आप संख्या के बजाय गुणवत्ता पर विश्वास करते हैं। पचास सतही परिचितों के मुकाबले दो-तीन ऐसे सच्चे मित्र होना आपके लिए कहीं ज्यादा कीमती है जो सुख और दुख दोनों में बिना किसी शर्त के आपके साथ खड़े रहें।`;
  }

  return `In conversations and relationships, your approach is ${f.lagnaProfile.socialStyleEn}. You tend to steer clear of frivolous gossip, petty complaints, or pointless arguments that drain your time and energy, preferring conversations that have substance, honest humor, and real mutual respect.

When disagreements happen, your natural style is to focus on resolving the issue calmly rather than getting caught up in emotional crossfire. You believe in mutual respect and clear boundaries. If someone repeatedly disrespects your integrity, you rarely make a loud scene; instead, you quietly and permanently step back to protect your peace.

You naturally prefer a tight-knit circle of dependable allies over a sprawling network of superficial acquaintances. You invest your loyalty deeply in people who demonstrate character, and those close to you know they can count on your steady presence whenever things get tough.`;
}

function generateSection5(f: SynthesizedChartFactors, isHi: boolean): string {
  // Synthesizes: 10th Lord + Sun placement + Lagna Lord + Age gating (Desh-Kaal-Patra)
  if (f.isYoungChild) {
    if (isHi) {
      return `वर्तमान बाल्यावस्था (${f.nativeAge} वर्ष) के अनुसार, बच्चे के भीतर ${f.moonNakshatra.learningToneHi} सीखने की एक बहुत मजबूत स्वाभाविक जिज्ञासा है। रटने या दबाव बनाने के बजाय यदि व्यावहारिक उदाहरणों, रचनात्मक माहौल और खेल-खेल में सीखने का अवसर दिया जाए, तो इनकी आंतरिक प्रतिभा बहुत तेजी से निखरेगी।

इस उम्र में किसी निश्चित भविष्य, कॉलेज या करियर की चिंता करने के बजाय एक शांत, सुरक्षित और स्नेही वातावरण देना ही सबसे अच्छा संबल है। नियमित दिनचर्या, भरपूर नींद, खेलकूद और माता-पिता का स्नेही प्रोत्साहन ही बच्चे के आत्मविश्वास की सबसे मजबूत नींव है।`;
    }

    return `At this early developmental age (${f.nativeAge} years), the child's natural orientation is toward ${f.moonNakshatra.learningToneEn}. Nurturing natural curiosity, encouraging open questions, and providing a warm, supportive home routine will build foundational confidence far better than any premature pressure about distant academic or career paths.

Rather than worrying about specific career outcomes or competitive exams at this stage, the priority is a stress-free learning environment, healthy physical play, and consistent parental encouragement that lets their natural problem-solving instincts flourish.`;
  }

  if (isHi) {
    return `कामकाज और आजीविका के क्षेत्र में आपकी कार्यशैली ${f.lagnaProfile.workDriveHi} है। आपकी महत्वाकांक्षा किसी क्षणिक दिखावे या जल्दबाजी पर नहीं टिकी है; आप केवल लोगों की नजरों में आने के लिए नहीं, बल्कि किसी ऐसे काम के लिए पहचान बनाना चाहते हैं जिसे देखकर लोग आपकी मेहनत और काबिलियत का सम्मान करें।

कामकाज में आपकी सबसे बड़ी पूंजी आपकी निरंतरता, जिम्मेदारी का गहरा अहसास और सुव्यवस्थित सोच है। आप ऐसे माहौल में सबसे बेहतर काम करते हैं जहां आपको स्पष्ट जिम्मेदारी सौंपी जाए और अपने काम को सही तरीके से पूरा करने की स्वतंत्रता मिले। आप अनिश्चित शॉर्टकट से बचते हैं और हर कदम मजबूती से आगे बढ़ाते हैं। समय के साथ यह कार्यशैली आपकी साख और प्रभाव को लगातार मजबूत बनाती जाती है।`;
  }

  return `In your professional life and personal endeavors, your approach is ${f.lagnaProfile.workDriveEn}. Your ambition is driven by a desire for genuine competence and enduring achievement rather than quick, hollow recognition. You are not interested in flash-in-the-pan success; you want what you build to have real substance, integrity, and lasting value.

In your day-to-day work, your greatest assets are steady consistency, personal accountability, and an organized approach to responsibilities. You do your best work in environments where expectations are clear and you have the autonomy to execute with precision. You naturally steer clear of reckless shortcuts, preferring to build your capabilities step by step. Over time, this reliability compounds into solid professional respect and steady stability.`;
}

function generateSection6(f: SynthesizedChartFactors, isHi: boolean): string {
  // Synthesizes 4 distinct, chart-specific strengths derived from:
  // 1. Strongest planet, 2. Element harmony, 3. Lagna Lord, 4. Nakshatra core strength
  const pName = f.strongestPlanetName;
  
  let pStrengthEn = "Resilient determination and courage under pressure";
  let pStrengthHi = "कठिन परिस्थितियों में अटूट साहस और दृढ़ संकल्प";
  if (pName === 'Jupiter') {
    pStrengthEn = "Broad wisdom, visionary clarity, and principled judgment";
    pStrengthHi = "दूरदर्शी समझ, विवेकपूर्ण निर्णय और नैतिक स्पष्टता";
  } else if (pName === 'Mercury') {
    pStrengthEn = "Sharp analytical agility and resourceful problem-solving";
    pStrengthHi = "तीक्ष्ण बौद्धिक विश्लेषण और व्यावहारिक सूझबूझ";
  } else if (pName === 'Venus') {
    pStrengthEn = "Refined diplomacy, creative balance, and relationship harmony";
    pStrengthHi = "सुरुचिपूर्ण सामंजस्य, कलात्मक दृष्टि और रिश्तों में संतुलन";
  } else if (pName === 'Saturn') {
    pStrengthEn = "Exceptional endurance, disciplined patience, and long-term focus";
    pStrengthHi = "अथक धैर्य, अनुशासित कार्यशैली और दीर्घकालिक निर्माण";
  } else if (pName === 'Sun') {
    pStrengthEn = "Natural leadership, unshakeable self-respect, and clear purpose";
    pStrengthHi = "स्वाभाविक नेतृत्व क्षमता, आत्मसम्मान और स्पष्ट लक्ष्य";
  } else if (pName === 'Moon') {
    pStrengthEn = "Deep empathetic intuition, emotional adaptability, and protective care";
    pStrengthHi = "गहरा अंतर्ज्ञान, भावनात्मक समझ और दूसरों की परवाह";
  }

  if (isHi) {
    return `जीवन के हर मोड़ पर जब भी परिस्थितियां कठिन हों या निर्णय लेना चुनौतीपूर्ण लगे, ये आपकी वे 4 मुख्य आंतरिक ताकतें हैं जिन पर आप पूरी तरह भरोसा कर सकते हैं:

1. **${pStrengthHi}**: जब भी कोई कठिन चुनौती सामने आती है, आपकी यह आंतरिक शक्ति आपको कभी डगमगाने नहीं देती और सही दिशा में आगे बढ़ाती है।
2. **${f.lagnaProfile.archetypeHi} की क्षमता**: आपकी यह मूल प्रकृति आपको परिस्थितियों को व्यावहारिक और ठोस नजरिए से देखने में मदद करती है, जिससे आपकी ऊर्जा केवल सही दिशा में लगती है।
3. **विश्वसनीयता एवं निष्ठा**: लोग जानते हैं कि यदि आपने किसी काम की जिम्मेदारी ली है या किसी को अपनी बात दी है, तो आप उसे पूरी ईमानदारी से निभाएंगे।
4. **दबाव में मानसिक संतुलन**: संकट या तनाव के क्षणों में जब दूसरे घबरा जाते हैं, तब आपकी शांत रहने और वस्तुस्थिति को तौलने की क्षमता आपको सबसे सही कदम उठाने में मदद करती है।`;
  }

  return `Whenever circumstances become uncertain or decisions feel weighty, here are the four core internal strengths you can consistently rely on:

1. **${pStrengthEn}**: When complex challenges arise, this primary core strength keeps your focus steady and prevents you from losing your bearings.
2. **The Mindset of a ${f.lagnaProfile.archetypeEn}**: Your fundamental nature allows you to distinguish genuine opportunities from impractical distractions, ensuring your energy goes where it actually counts.
3. **Unshakeable Reliability**: People around you know that when you take on a responsibility or give your word, you will see it through with thoroughness and integrity.
4. **Composure Under Stress**: In high-pressure moments where others panic, your ability to pause, evaluate the situation rationally, and keep your cool is one of your greatest advantages.`;
}

function generateSection7(f: SynthesizedChartFactors, isHi: boolean): string {
  // Child Desh-Kaal-Patra Gating for blindspots/growth areas
  if (f.isYoungChild) {
    if (isHi) {
      return `विकास के इस कोमल चरण (${f.nativeAge} वर्ष) में, बच्चे के व्यक्तित्व को सहज रूप से संवारने के लिए इन 3 बातों पर ध्यान देना लाभकारी रहेगा:

1. **अति-उत्तेजना या स्क्रीन से दूरी**: बहुत अधिक डिजिटल उत्तेजना या शोर-शराबा बच्चे के संवेदनशील मन को जल्दी थका सकता है; शांत परिवेश स्वाभाविक एकाग्रता बढ़ाता है।
2. **धीमी गति से सीखने का अवसर**: किसी नए काम में बच्चे पर तुरंत परिणाम देने का दबाव न बनाएं; उन्हें अपनी स्वाभाविक गति से गलतियों से सीखने दें।
3. **भावनाओं को सहजता से व्यक्त करना**: कभी-कभार अपनी बात न कह पाने की खीझ को डांटने के बजाय आत्मीयता से समझें और उन्हें अपनी भावनाएं शब्दों में कहना सिखाएं।`;
    }

    return `During these gentle developmental years (${f.nativeAge} years), nurturing healthy behavioral balance involves mindfully attending to three supportive focus areas:

1. **Managing Sensory & Digital Overload**: High-stimulus environments or excessive screen exposure can fatigue their sensitive nervous system; quiet, predictable surroundings restore calm focus.
2. **Allowing Unhurried Learning Cycles**: Avoid imposing premature speed benchmarks on new tasks; giving them the space to make mistakes and self-correct builds durable confidence.
3. **Gentle Emotional Articulation**: When frustration arises from being unable to express complex feelings, encourage calm conversation rather than corrective discipline.`;
  }

  // Dominant Strength Overuse (derived from strongest planet)
  const strongP = f.strongestPlanetName;
  let strongOveruseTitleEn = "Over-Analyzing & Analysis Fatigue";
  let strongOveruseDescEn = "Because your analytical mind wants to examine every contingency before taking action, you can spend too much energy evaluating variables. Timely execution on a solid 80% plan is often far more effective than waiting indefinitely for absolute perfection.";
  let strongOveruseTitleHi = "अत्यधिक सोच-विचार और विश्लेषण में समय गंवाना";
  let strongOveruseDescHi = "क्योंकि आपकी बौद्धिक प्रकृति हर पहलू की पूरी पड़ताल करना चाहती है, इसलिए कभी-कभी आप निर्णय लेने में जरूरत से ज्यादा समय लगा सकते हैं। सही समय पर उठाया गया व्यावहारिक कदम, बहुत देर से उठाए गए 'परफेक्ट' कदम से कहीं बेहतर परिणाम देता है।";

  if (strongP === 'Mars') {
    strongOveruseTitleEn = "Impatience with Slower Paces & Premature Urgency";
    strongOveruseDescEn = "When you clearly envision the execution path, you can experience sharp internal restlessness when colleagues move slowly or administrative processes drag. Allowing others adequate room to process builds lasting cooperation and prevents friction.";
    strongOveruseTitleHi = "धीमी गति पर अधीरता और अत्यधिक जल्दबाजी";
    strongOveruseDescHi = "जब आपको आगे का रास्ता साफ दिखाई दे रहा होता है, तब दूसरों की धीमी गति या कागजी देरी आपको बेचैन कर सकती है। दूसरों को उनकी गति से चलने का अवसर देना और अनावश्यक जल्दबाजी से बचना बेहतर संबंध बनाए रखेगा।";
  } else if (strongP === 'Saturn') {
    strongOveruseTitleEn = "Over-Caution & Resistance to Agile Adaptation";
    strongOveruseDescEn = "Relying heavily on conservative routines and proven historical precedents can make you overly hesitant to embrace innovative, unconventional opportunities. Calculated flexibility unlocks fresh momentum without compromising your foundational safety.";
    strongOveruseTitleHi = "अत्यधिक सतर्कता और नए प्रयोगों में संकोच";
    strongOveruseDescHi = "केवल पुराने और परखे हुए रास्तों पर ही टिके रहने की जिद आपको नए और लीक से हटकर मिलने वाले आशाजनक अवसरों से दूर रख सकती है। थोड़ा सोचा-समझा लचीलापन आपकी प्रगति को नई गति देगा।";
  } else if (strongP === 'Venus') {
    strongOveruseTitleEn = "Avoidance of Necessary Friction & Over-Accommodating";
    strongOveruseDescEn = "Your instinct for harmony and pleasant atmospheres may tempt you to sidestep uncomfortable confrontations or concede too early in negotiations. Direct, candid clarity preserves true long-term respect far better than superficial peace.";
    strongOveruseTitleHi = "टकराव टालने की प्रवृत्ति और अत्यधिक समझौता";
    strongOveruseDescHi = "सौहार्द और शांति बनाए रखने की स्वाभाविक चाह में कभी-कभी आप जरूरी असहमति जताने या कड़े फैसले लेने से बचते हैं। याद रखें कि स्पष्ट और खरी बात कहना रिश्ते और काम दोनों को लंबे समय में मजबूत बनाता है।";
  } else if (strongP === 'Sun') {
    strongOveruseTitleEn = "Excessive Self-Reliance & Reluctance to Delegate";
    strongOveruseDescEn = "Holding yourself to exacting standards of personal excellence can make you reluctant to entrust critical milestones to others or seek assistance when overloaded. Distributing ownership empowers your team and preserves your executive bandwidth.";
    strongOveruseTitleHi = "अत्यधिक आत्मनिर्भरता और जिम्मेदारियां बांटने में हिचक";
    strongOveruseDescHi = "अपने काम के उच्च मानकों के कारण आपको लग सकता है कि सारा काम खुद ही करना बेहतर है। दूसरों पर भरोसा करना और जिम्मेदारियां सौंपना आपकी मानसिक ऊर्जा को बड़े लक्ष्यों के लिए स्वतंत्र रखेगा।";
  } else if (strongP === 'Jupiter') {
    strongOveruseTitleEn = "Broad Idealism Underestimating Operational Friction";
    strongOveruseDescEn = "Your expansive vision and optimistic mindset can occasionally overlook granular logistical hurdles or underestimate the time needed to complete complex projects. Grounding philosophical aspirations in detailed milestone checklists ensures seamless execution.";
    strongOveruseTitleHi = "अति-उत्साह में जमीनी अड़चनों की अनदेखी";
    strongOveruseDescHi = "आपकी व्यापक दूरदर्शिता और सकारात्मक दृष्टिकोण कभी-कभी बारीक व्यवहारिक कठिनाइयों या समय की सीमाओं का कम आकलन कर सकती है। बड़ी योजनाओं को छोटे-छोटे चरणों में बांटना सफलता की गारंटी देगा।";
  } else if (strongP === 'Moon') {
    strongOveruseTitleEn = "Absorbing Environmental Stress & Over-Identification";
    strongOveruseDescEn = "Your high emotional receptivity means you can unconsciously absorb the unexpressed tensions, anxieties, or mood swings of people around you. Maintaining energetic boundaries protects your clarity and vital stamina.";
    strongOveruseTitleHi = "दूसरों के तनाव और मानसिक भारीपन को सोखना";
    strongOveruseDescHi = "आपकी गहरी संवेदनशीलता के कारण आप अनजाने में अपने आसपास के लोगों की चिंताओं और नकारात्मकता को खुद पर ले लेते हैं। अपने आंतरिक मन को दूसरों के तनाव से अप्रभावित रखना आवश्यक है।";
  }

  // Weakest Planet Vulnerability (derived from weakest planet)
  const weakP = f.weakestPlanetName;
  let weakVulnTitleEn = "Information Overload & Detail Fatigue";
  let weakVulnDescEn = "Sifting through massive volumes of unstructured data, ambiguous instructions, or tedious fine print can cause mental fatigue, increasing the risk of miscommunication if key agreements are not systematically logged in writing.";
  let weakVulnTitleHi = "सूचनाओं के अत्यधिक बोझ से मानसिक थकान";
  let weakVulnDescHi = "बहुत सारी बिखरी हुई जानकारियों या बारीक औपचारिकताओं को संभालने में मानसिक ऊर्जा जल्दी खर्च हो सकती है। संवाद में स्पष्टता रखना और जरूरी बातों को लिखित रूप में दर्ज करना आपको भ्रम से बचाएगा।";

  if (weakP === 'Sun') {
    weakVulnTitleEn = "Hesitation with Self-Advocacy & Solo Visibility";
    weakVulnDescEn = "You may occasionally hesitate to assert your full personal authority or claim rightful recognition before senior authorities, preferring to let your quiet work speak for itself even when proactive self-representation is essential.";
    weakVulnTitleHi = "स्वाभाविक अधिकार जताने और आगे आने में संकोच";
    weakVulnDescHi = "वरिष्ठ मंचों या उच्च अधिकारियों के समक्ष अपनी योग्यता और किए गए कार्यों का श्रेय पूरे आत्मविश्वास से प्रस्तुत करने में कभी-कभी झिझक होना। अपनी उपलब्धियों को विनम्रता लेकिन दृढ़ता के साथ सामने रखना सीखें।";
  } else if (weakP === 'Moon') {
    weakVulnTitleEn = "Fluctuations in Inner Emotional Equilibrium";
    weakVulnDescEn = "Maintaining a steady inner climate during sudden external chaos can take extra deliberate effort, leaving you vulnerable to occasional mood fatigue or second-guessing when emotional atmospheres shift unexpectedly.";
    weakVulnTitleHi = "आंतरिक भावनात्मक स्थिरता में उतार-चढ़ाव";
    weakVulnDescHi = "बाहरी अनिश्चितता या अचानक बदले माहौल में मन की शांति बनाए रखने में अतिरिक्त प्रयास लगना, जिससे कभी-कभी भीतर ही भीतर अशांति या संशय महसूस हो सकता है।";
  } else if (weakP === 'Mars') {
    weakVulnTitleEn = "Hesitation in Enforcing Firm Boundaries";
    weakVulnDescEn = "You may find it challenging to confront difficult situations head-on or push back decisively against unreasonable demands, occasionally delaying necessary boundary-setting to avoid momentary friction.";
    weakVulnTitleHi = "कड़े फैसले लेने और स्पष्ट सीमाएं खींचने में संकोच";
    weakVulnDescHi = "अनुचित मांगों या असहज परिस्थितियों में तुरंत दृढ़ता से 'ना' कहने में हिचकिचाहट महसूस होना। समय पर स्पष्ट सीमाएं तय न करने से बाद में आपके समय और ऊर्जा पर अनावश्यक दबाव बन सकता है।";
  } else if (weakP === 'Mercury') {
    weakVulnTitleEn = "Vulnerability to Ambiguous Details & Miscommunication";
    weakVulnDescEn = "When juggling complex administrative fine print or ambiguous verbal commitments, subtle nuances can slip through the cracks. Always insist on structured agendas, clear written confirmations, and single-topic discussions.";
    weakVulnTitleHi = "बारीक औपचारिकताओं में भ्रम एवं संवाद की चूक";
    weakVulnDescHi = "जटिल शर्तों या अस्पष्ट मौखिक बातों को समझने में गलतफहमी की गुंजाइश बन सकती है। महत्वपूर्ण समझौतों को हमेशा लिखित रूप में स्पष्ट रखना और एक समय में एक विषय पर बात करना हितकर रहेगा।";
  } else if (weakP === 'Jupiter') {
    weakVulnTitleEn = "Uncertainty in Big-Picture Faith & Expansive Decisions";
    weakVulnDescEn = "In pivotal crossroad moments requiring a bold leap of trust, you might over-scrutinize immediate risks and struggle to trust your broader life philosophy, leading to hesitation on expansive growth opportunities.";
    weakVulnTitleHi = "दीर्घकालिक दृष्टिकोण और व्यापक विश्वास में संशय";
    weakVulnDescHi = "बड़े और दूरगामी निर्णयों के समय अपने व्यापक दृष्टिकोण पर पूरी तरह भरोसा करने में कभी-कभी असमंजस होना, जिससे जीवन में बड़े और सकारात्मक विस्तार के अवसरों को अपनाने में देर हो सकती है।";
  } else if (weakP === 'Venus') {
    weakVulnTitleEn = "Straining Relational Balance Through Over-Sacrifice";
    weakVulnDescEn = "In your genuine desire to maintain smooth collaboration, you may give away too much personal bandwidth or downplay your own comfort, occasionally feeling unappreciated when mutual reciprocity is lacking.";
    weakVulnTitleHi = "दूसरों के लिए अत्यधिक समझौते से आंतरिक थकान";
    weakVulnDescHi = "सद्भाव बनाए रखने के लिए अपनी व्यक्तिगत प्राथमिकताओं या आराम को बार-बार पीछे छोड़ देना, जिससे समय के साथ मन में उपेक्षित होने का भाव या असंतोष पैदा हो सकता है।";
  } else if (weakP === 'Saturn') {
    weakVulnTitleEn = "Discouragement from Slow, Tedious Drudgery";
    weakVulnDescEn = "Sustaining high enthusiasm through prolonged periods of repetitive, unglamorous groundwork or bureaucratic delay can be difficult, leading to impatience when compound progress requires mundane persistence.";
    weakVulnTitleHi = "धीमी गति और नीरस दिनचर्या से हतोत्साहित होना";
    weakVulnDescHi = "जब किसी महत्वपूर्ण कार्य में परिणाम बहुत धीरे-धीरे और नीरस परिश्रम के बाद मिलने हों, तब आंतरिक उत्साह बनाए रखने में संघर्ष होना। धैर्य और निरंतरता को अपना संबल बनाएं।";
  }

  // Temperamental Baseline Friction (Lagna & Moon dynamic)
  const lEl = f.elementMix.lagna;
  let baselineFrictionTitleEn = "Reluctance to Course-Correct Mid-Stream";
  let baselineFrictionDescEn = "Once you have committed substantial mental or physical investment to a chosen pathway, your natural loyalty can tempt you to continue pushing long after external conditions suggest an agile pivot.";
  let baselineFrictionTitleHi = "योजना में समय रहते बदलाव करने में संकोच";
  let baselineFrictionDescHi = "जब आप किसी योजना या रास्ते पर अपनी काफी ऊर्जा लगा देते हैं, तब परिस्थितियां बदलने पर भी उस रास्ते को छोड़ने में हिचक महसूस होना। समय की मांग के अनुसार रणनीतिक बदलाव करना सीखें।";

  if (lEl === 'Air') {
    baselineFrictionTitleEn = "Dispersing Focus Across Too Many Concurrent Horizons";
    baselineFrictionDescEn = "Because multiple fascinating concepts appeal to you simultaneously, you risk spreading your attention thin. Forcing yourself to finish one project completely before starting another multiplies your impact.";
    baselineFrictionTitleHi = "एक साथ कई दिशाओं में ध्यान बिखेरना";
    baselineFrictionDescHi = "अनेक रोचक विचारों के प्रति एक साथ आकर्षित होने से ऊर्जा बंट सकती है। एक प्रोजेक्ट को पूरी तरह समाप्त करने के बाद ही दूसरे पर हाथ लगाना आपकी सफलता को कई गुना बढ़ाएगा।";
  } else if (lEl === 'Fire') {
    baselineFrictionTitleEn = "Burning Through Reserves Before Strategic Pacing";
    baselineFrictionDescEn = "Launching into projects with full intensity without factoring in the marathon nature of execution can lead to energy dips. Pacing your stamina sustains your effectiveness.";
    baselineFrictionTitleHi = "शुरुआत में ही अत्यधिक ऊर्जा खर्च कर देना";
    baselineFrictionDescHi = "किसी भी कार्य की शुरुआत में पूरी ताकत लगा देने से मध्य में ऊर्जा की कमी महसूस हो सकती है। अपनी गति को संतुलित रखना दीर्घकालिक सफलता देगा।";
  } else if (lEl === 'Water') {
    baselineFrictionTitleEn = "Defensive Retreat in the Face of Conflict";
    baselineFrictionDescEn = "When interpersonal atmosphere turns abrasive, your instinct may be to withdraw into protective silence rather than hashing out a constructive resolution. Calm, objective dialogue prevents lingering distance.";
    baselineFrictionTitleHi = "टकराव की स्थिति में अपने खोल में सिमट जाना";
    baselineFrictionDescHi = "कठिन या तनावपूर्ण संवाद के समय खुद को समेटकर मौन हो जाने के बजाय, शांत और स्पष्ट शब्दों में अपनी बात रखना गलतफहमियों को तुरंत दूर करेगा।";
  }

  if (isHi) {
    return `हर सशक्त व्यक्तित्व में कुछ ऐसे बिंदु होते हैं जहां आपकी ताकत का अति-उपयोग या कोई अप्रकट संवेदनशीलता आपकी स्वाभाविक प्रगति में अवरोध बन सकती है। आत्म-विकास के लिए इन 3 प्रवृत्तियों पर सजग नजर रखें:

1. **${strongOveruseTitleHi}**: ${strongOveruseDescHi}
2. **${weakVulnTitleHi}**: ${weakVulnDescHi}
3. **${baselineFrictionTitleHi}**: ${baselineFrictionDescHi}`;
  }

  return `Every dynamic personality has areas where an overextended strength or an unaddressed vulnerability can create unintentional friction. To keep your life trajectory smooth and progressive, here are three specific patterns worth keeping in conscious awareness:

1. **${strongOveruseTitleEn}**: ${strongOveruseDescEn}
2. **${weakVulnTitleEn}**: ${weakVulnDescEn}
3. **${baselineFrictionTitleEn}**: ${baselineFrictionDescEn}`;
}

function generateSection8(f: SynthesizedChartFactors, isHi: boolean): string {
  // Polarities & Contradiction Detection: Lagna (Outer) vs Moon (Inner) + Dominant Planet Polarity Layer
  const outerStyle = f.lagnaProfile.archetypeEn;
  const outerStyleHi = f.lagnaProfile.archetypeHi;
  const innerStyle = f.moonProfile.archetypeEn;
  const innerStyleHi = f.moonProfile.archetypeHi;
  const strongP = f.strongestPlanetName;

  // Dominant planet dynamic synthesis
  let dominantPolarityEn = "";
  let dominantPolarityHi = "";

  if (strongP === 'Mars') {
    dominantPolarityEn = "Your foundational presence is charged by a vigorous, decisive driving engine. This introduces a fascinating dynamic: an outward demeanor of grounded composure hosting an urgent, high-velocity problem-solving engine beneath. You perform at your absolute peak when you channel that sharp initiative into structured, high-leverage milestones without letting urgency disrupt your natural calmness.";
    dominantPolarityHi = "आपकी स्वाभाविक गरिमा के भीतर एक अत्यंत तीव्र, साहसी और निर्णायक कार्य-ऊर्जा का प्रवाह है। यह आपके भीतर एक सुंदर समन्वय बनाता है: बाहर से शांत और संतुलित दिखने वाले व्यक्तित्व के भीतर एक त्वरित और निर्भीक समस्या-समाधान की शक्ति। जब आप अपनी इस तेज गति को एक सुव्यवस्थित ढांचे में ढालते हैं, तो आप असाधारण परिणाम प्राप्त करते हैं।";
  } else if (strongP === 'Mercury') {
    dominantPolarityEn = "Your core foundation is enlivened by exceptional analytical agility, investigative curiosity, and rapid information processing. The dynamic polarity is the union of practical continuity and continuous intellectual inquiry: you anchor agile conceptual breakthroughs into tangible, real-world systems that actually function.";
    dominantPolarityHi = "आपके आधार को निरंतर तीक्ष्ण बौद्धिक विश्लेषण, नई जानकारियों की खोज और तार्किक सूझबूझ का संबल प्राप्त है। आपकी वास्तविक शक्ति स्थिरता और बौद्धिक गतिशीलता के इसी सुंदर समन्वय में है, जहां आपके विचार केवल ख्यालों में न रहकर ठोस, व्यावहारिक रूप लेते हैं।";
  } else if (strongP === 'Venus') {
    dominantPolarityEn = "Your steady baseline is elevated by a refined drive for aesthetic beauty, relational elegance, and value creation. The central polarity is one of purposeful enhancement: you do not merely maintain stability for its own sake, but continually seek to elevate the quality, harmony, and dignity of everything you build.";
    dominantPolarityHi = "आपकी स्वाभाविक स्थिरता को एक सुरुचिपूर्ण सौंदर्य-दृष्टि, कलात्मक संतुलन और मानवीय गरिमा का गहरा संबल प्राप्त है। आप केवल चीजों को बनाए रखने में ही विश्वास नहीं रखते, बल्कि अपने परिवेश, कार्य और रिश्तों को और अधिक सुंदर, मूल्यवान व सामंजस्यपूर्ण बनाने के लिए निरंतर प्रयासरत रहते हैं।";
  } else if (strongP === 'Saturn') {
    dominantPolarityEn = "Your natural foundation is deepened by a profound sense of institutional duty, generational endurance, and disciplined responsibility. The unique dynamic is balancing your personal desire for peace with the willingness to shoulder heavy, complex commitments that demand relentless, unglamorous persistence.";
    dominantPolarityHi = "आपकी प्रकृति को एक गहरे कर्तव्यबोध, अटूट अनुशासन और दीर्घकालिक निर्माण की शक्ति मिलती है। आपके भीतर का मुख्य खिंचाव व्यक्तिगत शांति और बड़े सामाजिक या पारिवारिक दायित्वों को पूरी निष्ठा से निभाने के बीच होता है, जहां आपका अडिग धैर्य आपकी सबसे बड़ी शक्ति बनता है।";
  } else if (strongP === 'Sun') {
    dominantPolarityEn = "Your grounded presence is vitalized by an undeniable call to personal sovereignty, high standards, and principled leadership. The dynamic polarity lies in balancing your private, unhurried temperament with the public responsibility of standing tall and guiding others through authentic personal example.";
    dominantPolarityHi = "आपकी शांत प्रकृति के भीतर एक स्वाभाविक नेतृत्व क्षमता, उच्च आत्मसम्मान और सिद्धांतों पर अडिग रहने का तेज है। आप अपनी शांति बनाए रखते हुए भी सही समय पर आगे बढ़कर अपनी बात गरिमा और अधिकार के साथ रखने में पूरी तरह सक्षम होते हैं।";
  } else if (strongP === 'Jupiter') {
    dominantPolarityEn = "Your realistic foundation is elevated by broad philosophical perspective, optimism, and unshakeable ethical values. The dynamic balance bridges pragmatic real-world realism with visionary idealism, allowing you to build large-scale endeavors anchored in lasting moral clarity.";
    dominantPolarityHi = "आपकी व्यावहारिक सूझबूझ को एक व्यापक दूरदर्शिता, सकारात्मक सोच और उच्च नैतिक मूल्यों का मार्गदर्शन प्राप्त है। आप जमीन से जुड़े रहकर भी बहुत बड़े और गरिमापूर्ण लक्ष्यों को साकार करने में पारंगत होते हैं।";
  } else { // Moon
    dominantPolarityEn = "Your steady exterior serves as a protective sanctuary for profound emotional depth, intuitive empathy, and nurturing discernment. Your primary dynamic is using your practical composure to safeguard and channel rich inner sensitivity into meaningful care and psychological wisdom.";
    dominantPolarityHi = "आपका बाहरी शांत स्वभाव आपकी गहरी भावनात्मक संवेदनशीलता और अंतर्दृष्टि के लिए एक सुरक्षित कवच का कार्य करता है। आपकी व्यावहारिक समझ आपकी करुणा को एक ठोस और सार्थक दिशा देती है।";
  }

  // Chart-sensitive closing paragraph synthesizing the exact Lagna, Moon, and dominant-planet interplay
  let closingEn = "";
  let closingHi = "";

  if (strongP === 'Mars') {
    closingEn = `Mastering this dynamic gives you a formidable competitive edge: your outer ${outerStyle.toLowerCase()} presence keeps relationships calm and negotiations steady, while your internal Martian engine supplies the courage to execute decisive breakthroughs where others hesitate.`;
    closingHi = `इस समन्वय को साधना आपको एक अद्वितीय लाभ देता है: आपका बाहरी ${outerStyleHi} आचरण संवाद और वातावरण को शांत रखता है, जबकि आपके भीतर की निर्भीक मंगल ऊर्जा आपको वहां भी निर्णायक कदम उठाने का साहस देती है जहां दूसरे हिचकिचाते हैं।`;
  } else if (strongP === 'Mercury') {
    closingEn = `Integrating this polarity turns your intelligence into lasting impact: your outer composure shields your sharp analytical mind from external panic, allowing you to systematically synthesize complex information into actionable, real-world solutions.`;
    closingHi = `इस संतुलन को समझना आपकी बुद्धि को एक स्थायी शक्ति बनाता है: आपका बाहरी धैर्य आपके तीक्ष्ण बौद्धिक विश्लेषण को शांत बनाए रखता है, जिससे आप जटिल जानकारियों को सुव्यवस्थित करके ठोस और व्यावहारिक समाधानों में बदल पाते हैं।`;
  } else if (strongP === 'Venus') {
    closingEn = `Unifying these forces elevates your life into a masterclass in craftsmanship: your steady foundation ensures financial and emotional security, while your Venusian eye continually infuses grace, beauty, and deep human value into everything you touch.`;
    closingHi = `इन दोनों शक्तियों का समन्वय आपके जीवन को एक उत्कृष्ट गरिमा प्रदान करता है: आपकी स्वाभाविक स्थिरता आपके जीवन में सुरक्षा और शांति बनाए रखती है, जबकि आपकी सौंदर्य-दृष्टि आपके हर काम और रिश्ते में सार्थकता और मिठास घोलती है।`;
  } else if (strongP === 'Saturn') {
    closingEn = `Harmonizing this axis transforms your patience into undeniable authority: your grounded exterior earns instant trust, while your disciplined endurance allows you to outlast obstacles and build institutional legacies that stand the test of time.`;
    closingHi = `इस धुरी को साधना आपके धैर्य को एक गहरी अधिकारिक शक्ति बनाता है: आपकी स्वाभाविक सौम्यता लोगों का अटूट विश्वास जीतती है, जबकि आपका अनुशासन और दृढ़ संकल्प आपको कठिन से कठिन बाधाओं को पार कर स्थायी निर्माण करने की क्षमता देता है।`;
  } else if (strongP === 'Sun') {
    closingEn = `Aligning these layers unlocks authentic leadership: your unhurried composure prevents arrogance, while your solar vitality commands natural respect and inspires others through consistent personal integrity.`;
    closingHi = `इस तालमेल को समझना एक सच्चे और प्रेरक नेतृत्व को जन्म देता है: आपका शांत स्वभाव अहंकार से दूर रखता है, जबकि आपका आंतरिक आत्मबल और निष्ठा दूसरों को स्वतः प्रेरित करती है।`;
  } else if (strongP === 'Jupiter') {
    closingEn = `Synthesizing this balance anchors high wisdom into practical reality: your realistic approach keeps your feet firmly planted, while your expansive Jupiterian optimism ensures your grandest ambitions are always guided by unshakeable moral purpose.`;
    closingHi = `इस समन्वय से उच्च ज्ञान और जमीनी यथार्थ का सुंदर मिलन होता है: आपकी व्यावहारिक समझ आपको हमेशा यथार्थ से जोड़े रखती है, जबकि आपकी व्यापक सोच आपके बड़े लक्ष्यों को सदा नैतिक स्पष्टता और दूरदर्शिता प्रदान करती है।`;
  } else {
    closingEn = `Balancing this axis safeguards your deepest strengths: your outer composure serves as a dependable sanctuary for your rich emotional intuition, allowing you to care deeply and guide others with profound empathy without losing your own footing.`;
    closingHi = `इस आंतरिक संतुलन को समझना आपकी संवेदनशीलता को आपकी सबसे बड़ी शक्ति बनाता है: आपका बाहरी संयम आपके संवेदनशील मन के लिए एक सुरक्षा कवच बनता है, जिससे आप बिना विचलित हुए दूसरों का मार्गदर्शन कर पाते हैं।`;
  }

  if (isHi) {
    return `आपके व्यक्तित्व का सबसे प्रभावशाली और अनूठा पहलू यह है कि इसमें दो अलग-अलग धाराएं एक साथ मिलकर एक संतुलित शक्ति का निर्माण करती हैं।

एक ओर, आपका बाहरी आचरण ${f.lagnaProfile.presenceHi} है। लोग आपको एक ${outerStyleHi} के रूप में देखते हैं, जो अपनी गरिमा, संतुलन और विश्वसनीयता बनाए रखता है।

दूसरी ओर, आपके भीतर ${f.moonProfile.emotionalCoreHi} का गहरा प्रभाव है, जो आपको ${innerStyleHi} की आंतरिक प्रेरणा देता है। इसका अर्थ यह है कि आप बाहर से जितने सहज और संतुलित नजर आते हैं, अंदर से आपके लक्ष्य, आपकी संवेदनशीलता और आपका संकल्प उतना ही गहरा और केंद्रित होता है।

इसके साथ ही, ${dominantPolarityHi}

${closingHi}`;
  }

  return `One of the most defining aspects of your personality is an intricate and rewarding dynamic between how you come across outwardly, what drives you emotionally, and the core energy that powers your execution.

On the outside, you naturally project the presence of a ${outerStyle}, which people experience as ${f.lagnaProfile.presenceEn}. You are perceived as someone who carries themselves with grounded dignity, reliable composure, and steady presence.

Beneath that exterior, your inner world is anchored by the instincts of a ${innerStyle}, characterized by ${f.moonProfile.emotionalCoreEn}. You may not advertise your inner thoughts loudly, but your mind is constantly tracking progress against your personal standards and deeper purpose.

Furthermore, ${dominantPolarityEn}

${closingEn}`;
}

function generateSection9(f: SynthesizedChartFactors, isHi: boolean): string {
  // Evolution with Age: Dynamic synthesis of Moon Element, Modality, Nakshatra trajectory & Lagna-Moon polarity
  // Child Desh-Kaal-Patra Gating
  if (f.isYoungChild) {
    if (isHi) {
      return `विकास के प्रारंभिक वर्षों (${f.nativeAge} वर्ष) में, बच्चे का आंतरिक स्वभाव मुख्य रूप से अपनी स्वाभाविक गति से दुनिया को समझने और सुरक्षित परिवेश में सीखने की ओर उन्मुख है।

इस चरण में सबसे महत्वपूर्ण बात यह है कि बच्चे पर किसी पूर्व-निर्धारित सांचे या कठोर अपेक्षाओं का बोझ न डाला जाए। ${f.moonNakshatra.learningToneHi} का प्रभाव होने के कारण, इन्हें अपनी जिज्ञासा के अनुसार खोजने, खेल-खेल में अनुभव करने और माता-पिता के आत्मीय प्रोत्साहन से सबसे अधिक संबल मिलता है।

जैसे-जैसे आयु बढ़ेगी, इनका प्रारंभिक संकोच अथवा अधीरता स्वतः ही एक संतुलित, आत्मविश्वासी और व्यावहारिक सूझबूझ में परिवर्तित होती जाएगी।`;
    }

    return `During these formative early years (${f.nativeAge} years), the developmental priority centers on establishing emotional security and nurturing natural curiosity rather than pushing premature performance benchmarks.

Because the child's processing style is ${f.moonNakshatra.learningToneEn}, learning unfolds best through playful exploration, gentle structure, and warm encouragement from caregivers. Giving them the freedom to explore questions without fear of being judged builds an unshakeable foundation for later confidence.

As they grow through childhood into adolescence, their natural instincts will organically mature into dependable emotional self-regulation and healthy problem-solving capabilities.`;
  }

  const mEl = f.elementMix.moon;
  const lEl = f.elementMix.lagna;
  const mMod = f.moonProfile.modality;
  const nakInstinctEn = f.moonNakshatra.instinctEn;
  const nakInstinctHi = f.moonNakshatra.instinctHi;

  if (isHi) {
    let earlyTendencyHi = "";
    let matureTrajectoryHi = "";

    if (mEl === 'Fire') {
      earlyTendencyHi = "शुरुआती वर्षों में आपके भीतर एक तीव्र आंतरिक उतावली, तुरंत परिणाम पाने की चाह और किसी भी रुकावट या धीमी गति पर गहरी अधीरता हो सकती थी। आप किसी भी समस्या को सीधे टकराकर तुरंत सुलझाना चाहते थे।";
      if (lEl === 'Earth') {
        matureTrajectoryHi = "आयु और अनुभव के साथ, आप अपनी इस तेज आंतरिक ऊर्जा को अपने व्यावहारिक, शांत बाहरी स्वभाव के साथ साधना सीख जाते हैं। आप समझ जाते हैं कि कब तुरंत कदम उठाना आवश्यक है और कब धैर्य से काम लेना स्थायी सफलता दिलाता है। आपकी जल्दबाजी एक नियंत्रित, निर्णायक और दूरदर्शी नेतृत्व में बदल जाती है।";
      } else if (lEl === 'Water') {
        matureTrajectoryHi = "समय के साथ आप अपनी संवेदनशीलता और तेज निर्णय क्षमता का सुंदर समन्वय कर लेते हैं। आपकी भावनात्मक प्रतिक्रियाएं शांत होकर गहरी अंतर्दृष्टि और सही समय पर निर्णायक पहल करने की क्षमता में परिपक्व होती हैं।";
      } else if (lEl === 'Air') {
        matureTrajectoryHi = "समय के साथ आपकी बौद्धिक फुर्ती और कार्य करने का उत्साह मिलकर एक ऐसी स्पष्ट दिशा बनाते हैं जहां आप विचार से क्रियान्वयन तक बिना ऊर्जा गंवाए सीधे पहुंचते हैं।";
      } else {
        matureTrajectoryHi = "उम्र बढ़ने के साथ आपकी आक्रामकता एक शांत, गरिमापूर्ण अधिकार और प्रेरक आत्मविश्वास में बदल जाती है, जहां आपको अपनी क्षमता साबित करने के लिए संघर्ष नहीं करना पड़ता।";
      }
    } else if (mEl === 'Earth') {
      earlyTendencyHi = "जीवन के शुरुआती दौर में आपके भीतर सुरक्षा और जाने-पहचाने तौर-तरीकों से गहरा लगाव था। किसी भी अप्रत्याशित बदलाव, अनिश्चितता या नई व्यवस्था के प्रति आपके मन में एक स्वाभाविक संकोच या हठ पैदा हो सकता था।";
      if (lEl === 'Fire') {
        matureTrajectoryHi = "अनुभव के साथ आप सीखते हैं कि अपनी साहसी ऊर्जा को सुरक्षित और ठोस ढांचों में कैसे ढाला जाए। आपकी शुरुआती बेचैनी एक टिकाऊ, मजबूत और व्यवस्थित निर्माण क्षमता में बदल जाती है।";
      } else if (lEl === 'Air') {
        matureTrajectoryHi = "समय के साथ आपके नए विचार और प्रयोग ठोस यथार्थ की जमीन पर उतरने लगते हैं, जिससे आप केवल योजनाएं बनाने के बजाय उन्हें धरातल पर साकार करने में पारंगत हो जाते हैं।";
      } else if (lEl === 'Water') {
        matureTrajectoryHi = "परिपक्वता के साथ आप अपनी गहरी भावनाओं को एक स्थिर, व्यावहारिक संबल देना सीख जाते हैं, जिससे आंतरिक उतार-चढ़ाव आपको विचलित नहीं करते और आप दूसरों के लिए एक सुरक्षित आश्रय बनते हैं।";
      } else {
        matureTrajectoryHi = "जैसे-जैसे परिपक्वता आती है, आप समझते हैं कि सच्ची स्थिरता का अर्थ जड़ता नहीं, बल्कि सहज अनुकूलनशीलता है। आप अपनी दृढ़ता और निरंतरता को बरकरार रखते हुए परिस्थितियों के अनुसार शालीनता से ढलना सीख जाते हैं, जो आपकी कार्यकुशलता को बेजोड़ बना देता है।";
      }
    } else if (mEl === 'Air') {
      earlyTendencyHi = "शुरुआती दौर में आपका मन कई दिशाओं में एक साथ दौड़ता था—अत्यधिक सोच-विचार, नई-नई चीजों को जानने की बेचैनी और भावनाओं को भी केवल तर्क की कसौटी पर तौलने की प्रवृत्ति आपको कभी-कभी मानसिक रूप से थका देती थी।";
      if (lEl === 'Earth') {
        matureTrajectoryHi = "आयु बढ़ने के साथ आप अपनी इस विस्तृत बौद्धिक जिज्ञासा को अपने स्थिर, व्यावहारिक आधार के साथ जोड़ना सीख लेते हैं। आप व्यर्थ के मानसिक भटकाव को छोड़कर अपनी बौद्धिक ऊर्जा को ठोस, वास्तविक परिणामों में बदलना सीख जाते हैं।";
      } else if (lEl === 'Water') {
        matureTrajectoryHi = "समय के साथ आप समझ जाते हैं कि कब तर्क की आवश्यकता है और कब अंतरात्मा की आवाज सुननी है, जिससे आपका मानसिक विश्लेषण और भावनात्मक संतुलन एक-दूसरे के पूरक बन जाते हैं।";
      } else if (lEl === 'Fire') {
        matureTrajectoryHi = "परिपक्वता के साथ आपके विचार और आपकी कार्यशैली एक अत्यंत प्रभावी, रणनीतिक गति प्राप्त करते हैं, जिससे आप जटिल से जटिल योजनाओं को तेजी से लागू करने में सक्षम होते हैं।";
      } else {
        matureTrajectoryHi = "उम्र के साथ आपका बौद्धिक बिखराव एक गहरी एकाग्रता और विशेषज्ञता में बदल जाता है। आप अनावश्यक मानसिक तनाव से मुक्त होकर अपनी मानसिक चपलता का उपयोग गंभीर और दूरगामी समाधान खोजने में करते हैं।";
      }
    } else { // Water
      earlyTendencyHi = "शुरुआती जीवन में आपकी गहरी संवेदनशीलता और दूसरों के मानसिक स्पंदनों को सोख लेने की आदत आपको भीतर से बहुत जल्दी थका या आहत कर सकती थी। आप अक्सर अपने भावनात्मक खोल में सिमट जाते थे।";
      if (lEl === 'Earth') {
        matureTrajectoryHi = "समय के साथ आप अपने बाहरी व्यावहारिक संयम को अपनी आंतरिक संवेदनशीलता के लिए एक मजबूत सुरक्षा कवच बनाना सीख लेते हैं। आप अपनी संवेदनशीलता को खोए बिना मजबूत भावनात्मक सीमाएं बना लेते हैं, जिससे आपकी अंतर्दृष्टि एक शांत और अभेद्य शक्ति बन जाती है।";
      } else if (lEl === 'Fire') {
        matureTrajectoryHi = "अनुभव के साथ आप अपनी गहरी भावनाओं को उत्साह और रचनात्मक कर्म में रूपांतरित करना सीख जाते हैं, जिससे आपका मन अनावश्यक संशय से मुक्त होकर निर्भीक बनता है।";
      } else if (lEl === 'Air') {
        matureTrajectoryHi = "परिपक्वता के साथ आप अपनी भावनाओं को स्पष्ट शब्दों में व्यक्त करना और मन की उलझनों को सुलझाना सीख जाते हैं, जिससे आपके भीतर एक दुर्लभ मानसिक स्पष्टता और शांति आती है।";
      } else {
        matureTrajectoryHi = "जैसे-जैसे जीवन का अनुभव बढ़ता है, आपकी संवेदनशीलता एक अत्यंत परिपक्व मनोवैज्ञानिक समझ, करुणा और अचूक अंतर्ज्ञान का रूप ले लेती है, जहां आप बिना खुद को आहत किए दूसरों को सही मार्गदर्शन दे पाते हैं।";
      }
    }

    return `आयु और जीवन के अनुभवों के साथ आपके व्यक्तित्व का विकास तीन प्रमुख चरणों में परिपक्व होता है:

1. **आधारभूत संचय चरण (प्रारंभिक धरातल)**:
${earlyTendencyHi} यह वह दौर था जहां आपने तकनीकी अनुशासन, मौन कर्म और मूल दक्षताओं का निर्माण किया।

2. **कार्यकारी परिवर्तन एवं रणनीतिक दिशा (वर्तमान ${f.activeMahadasha} दशा)**:
${matureTrajectoryHi} वर्तमान चरण में आपका झुकाव प्रत्यक्ष संघर्ष से हटकर उच्च-स्तरीय रणनीति, व्यवस्था प्रबंधन और निर्णायक नेतृत्व की ओर रूपांतरित हो रहा है।

3. **अधिकार, प्रतिष्ठा एवं स्थायी विरासत का क्षितिज**:
आगामी वर्षों में आपके आचरण में ${nakInstinctHi} का प्रभाव और अधिक गहरा होगा। आप एक विश्वसनीय सलाहकार, मार्गदर्शक और स्थायी परिसंपत्तियों के संरक्षक के रूप में प्रतिष्ठित होंगे।`;
  }

  // English Generation
  let earlyTendencyEn = "";
  let matureTrajectoryEn = "";

  if (mEl === 'Fire') {
    earlyTendencyEn = "In your earlier years, your internal rhythm carried a restless spark—an urgent desire for immediate momentum, quick resolutions, and a sharp impatience with bureaucratic delays or indecisive colleagues. When an obstacle appeared, your instinctive reaction was to charge straight at it.";
    if (lEl === 'Earth') {
      matureTrajectoryEn = "With maturity and lived experience, you masterfully anchor that rapid inner fire into your calm, patient outer foundation. You learn precisely when immediate direct action yields high leverage and when strategic patience produces far superior results. What once expressed as internal friction matures into disciplined courage, measured execution, and decisive leadership.";
    } else if (lEl === 'Water') {
      matureTrajectoryEn = "Over time, you learn to harmonize your quick protective instincts with deep emotional discernment. Your early reactive impulses settle into seasoned intuition, allowing you to act courageously without burning through vital emotional energy.";
    } else if (lEl === 'Air') {
      matureTrajectoryEn = "As you mature, the union of rapid mental insight and decisive physical initiative becomes your hallmark, allowing you to turn conceptual breakthroughs into real-world momentum without wasting time in hesitation.";
    } else {
      matureTrajectoryEn = "With age, raw competitive drive matures into a quiet, unforced authority. You no longer feel compelled to assert your presence aggressively; your natural competence and composed confidence command immediate respect.";
    }
  } else if (mEl === 'Earth') {
    earlyTendencyEn = "In your formative years, your natural gravity toward security made you strongly attached to familiar routines and proven methods. When unexpected disruptions occurred or someone proposed an unproven direction, your first reaction was cautious resistance and a desire to hold firmly to your ground.";
    if (lEl === 'Fire') {
      matureTrajectoryEn = "With time, you discover how to channel your pioneering ambitions into durable, methodical frameworks. Your early impatience settles into immense building stamina, allowing you to build endeavors that stand the test of time.";
    } else if (lEl === 'Air') {
      matureTrajectoryEn = "Over the years, your innovative ideas find solid practical footing. You evolve from simply conceptualizing agile possibilities to systematically engineering tangible, real-world systems that function flawlessly.";
    } else if (lEl === 'Water') {
      matureTrajectoryEn = "As you mature, you provide a stable, comforting sanctuary for your rich inner feelings. You learn that your emotional depth is fortified, rather than threatened, by practical boundaries and grounded daily habits.";
    } else {
      matureTrajectoryEn = "With maturity, you come to realize that genuine stability does not require rigidity. You retain your extraordinary endurance and craftsmanship while developing an effortless, graceful adaptability. You become capable of pivoting smoothly when reality demands it, without ever losing your core grounding.";
    }
  } else if (mEl === 'Air') {
    earlyTendencyEn = "In earlier life phases, your mind was prone to running in multiple directions at once—juggling competing curiosities, over-rationalizing emotional subtleties, and wrestling with a mental restlessness that made it hard to settle deeply into a single track.";
    if (lEl === 'Earth') {
      matureTrajectoryEn = "As you gain experience, you learn to bridge your wide intellectual curiosity with your grounded physical discipline. You systematically filter out conceptual clutter, converting broad mental versatility into focused, high-impact mastery and practical expertise.";
    } else if (lEl === 'Water') {
      matureTrajectoryEn = "Over time, you learn to balance cerebral analysis with gut intuition. You stop overthinking your feelings, discovering a serene mental space where clarity of mind and emotional authenticity enrich one another.";
    } else if (lEl === 'Fire') {
      matureTrajectoryEn = "With age, your mental agility combines with your action drive into an unstoppable strategic momentum, enabling you to identify opportunities swiftly and execute them with flawless precision.";
    } else {
      matureTrajectoryEn = "As the years progress, intellectual restlessness settles into profound mental discipline. You develop an exceptional ability to cut through superficial informational noise, directing your agile mind toward substantial, enduring contributions.";
    }
  } else { // Water
    earlyTendencyEn = "In your earlier stages, your porous emotional sensitivity and intuitive radar meant you unconsciously absorbed the moods and unspoken tensions of those around you. This frequently left you feeling emotionally depleted, prompting a sudden need to retreat into protective solitude.";
    if (lEl === 'Earth') {
      matureTrajectoryEn = "With time and maturity, you discover that your outer practical composure is the ideal vessel to protect your rich inner world. You build strong, healthy emotional boundaries without hardening your heart, allowing your intuitive sensitivity to function as a sharp, compassionate asset rather than an exhausting vulnerability.";
    } else if (lEl === 'Fire') {
      matureTrajectoryEn = "Over the years, your deep emotional currents find powerful, creative expression through purposeful action. You learn to trust your instinctive timing and channel empathy into transformative leadership.";
    } else if (lEl === 'Air') {
      matureTrajectoryEn = "As you mature, you become adept at articulating your complex feelings with clarity and grace. You step out of emotional overwhelm into a lucid, compassionate understanding of both yourself and others.";
    } else {
      matureTrajectoryEn = "With lived experience, your emotional receptivity matures into profound psychological wisdom and steady empathy. You become a perceptive, safe anchor who can navigate deep human crises with calmness and grace.";
    }
  }

  return `Across the arc of your life, your personality and operational leadership mature through three distinct structural phases:

1. **Foundational & Accumulation Phase (Groundwork Mastery)**:
${earlyTendencyEn} This formative phase prioritized technical discipline, silent execution, and establishing foundational domain competence.

2. **Executive Pivot & Strategic Direction (Current ${f.activeMahadasha} Phase)**:
${matureTrajectoryEn} In this active phase, your posture pivots decisively from tactical execution to high-level strategy, governance, program management, and structural authority.

3. **Horizon of Authority & Enduring Legacy (Future Horizon)**:
As you advance, reinforced by ${nakInstinctEn}, your decisions become anchored in seasoned mentorship, advisory authority, and building sustainable institutional assets that outlast short-term trends.`;
}

function generateSection10(f: SynthesizedChartFactors, isHi: boolean): string {
  // Active Mahadasha + Antardasha timing window + Chart-Tailored 3 Satvik Daily Habits
  // Child Desh-Kaal-Patra Gating
  if (f.isYoungChild) {
    if (isHi) {
      return `वर्तमान बाल्यावस्था (${f.nativeAge} वर्ष) के अनुसार, बच्चे के समग्र विकास और मानसिक शांति के लिए 3 सरल और स्नेही दैनिक नियम:

1. **सहानुभूतिपूर्ण और नियमित दिनचर्या**: भोजन, विश्राम और अध्ययन के लिए एक शांत और पूर्वानुमेय समय-सारणी रखें ताकि बच्चे को मानसिक सुरक्षा का अहसास रहे।
2. **जिज्ञासा-आधारित खेल**: स्क्रीन से दूर रखकर प्राकृतिक वातावरण में खेलकूद और रचनात्मक गतिविधियों (जैसे चित्रकला, शिल्प या संगीत) के लिए दैनिक समय दें।
3. **स्नेही संवाद**: दिन के अंत में बच्चे के साथ बिना किसी डांट-फटकार के हल्की-फुल्की बातचीत करें और उनकी छोटी-छोटी बातों को धैर्य से सुनें।`;
    }

    return `At this early developmental age (${f.nativeAge} years), supporting healthy growth and emotional balance is best achieved through three nurturing daily practices:

1. **Predictable & Warm Daily Rhythm**: Maintain consistent, calming schedules for meals, restful sleep, and gentle study to provide deep subconscious security.
2. **Hands-On Creative Play**: Encourage screen-free time outdoors and tactile creative activities (such as drawing, building, or music) that match their natural learning pace.
3. **Attentive Listening at Bedtime**: Spend 10 unhurried minutes at the end of each day listening to their thoughts and experiences without critique, reinforcing their emotional trust.`;
  }

  const dashaText = isHi
    ? `${f.activeMahadasha} महादशा ${f.activeAntardasha ? `(अंतर्दशा: ${f.activeAntardasha})` : ''}`
    : `${f.activeMahadasha} planetary chapter ${f.activeAntardasha ? `(with ${f.activeAntardasha} sub-influence)` : ''}`;

  const mEl = f.elementMix.moon;
  const lEl = f.elementMix.lagna;
  const nakName = f.moonNakshatraName;

  // Habit 1: Dynamic to Moon Element
  let habit1TitleEn = "";
  let habit1DescEn = "";
  let habit1TitleHi = "";
  let habit1DescHi = "";

  if (mEl === 'Fire') {
    habit1TitleEn = "Vigorous Physical Energy Release";
    habit1DescEn = "Dedicate 20 to 30 minutes daily to brisk physical exertion, brisk walking, or focused workouts. Expending physical energy early prevents internal restlessness from turning into unnecessary impatience or irritability during high-stakes tasks.";
    habit1TitleHi = "नियमित शारीरिक ऊर्जा निकास";
    habit1DescHi = "प्रतिदिन 20 से 30 मिनट तेज गति के व्यायाम, योग या खुली हवा में टहलने के लिए निकालें। इस शारीरिक सक्रियता से आपके भीतर की आंतरिक बेचैनी शांत होगी और दिनभर एकाग्रता व धैर्य बना रहेगा।";
  } else if (mEl === 'Earth') {
    habit1TitleEn = "Tactile Sensory Grounding & Nature Contact";
    habit1DescEn = "Spend 10 to 15 minutes in deliberate contact with nature, mindful walking on grass, or organizing your immediate physical workspace. Tangible order and sensory calmness instantly restore your mental clarity and stamina.";
    habit1TitleHi = "प्राकृतिक सान्निध्य एवं कार्यस्थल की सुव्यवस्था";
    habit1DescHi = "प्रतिदिन 10 से 15 मिनट प्रकृति के बीच समय बिताएं या अपने काम की जगह को व्यवस्थित करें। भौतिक परिवेश की स्वच्छता और शांति आपके मन को तुरंत स्थिरता और ऊर्जा प्रदान करती है।";
  } else if (mEl === 'Air') {
    habit1TitleEn = "Structured Mental Decluttering";
    habit1DescEn = "Keep a concise physical notebook to capture and categorize incoming ideas before your mind juggles too many threads at once. Limiting simultaneous information streams and closing digital tabs before rest prevents mental fatigue.";
    habit1TitleHi = "विचारों को लिपिबद्ध करना एवं मानसिक शांति";
    habit1DescHi = "अपने मन में आने वाले विचारों और जरूरी कामों को एक डायरी में क्रमबद्ध रूप से लिखें। एक साथ कई दिशाओं में सोचने के बजाय एक समय में एक विचार पर ध्यान केंद्रित करना मानसिक तनाव से बचाएगा।";
  } else { // Water
    habit1TitleEn = "Daily Emotional Decompression & Stillness";
    habit1DescEn = "Establish a dedicated 15-minute quiet window in the evening near water or in peaceful solitude to consciously release the emotional atmospheres absorbed from others throughout the day.";
    habit1TitleHi = "दैनिक मानसिक विश्राम एवं भावनात्मक शुद्धि";
    habit1DescHi = "शाम के समय 10-15 मिनट का शांत एकांत निकालें। दिनभर में आसपास के लोगों से अनजाने में सोखे गए तनाव को मन से मुक्त करने के लिए मौन बैठना या शांत संगीत सुनना अत्यंत लाभकारी रहेगा।";
  }

  // Habit 2: Dynamic to Weakest Planet Vulnerability & Lagna-Moon Balance
  const weakP = f.weakestPlanetName;
  let habit2TitleEn = "";
  let habit2DescEn = "";
  let habit2TitleHi = "";
  let habit2DescHi = "";

  if (weakP === 'Mars') {
    habit2TitleEn = "Clear Boundary Enforcement & Direct Responses";
    habit2DescEn = "When unreasonable demands or uncomfortable requests arise, practice giving a clear, unambiguous response within 5 seconds rather than delaying to avoid friction. Protecting your time and energy decisively safeguards your inner peace.";
    habit2TitleHi = "स्पष्ट व्यक्तिगत सीमाएं और त्वरित 'ना' कहना";
    habit2DescHi = "जब भी कोई अनुचित मांग या अनावश्यक काम सामने आए, टकराव टालने के लिए हां कहने के बजाय तुरंत विनम्र लेकिन दृढ़ शब्दों में अपनी सीमा स्पष्ट करें। अपनी ऊर्जा को सुरक्षित रखना आपकी प्रगति के लिए आवश्यक है।";
  } else if (weakP === 'Mercury') {
    habit2TitleEn = "Written Documentation & Single-Topic Agendas";
    habit2DescEn = "After critical discussions or complex verbal agreements, immediately log a concise 3-bullet written summary. Structuring ambiguous fine print into written clarity eliminates misunderstandings and saves mental bandwidth.";
    habit2TitleHi = "लिखित पुष्टि एवं एक समय में एक विषय";
    habit2DescHi = "किसी भी महत्वपूर्ण बातचीत या समझौते के तुरंत बाद मुख्य बिंदुओं को संक्षेप में लिख लें। अस्पष्ट मौखिक बातों को लिखित स्पष्टता देना आपको गलतफहमियों और मानसिक उलझनों से बचाएगा।";
  } else if (weakP === 'Saturn') {
    habit2TitleEn = "Compound Consistency & Celebrating Small Milestones";
    habit2DescEn = "Break long, tedious groundwork into 25-minute uninterrupted blocks and celebrate daily routine completions. Reframing repetitive drudgery as compound progress keeps your enthusiasm high through slow phases.";
    habit2TitleHi = "निरंतरता का अभ्यास एवं छोटे लक्ष्यों की सराहना";
    habit2DescHi = "लंबी और नीरस प्रक्रियाओं को 25-25 मिनट के छोटे अंतरालों में बांटें। हर दिन के छोटे-छोटे अभ्यासों को अपनी दीर्घकालिक सफलता की नींव मानकर धैर्यपूर्वक आगे बढ़ें।";
  } else if (weakP === 'Sun') {
    habit2TitleEn = "Proactive Self-Advocacy & Claiming Visibility";
    habit2DescEn = "In professional forums or team reviews, make it a deliberate habit to speak early and articulate your contributions clearly. Stepping forward with quiet, authentic confidence ensures your valuable work receives the rightful recognition it deserves.";
    habit2TitleHi = "सक्रिय आत्मविश्वास एवं योग्यता की प्रस्तुति";
    habit2DescHi = "महत्वपूर्ण बैठकों या समूह चर्चाओं में शुरुआत में ही अपनी बात स्पष्ट और गरिमापूर्ण ढंग से रखें। अपने काम की गुणवत्ता पर भरोसा रखते हुए आगे बढ़कर श्रेय स्वीकार करना सीखें।";
  } else if (weakP === 'Venus') {
    habit2TitleEn = "Protecting Personal Restoration Windows";
    habit2DescEn = "Schedule non-negotiable personal comfort and creative restoration windows into your calendar before filling it with social obligations. Prioritizing genuine self-care prevents relationship fatigue.";
    habit2TitleHi = "व्यक्तिगत विश्राम एवं आत्म-संतुष्टि के लिए समय";
    habit2DescHi = "दूसरों को प्रसन्न करने या सामाजिक जिम्मेदारियां निभाने से पहले अपनी दिनचर्या में विश्राम और कलात्मक आनंद के लिए समय तय करें। खुद को तरोताजा रखकर ही आप दूसरों को श्रेष्ठ योगदान दे सकते हैं।";
  } else if (weakP === 'Jupiter') {
    habit2TitleEn = "Big-Picture Perspective Alignment";
    habit2DescEn = "Dedicate 10 minutes at the start of each month to zoom out from operational minutiae and review your grand life philosophy. Reconnecting with broad meaning provides the courage needed for bold expansion.";
    habit2TitleHi = "दीर्घकालिक दृष्टि एवं व्यापक दृष्टिकोण का स्मरण";
    habit2DescHi = "महीने की शुरुआत में 10 मिनट निकालकर रोजमर्रा की भागदौड़ से अलग हटें और अपने बड़े जीवन मूल्यों पर विचार करें। व्यापक दृष्टिकोण आपको साहसी और दूरगामी निर्णय लेने का बल देगा।";
  } else if (weakP === 'Moon') {
    habit2TitleEn = "Emotional Center Reset Before Crucial Decisions";
    habit2DescEn = "Whenever emotional tides shift or external environments become turbulent, take 5 slow diaphragmatic breaths before committing to a decision. Grounding your inner climate preserves objective clarity.";
    habit2TitleHi = "भावनात्मक स्थिरता एवं शांत सांसों का अभ्यास";
    habit2DescHi = "किसी भी तनावपूर्ण स्थिति या बड़े निर्णय से पहले 5 गहरी और शांत सांसें लें। अपने आंतरिक मन को स्थिर करने के बाद ही कोई कदम उठाएं ताकि निर्णय संतुलित रहे।";
  } else {
    // Fallback to Lagna-Moon polarity
    if (lEl === 'Earth' && mEl === 'Fire') {
      habit2TitleEn = "Time-Bounded Sprint Execution";
      habit2DescEn = "Structure your day by pairing steady morning planning with short, high-intensity 45-minute execution sprints. This honors your outer need for methodical structure while giving your inner fire an immediate, productive channel.";
      habit2TitleHi = "योजनाबद्ध समय-सीमा में त्वरित कार्य";
      habit2DescHi = "अपने दिन को शांत योजना और 45 मिनट के केंद्रित कार्य-सत्रों में बांटें। यह आपकी बाहरी स्थिरता और आंतरिक गति के बीच एक आदर्श संतुलन स्थापित करेगा।";
    } else {
      habit2TitleEn = "Balanced Energy Cadence";
      habit2DescEn = "Alternate focused working blocks with intentional 5-minute pauses to reflect on progress, preventing mid-day burnout and keeping your primary goals clearly in view.";
      habit2TitleHi = "संतुलित कार्य गति";
      habit2DescHi = "लगातार काम करने के बीच 5 मिनट का छोटा विश्राम लें और अपनी प्रगति का अवलोकन करें ताकि दिन के अंत तक आपकी ऊर्जा और उत्साह बना रहे।";
    }
  }

  // Habit 3: Dynamic to Nakshatra behavioral motif & Active Dasha
  let habit3TitleEn = "Purposeful Weekly Milestone Review";
  let habit3DescEn = `Under your active ${f.activeMahadasha} chapter, dedicate Sunday evenings to reviewing what truly advanced your long-term vision versus what was merely reactive busywork. Align your weekly calendar around your highest-leverage goals.`;
  let habit3TitleHi = "साप्ताहिक प्राथमिकताओं का पुनरावलोकन";
  let habit3DescHi = `अपनी वर्तमान ${f.activeMahadasha} दशा के अंतर्गत, सप्ताह में एक बार यह मूल्यांकन करें कि आपके कौन से कार्यों ने आपको वास्तविक लक्ष्य की ओर बढ़ाया और कहां समय व्यर्थ हुआ। अपने समय को केवल सबसे महत्वपूर्ण कार्यों पर केंद्रित रखें।`;

  if (nakName === 'Ashwini') {
    habit3TitleEn = "Rapid Milestone Checkpoints";
    habit3DescEn = `Harnessing your swift initiative under the current ${f.activeMahadasha} cycle, break large complex objectives into crisp 3-day sprints. Finishing small milestones quickly maintains your high enthusiasm without letting tasks drag.`;
    habit3TitleHi = "लघु समय-सीमा में लक्ष्य साधना";
    habit3DescHi = `अपनी त्वरित कार्यक्षमता और वर्तमान ${f.activeMahadasha} दशा का लाभ उठाते हुए, बड़े कामों को 3-3 दिन के छोटे हिस्सों में बांटें। त्वरित परिणाम आपका उत्साह बनाए रखेंगे।`;
  } else if (nakName === 'Rohini') {
    habit3TitleEn = "Cultivating Quality and Tangible Completion";
    habit3DescEn = `Channeling your creative refinement under the ${f.activeMahadasha} influence, focus on the polished completion of your ongoing key projects before starting new ventures. True excellence lies in your finishing touch.`;
    habit3TitleHi = "गुणवत्तापूर्ण निर्माण एवं कार्य की पूर्णता";
    habit3DescHi = `अपनी रचनात्मक दृष्टि और वर्तमान ${f.activeMahadasha} दशा के दौरान, जो काम हाथ में लिया है उसे पूरी सुंदरता और गुणवत्ता के साथ समाप्त करने पर ध्यान दें।`;
  } else if (nakName === 'Ashlesha') {
    habit3TitleEn = "Strategic Discretion and Observation";
    habit3DescEn = `Utilizing your perceptive instincts during this ${f.activeMahadasha} phase, keep critical strategies confidential until they are fully developed. Quiet observation before speaking provides unmatched leverage.`;
    habit3TitleHi = "रणनीतिक सतर्कता एवं सूक्ष्म अवलोकन";
    habit3DescHi = `अपनी गहरी समझ और वर्तमान ${f.activeMahadasha} दशा में, अपनी महत्वपूर्ण योजनाओं को पूरी तरह तैयार होने से पहले सार्वजनिक न करें। पहले स्थिति को गहराई से समझें, फिर कदम उठाएं।`;
  } else if (nakName === 'Punarvasu') {
    habit3TitleEn = "Return-to-Basics Alignment Cycles";
    habit3DescEn = `Harmonizing with your renewal capacity during this ${f.activeMahadasha} period, periodically reset your core fundamentals. When projects feel scattered, strip away complications and return to your simplest, most elegant core principles.`;
    habit3TitleHi = "मूल सिद्धांतों पर पुनः एकाग्रता";
    habit3DescHi = `अपनी नवीनीकरण की स्वाभाविक शक्ति और वर्तमान ${f.activeMahadasha} दशा के समय, जब भी चीजें उलझती दिखें, तब अपने बुनियादी सिद्धांतों की ओर लौटें और सरलता से नई शुरुआत करें।`;
  } else if (nakName === 'Bharani') {
    habit3TitleEn = "Pacing Intense Commitments";
    habit3DescEn = `Under your active ${f.activeMahadasha} period, avoid taking on excessive burdens out of sheer passion. Setting realistic capacity boundaries allows your formidable inner endurance to shine without strain.`;
    habit3TitleHi = "दायित्वों का संतुलित प्रबंधन";
    habit3DescHi = `वर्तमान ${f.activeMahadasha} दशा में अपनी पूरी निष्ठा के साथ काम करते हुए भी खुद पर जरूरत से ज्यादा बोझ न डालें। सीमाएं तय करना आपकी ऊर्जा को सुरक्षित रखेगा।`;
  } else if (nakName === 'Krittika') {
    habit3TitleEn = "Constructive Channeling of Discernment";
    habit3DescEn = `Applying your incisive clarity during this ${f.activeMahadasha} window, use direct feedback constructively to elevate standards, ensuring you temper critical precision with encouraging patience.`;
    habit3TitleHi = "सटीक दृष्टि का सकारात्मक उपयोग";
    habit3DescHi = `अपनी तीक्ष्ण समझ और ${f.activeMahadasha} दशा के प्रभाव में, किसी भी कमी को सुधारते समय धैर्य और प्रोत्साहन का पुट रखें ताकि आपकी स्पष्टवादिता प्रेरणा बन सके।`;
  } else if (nakName === 'Ardra') {
    habit3TitleEn = "Constructive Transformation of Friction";
    habit3DescEn = `Under your ${f.activeMahadasha} cycle, view sudden unexpected challenges as opportunities to dismantle obsolete habits and rebuild cleaner, more resilient systems.`;
    habit3TitleHi = "चुनौतियों को अवसर में बदलना";
    habit3DescHi = `वर्तमान ${f.activeMahadasha} दशा में अप्रत्याशित परिवर्तनों से घबराने के बजाय पुरानी आदतों को छोड़कर नई और बेहतर व्यवस्था बनाने पर ध्यान दें।`;
  } else if (nakName === 'Pushya') {
    habit3TitleEn = "Nurturing Sustainable Growth";
    habit3DescEn = `Drawing on your natural benevolence during this ${f.activeMahadasha} phase, invest steady effort into long-term learning and supporting people who share your foundational values.`;
    habit3TitleHi = "स्थायी प्रगति एवं सद्भाव निर्माण";
    habit3DescHi = `अपनी पोषण करने की स्वाभाविक क्षमता और ${f.activeMahadasha} दशा में, अपने ज्ञान के विस्तार और समर्पित सहयोगियों के साथ मिलकर दीर्घकालिक निर्माण पर ध्यान दें।`;
  } else if (nakName === 'Magha') {
    habit3TitleEn = "Upholding Principled Standards";
    habit3DescEn = `In this ${f.activeMahadasha} chapter, let your decisions be anchored by ancestral dignity and proven ethics. Leading by quiet personal example naturally commands authentic respect.`;
    habit3TitleHi = "पारंपरिक मूल्यों और मर्यादा की रक्षा";
    habit3DescHi = `वर्तमान ${f.activeMahadasha} दशा में अपने निर्णयों को उच्च नैतिक मूल्यों और मर्यादा पर आधारित रखें। आपका निष्पक्ष आचरण ही आपका सबसे बड़ा संबल है।`;
  } else if (nakName === 'Chitra') {
    habit3TitleEn = "Refining Architectural Precision";
    habit3DescEn = `Harnessing your sharp eye for design and structural beauty under the ${f.activeMahadasha} period, channel your creative intensity into refining one masterwork project to perfection.`;
    habit3TitleHi = "रचनात्मक बारीकियों में निपुणता";
    habit3DescHi = `अपनी कलात्मक दृष्टि और ${f.activeMahadasha} दशा में, अपने किसी एक मुख्य प्रोजेक्ट को पूरी बारीकी और सुंदरता के साथ संवारने पर ध्यान लगाएं।`;
  } else if (nakName === 'Swati') {
    habit3TitleEn = "Cultivating Independent Momentum";
    habit3DescEn = `Under this ${f.activeMahadasha} window, nurture your autonomy by establishing independent projects that allow you to adapt flexibly to changing winds without restrictive interference.`;
    habit3TitleHi = "स्वतंत्र एवं लचीली कार्यशैली";
    habit3DescHi = `वर्तमान ${f.activeMahadasha} दशा में अपनी स्वतंत्रता और लचीलेपन को बनाए रखते हुए नए संपर्कों और अवसरों का संतुलित लाभ उठाएं।`;
  } else if (nakName === 'Vishakha') {
    habit3TitleEn = "Single-Minded Goal Focus";
    habit3DescEn = `Channeling your fierce determination under your active ${f.activeMahadasha} phase, fix your sights on one central summit and systematically eliminate peripheral distractions until victory is achieved.`;
    habit3TitleHi = "एकनिष्ठ लक्ष्य साधना";
    habit3DescHi = `अपनी अदम्य इच्छाशक्ति और ${f.activeMahadasha} दशा का सदुपयोग करते हुए, एक मुख्य लक्ष्य पर अपनी संपूर्ण ऊर्जा लगाएं और जब तक सफलता न मिले तब तक निरंतर जुटे रहें।`;
  } else if (nakName === 'Anuradha') {
    habit3TitleEn = "Deepening Trust and Cross-Disciplinary Bonds";
    habit3DescEn = `In this ${f.activeMahadasha} period, invest in genuine long-term relationships and explore diverse, international or multidisciplinary connections that broaden your horizon.`;
    habit3TitleHi = "सच्चे संबंधों और नए क्षेत्रों में विस्तार";
    habit3DescHi = `वर्तमान ${f.activeMahadasha} दशा में निष्ठावान मित्रों के साथ मिलकर काम करें और नए व विविधतापूर्ण ज्ञान के क्षेत्रों में रुचि बढ़ाएं।`;
  } else if (nakName === 'Jyeshtha') {
    habit3TitleEn = "Commanding Strategic Readiness";
    habit3DescEn = `Leveraging your seniority and protective instincts under this ${f.activeMahadasha} cycle, anticipate upcoming shifts and prepare your defensive resources before challenges materialize.`;
    habit3TitleHi = "रणनीतिक तैयारी और दूरदर्शिता";
    habit3DescHi = `अपनी स्वाभाविक वरिष्ठता और ${f.activeMahadasha} दशा में, भविष्य में आने वाले बदलावों का पहले से अनुमान लगाकर अपनी योजनाओं को सुदृढ़ रखें।`;
  } else if (nakName === 'Mula') {
    habit3TitleEn = "Root-Cause Diagnostics";
    habit3DescEn = `Under your active ${f.activeMahadasha} chapter, don't settle for superficial band-aids. Courageously investigate and resolve the foundational root causes of any persistent obstacle.`;
    habit3TitleHi = "मूल कारणों का समाधान";
    habit3DescHi = `वर्तमान ${f.activeMahadasha} दशा में किसी भी समस्या के सतही समाधान के बजाय उसकी वास्तविक जड़ तक पहुंचकर ठोस और स्थायी सुधार करें।`;
  } else if (nakName === 'PurvaAshadha') {
    habit3TitleEn = "Inspirational Communication and Vision";
    habit3DescEn = `Applying your uplifting optimism under the ${f.activeMahadasha} influence, share your perspective candidly to motivate teams and infuse disheartened projects with fresh vitality.`;
    habit3TitleHi = "उत्साहवर्धक संवाद और दृष्टिकोण";
    habit3DescHi = `अपने स्वाभाविक आत्मविश्वास और ${f.activeMahadasha} दशा में, अपने सकारात्मक विचारों से साथियों का मनोबल बढ़ाएं और साझा लक्ष्यों को नई ऊर्जा दें।`;
  } else if (nakName === 'UttaraAshadha') {
    habit3TitleEn = "Enduring Ethical Dedication";
    habit3DescEn = `Anchored by unshakeable integrity during this ${f.activeMahadasha} period, continue your steady, selfless contribution to worthy causes. Lasting success builds quietly on duty and patience.`;
    habit3TitleHi = "नैतिक निष्ठा और निरंतर समर्पण";
    habit3DescHi = `अपनी नैतिक दृढ़ता और वर्तमान ${f.activeMahadasha} दशा में, बिना किसी जल्दबाजी के अपने कर्तव्य पथ पर आगे बढ़ते रहें। आपका धैर्य स्थायी प्रतिष्ठा दिलाएगा।`;
  } else if (nakName === 'Shravana') {
    habit3TitleEn = "Attentive Listening and Knowledge Synthesis";
    habit3DescEn = `Utilizing your reflective receptivity in this ${f.activeMahadasha} cycle, take time to listen deeply to mentors and advisors before finalizing major life strategies.`;
    habit3TitleHi = "गहन श्रवण और ज्ञान संकलन";
    habit3DescHi = `अपनी सुनने और सीखने की क्षमता तथा ${f.activeMahadasha} दशा में, कोई भी बड़ा निर्णय लेने से पहले अनुभवी लोगों के विचारों को ध्यानपूर्वक सुनें और समझें।`;
  } else if (nakName === 'Dhanishta') {
    habit3TitleEn = "Synchronizing Rhythm and Resource Timing";
    habit3DescEn = `Harnessing your impeccable timing under the ${f.activeMahadasha} influence, coordinate collective efforts and deploy resources systematically to maximize tangible prosperity.`;
    habit3TitleHi = "सटीक समय प्रबंधन और संसाधनों का समन्वय";
    habit3DescHi = `समय की सही पहचान और वर्तमान ${f.activeMahadasha} दशा में, अपनी ऊर्जा और साधनों का सही समय पर सही जगह उपयोग करके समृद्धि के नए द्वार खोलें।`;
  } else if (nakName === 'Shatabhisha') {
    habit3TitleEn = "Holistic Diagnostics and Boundary Preservation";
    habit3DescEn = `In this ${f.activeMahadasha} window, guard your private sanctum while deploying your analytical prowess to uncover comprehensive, systemic solutions to complex puzzles.`;
    habit3TitleHi = "गहन विश्लेषण और निजता की सुरक्षा";
    habit3DescHi = `अपनी सूक्ष्म विश्लेषणात्मक दृष्टि और ${f.activeMahadasha} दशा में, अपनी व्यक्तिगत निजता को बनाए रखते हुए समस्याओं के समग्र और स्थायी हल निकालें।`;
  } else if (nakName === 'PurvaBhadrapada') {
    habit3TitleEn = "Disciplined Conviction and Inner Transformation";
    habit3DescEn = `Under your active ${f.activeMahadasha} period, dedicate time to serious self-reflection and philosophical study, allowing deep ethical convictions to guide your practical actions.`;
    habit3TitleHi = "गहरा आत्म-अनुशासन और चिंतन";
    habit3DescHi = `वर्तमान ${f.activeMahadasha} दशा में अपने उच्च जीवन मूल्यों और आत्म-अनुशासन के साथ आगे बढ़ें और अपने संकल्प को कर्म में बदलें।`;
  } else if (nakName === 'UttaraBhadrapada') {
    habit3TitleEn = "Cultivating Oceanic Compassion and Stability";
    habit3DescEn = `Drawing upon your profound emotional stability in this ${f.activeMahadasha} cycle, act as a patient, reassuring shelter for those navigating distress, remaining unperturbed by outer storm.`;
    habit3TitleHi = "स्थिर संबल और क्षमाशीलता";
    habit3DescHi = `अपनी गहरी मानसिक शांति और वर्तमान ${f.activeMahadasha} दशा में, संकट के समय दूसरों के लिए एक शांत और विश्वसनीय सहारा बनें।`;
  } else if (nakName === 'Revati') {
    habit3TitleEn = "Nurturing Empathy with Healthy Boundaries";
    habit3DescEn = `Channeling your gentle compassion under the ${f.activeMahadasha} phase, engage in acts of selfless service while protecting your own energy from excessive emotional drainage.`;
    habit3TitleHi = "करुणा और ऊर्जा संरक्षण का संतुलन";
    habit3DescHi = `अपनी परोपकारी भावना और ${f.activeMahadasha} दशा में, कमजोरों की सहायता करते हुए भी अपनी मानसिक व शारीरिक ऊर्जा को सुरक्षित रखें।`;
  }

  if (isHi) {
    return `वर्तमान समय में आप **${dashaText}** के प्रभाव क्षेत्र में हैं। यह सक्रिय कालखंड अपनी क्षमताओं को मांजने, प्राथमिकताओं को सुव्यवस्थित करने और अपने जीवन लक्ष्यों की दिशा में ठोस प्रगति करने के लिए विशेष रूप से अनुकूल है।

अपने मानसिक संतुलन, शारीरिक ऊर्जा और दैनिक कार्यक्षमता को सर्वोत्तम स्थिति में बनाए रखने के लिए 3 विशिष्ट और व्यावहारिक आदतें:

1. **${habit1TitleHi}**: ${habit1DescHi}
2. **${habit2TitleHi}**: ${habit2DescHi}
3. **${habit3TitleHi}**: ${habit3DescHi}`;
  }

  return `During this current phase of your life, you are moving through a **${dashaText}**. This active window provides an especially supportive opportunity to get organized, hone your core capabilities, and make meaningful progress on your most important goals.

To keep your mind centered, your emotional energy balanced, and your daily momentum operating at its highest potential, here are three practical habits tailored to your chart:

1. **${habit1TitleEn}**: ${habit1DescEn}
2. **${habit2TitleEn}**: ${habit2DescEn}
3. **${habit3TitleEn}**: ${habit3DescEn}`;
}

// ----------------------------------------------------------------------------
// Main Exported Generator Function
// ----------------------------------------------------------------------------

/**
 * Generate a rich, deeply synthesized Integrated Personality & Life Profile
 * translating full horoscope calculations into direct, warm, natural client language.
 */
export function generateIntegratedProfile(kundali: CompleteKundali, lang: string = 'en'): IntegratedProfileResult {
  const isHi = lang === 'hi';
  const factors = extractAndWeightFactors(kundali);

  const activeDashaSummary = isHi
    ? `${factors.activeMahadasha} महादशा ${factors.activeAntardasha ? `(अंतर्दशा: ${factors.activeAntardasha})` : ''}`
    : `${factors.activeMahadasha} planetary chapter ${factors.activeAntardasha ? `(with ${factors.activeAntardasha} sub-influence)` : ''}`;

  // Single Introductory Disclosure (Mandatory)
  const disclosure = isHi
    ? 'यह विस्तृत परामर्श आपकी जन्म कुंडली के संयुक्त ग्रह विन्यास पर आधारित है — किसी एक ग्रह, भाव अथवा स्थिति के एकाकी आधार पर नहीं।'
    : 'This is a detailed reading based on the combined picture of your horoscope—not on any single planet, house or placement.';

  // Construct the 10 Synthesized Sections
  const sections: ProfileSection[] = [
    {
      id: 'fundamental-nature',
      title: isHi ? 'मूल प्रकृति एवं स्वाभाविक दृष्टिकोण' : 'Your Fundamental Nature',
      iconName: 'Compass',
      content: generateSection1(factors, isHi)
    },
    {
      id: 'how-your-mind-works',
      title: isHi ? 'विचार शैली एवं बौद्धिक कार्यप्रणाली' : 'How Your Mind Works',
      iconName: 'Brain',
      content: generateSection2(factors, isHi)
    },
    {
      id: 'emotional-pattern',
      title: isHi ? 'भावनात्मक प्रतिरूप एवं आंतरिक संसार' : 'Emotional Pattern & Inner World',
      iconName: 'Heart',
      content: generateSection3(factors, isHi)
    },
    {
      id: 'communication-relationships',
      title: isHi ? 'संवाद शैली एवं संबंध सामंजस्य' : 'Communication & Relationships',
      iconName: 'Users',
      content: generateSection4(factors, isHi)
    },
    {
      id: 'ambition-drive-work-style',
      title: isHi ? 'महत्वाकांक्षा, संकल्प एवं कार्यशैली' : 'Ambition, Drive & Work Style',
      iconName: 'Flame',
      content: generateSection5(factors, isHi)
    },
    {
      id: 'strengths',
      title: isHi ? 'आपकी स्वाभाविक मुख्य शक्तियां' : 'Strengths You Can Rely On',
      iconName: 'Award',
      content: generateSection6(factors, isHi)
    },
    {
      id: 'patterns-to-watch',
      title: isHi ? 'सावधानी के बिंदु एवं विकास के अवसर' : 'Patterns That Can Hold You Back',
      iconName: 'AlertTriangle',
      content: generateSection7(factors, isHi)
    },
    {
      id: 'conflicting-forces',
      title: isHi ? 'विरोधाभासी एवं पूरक आंतरिक शक्तियां' : 'Inner Tensions & Complementary Forces',
      iconName: 'Zap',
      content: generateSection8(factors, isHi)
    },
    {
      id: 'evolution-with-age',
      title: isHi ? 'आयु एवं अनुभवों के साथ व्यक्तित्व का विकास' : 'How This Pattern Evolves With Age',
      iconName: 'Clock',
      content: generateSection9(factors, isHi)
    },
    {
      id: 'practical-guidance',
      title: isHi ? 'वर्तमान चरण के लिए व्यावहारिक मार्गदर्शन' : 'Practical Guidance for the Current Phase',
      iconName: 'CheckCircle2',
      content: generateSection10(factors, isHi)
    }
  ];

  // Remedy Assessment (No Alarmism, Practical & Reassuring)
  const remedyVerdict = {
    required: false,
    text: isHi
      ? 'उपाय निर्णय: इस समय किसी भारी अथवा जटिल अनुष्ठान की आवश्यकता नहीं है। आपकी मूल ग्रह स्थिति संतुलित है। दैनिक अनुशासन, सकारात्मक सोच और नियमित दिनचर्या ही आपका सर्वोत्तम संबल है।'
      : 'Remedy: None specifically required at this time. Your primary planetary foundations remain balanced; maintaining healthy daily habits, ethical clarity, and mindful focus is completely sufficient.'
  };

  // Word count calculation
  const totalText = [disclosure, ...sections.map(s => s.content), remedyVerdict.text].join(' ');
  const wordCount = totalText.split(/\s+/).filter(Boolean).length;

  // Extract structured PersonalitySignature
  const personalitySignature: PersonalitySignature = {
    lagnaArchetype: {
      sign: factors.lagnaSign,
      lord: factors.lagnaLord,
      element: factors.elementMix.lagna,
      presenceEn: factors.lagnaProfile.presenceEn,
      presenceHi: factors.lagnaProfile.presenceHi,
      archetypeEn: factors.lagnaProfile.archetypeEn,
      archetypeHi: factors.lagnaProfile.archetypeHi
    },
    mindArchetype: {
      nakshatra: factors.moonNakshatraName,
      pada: factors.moonPada,
      instinctEn: factors.moonNakshatra.instinctEn,
      instinctHi: factors.moonNakshatra.instinctHi,
      cognitiveStyleEn: factors.moonProfile.decisionCoreEn,
      cognitiveStyleHi: factors.moonProfile.decisionCoreHi
    },
    emotionalCore: {
      moonSign: factors.moonSign,
      element: factors.elementMix.moon,
      receptivityEn: factors.moonProfile.emotionalCoreEn,
      receptivityHi: factors.moonProfile.emotionalCoreHi
    },
    dominantExecutiveEngine: {
      planet: factors.strongestPlanetName,
      rupas: factors.strongestPlanetRupas,
      strengthOveruseEn: `${factors.strongestPlanetName} high-capacity drive`,
      strengthOveruseHi: `${factors.strongestPlanetName} की उच्च कार्यकारी क्षमता`
    },
    fundamentalVulnerability: {
      planet: factors.weakestPlanetName,
      rupas: factors.weakestPlanetRupas,
      blindspotEn: `${factors.weakestPlanetName} vulnerability under pressure`,
      blindspotHi: `${factors.weakestPlanetName} का संवेदनशील प्रभाव`
    },
    corePolarityDynamic: {
      polarityEn: `Interaction between outer ${factors.elementMix.lagna} presence and inner ${factors.elementMix.moon} emotional center, propelled by ${factors.strongestPlanetName}`,
      polarityHi: `बाहरी ${factors.elementMix.lagna} व्यक्तित्व और आंतरिक ${factors.elementMix.moon} मन का ${factors.strongestPlanetName} ऊर्जा के साथ समन्वय`
    },
    maturationArc: {
      arcEn: `Maturation from early instinct to seasoned mastery through ${factors.strongestPlanetName} discipline`,
      arcHi: `${factors.strongestPlanetName} के अनुशासित प्रभाव से अनुभवों के साथ परिपक्वता`
    },
    nativeAge: factors.nativeAge,
    isYoungChild: factors.isYoungChild
  };

  return {
    disclosure,
    sections,
    wordCount,
    activeDashaSummary,
    remedyVerdict,
    personalitySignature
  };
}

// ----------------------------------------------------------------------------
// Standalone Personality Signature Extractor
// ----------------------------------------------------------------------------

export function extractPersonalitySignature(kundali: CompleteKundali): PersonalitySignature {
  const factors = extractAndWeightFactors(kundali);
  return {
    lagnaArchetype: {
      sign: factors.lagnaSign,
      lord: factors.lagnaLord,
      element: factors.elementMix.lagna,
      presenceEn: factors.lagnaProfile.presenceEn,
      presenceHi: factors.lagnaProfile.presenceHi,
      archetypeEn: factors.lagnaProfile.archetypeEn,
      archetypeHi: factors.lagnaProfile.archetypeHi
    },
    mindArchetype: {
      nakshatra: factors.moonNakshatraName,
      pada: factors.moonPada,
      instinctEn: factors.moonNakshatra.instinctEn,
      instinctHi: factors.moonNakshatra.instinctHi,
      cognitiveStyleEn: factors.moonProfile.decisionCoreEn,
      cognitiveStyleHi: factors.moonProfile.decisionCoreHi
    },
    emotionalCore: {
      moonSign: factors.moonSign,
      element: factors.elementMix.moon,
      receptivityEn: factors.moonProfile.emotionalCoreEn,
      receptivityHi: factors.moonProfile.emotionalCoreHi
    },
    dominantExecutiveEngine: {
      planet: factors.strongestPlanetName,
      rupas: factors.strongestPlanetRupas,
      strengthOveruseEn: `${factors.strongestPlanetName} high-capacity drive`,
      strengthOveruseHi: `${factors.strongestPlanetName} की उच्च कार्यकारी क्षमता`
    },
    fundamentalVulnerability: {
      planet: factors.weakestPlanetName,
      rupas: factors.weakestPlanetRupas,
      blindspotEn: `${factors.weakestPlanetName} vulnerability under pressure`,
      blindspotHi: `${factors.weakestPlanetName} का संवेदनशील प्रभाव`
    },
    corePolarityDynamic: {
      polarityEn: `Interaction between outer ${factors.elementMix.lagna} presence and inner ${factors.elementMix.moon} emotional center, propelled by ${factors.strongestPlanetName}`,
      polarityHi: `बाहरी ${factors.elementMix.lagna} व्यक्तित्व और आंतरिक ${factors.elementMix.moon} मन का ${factors.strongestPlanetName} ऊर्जा के साथ समन्वय`
    },
    maturationArc: {
      arcEn: `Maturation from early instinct to seasoned mastery through ${factors.strongestPlanetName} discipline`,
      arcHi: `${factors.strongestPlanetName} के अनुशासित प्रभाव से अनुभवों के साथ परिपक्वता`
    },
    nativeAge: factors.nativeAge,
    isYoungChild: factors.isYoungChild
  };
}

export const generatePersonalitySignature = extractPersonalitySignature;

// ----------------------------------------------------------------------------
// Comprehensive Astrological Factor Map for all 9 Life Areas (v5.14)
// ----------------------------------------------------------------------------

export const LIFE_AREA_FACTOR_MAPS: Record<LifeArea, LifeAreaFactorMap> = {
  career: {
    area: 'career',
    nameEn: 'Career, Vocation & Professional Path',
    nameHi: 'आजीविका, कार्यक्षेत्र एवं व्यवसाय',
    primaryBhavas: [10],
    secondaryBhavas: [6, 11, 2],
    primaryKarakas: ['Sun', 'Saturn', 'Mercury'],
    secondaryKarakas: ['Jupiter', 'Mars'],
    relevantDivisionalCharts: ['D10'],
    relevantYogaGroups: ['Raja Yoga', 'Dhana Yoga', 'Pancha Mahapurusha', 'Karma Alignment'],
    timingFactors: {
      dashaKeyHouses: [10, 11, 6, 2],
      dashaKeyPlanets: ['Sun', 'Saturn', 'Mercury'],
      transitTriggers: ['Saturn in 10H or over natal Moon (Sade Sati)', 'Jupiter transiting 10H or 11H']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Projects foundational presence and professional stamina through the Ascendant vessel.',
      inheritsLagnaTraitHi: 'लग्नानुसार कार्यक्षेत्र में स्थायित्व और प्रभाव की अभिव्यक्ति।',
      inheritsMoonTraitEn: 'Processes workplace challenges through the innate cognitive and emotional style.',
      inheritsMoonTraitHi: 'कार्यस्थल के तनाव और निर्णयों को चंद्र संस्कारों के माध्यम से साधना।',
      inheritsDominantEngineTraitEn: 'Channels highest-capacity planetary drive into strategic execution and leadership.',
      inheritsDominantEngineTraitHi: 'प्रमुख बली ग्रह की ऊर्जा को कार्यक्षेत्र में नेतृत्व और ठोस परिणामों में बदलना।',
      appliesWeakestVulnerabilityEn: 'Vigilant against overextending or miscommunicating in high-stakes professional situations.',
      appliesWeakestVulnerabilityHi: 'कमजोर ग्रह के प्रभाव से उत्पन्न होने वाले कार्यक्षेत्र के संकोच या असावधानी से बचाव।'
    }
  },
  wealth: {
    area: 'wealth',
    nameEn: 'Wealth, Financial Growth & Assets',
    nameHi: 'धन, आर्थिक समृद्धि एवं परिसंपत्तियां',
    primaryBhavas: [2, 11],
    secondaryBhavas: [5, 9, 4],
    primaryKarakas: ['Jupiter', 'Venus'],
    secondaryKarakas: ['Mercury', 'Sun'],
    relevantDivisionalCharts: ['D2', 'D4'],
    relevantYogaGroups: ['Dhana Yoga', 'Lakshmi Yoga', 'Vasumati Yoga', 'Indu Lagna Bindus'],
    timingFactors: {
      dashaKeyHouses: [2, 11, 5, 9],
      dashaKeyPlanets: ['Jupiter', 'Venus', 'Mercury'],
      transitTriggers: ['Jupiter transiting 2H or 11H', 'Rahu transiting 2H/8H axis']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Applies elemental temperament to asset accumulation, capital preservation, and risk appetite.',
      inheritsLagnaTraitHi: 'धन संचय और निवेश प्राथमिकताओं में लग्न स्वभाव का प्रभाव।',
      inheritsMoonTraitEn: 'Guides financial decisions through emotional security needs and Nakshatra discernment.',
      inheritsMoonTraitHi: 'आर्थिक सुरक्षा की आंतरिक आवश्यकता और सुविचारित निर्णय प्रक्रिया।',
      inheritsDominantEngineTraitEn: 'Leverages dominant planetary energy to generate scalable value and enduring assets.',
      inheritsDominantEngineTraitHi: 'प्रमुख ग्रह की क्षमता से धनार्जन के नए अवसर और स्थायी संपत्ति का निर्माण।',
      appliesWeakestVulnerabilityEn: 'Guards against impulsive financial commitments or ambiguous monetary agreements.',
      appliesWeakestVulnerabilityHi: 'वित्तीय समझौतों में अस्पष्टता या अनावश्यक जोखिम से बचाव।'
    }
  },
  marriage: {
    area: 'marriage',
    nameEn: 'Marriage, Partnerships & Relational Harmony',
    nameHi: 'वैवाहिक जीवन, साझेदारी एवं संबंध सामंजस्य',
    primaryBhavas: [7],
    secondaryBhavas: [2, 8, 12],
    primaryKarakas: ['Venus', 'Jupiter'],
    secondaryKarakas: ['Moon', 'Mars'],
    relevantDivisionalCharts: ['D9'],
    relevantYogaGroups: ['Kalatra Yoga', 'Upapada Lagna', 'Manglik Alignment', 'Darakaraka Placement'],
    timingFactors: {
      dashaKeyHouses: [7, 2, 8],
      dashaKeyPlanets: ['Venus', 'Jupiter'],
      transitTriggers: ['Jupiter aspecting 7H or Lagna', 'Saturn transiting 7H']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Maintains personal boundaries and interpersonal presence in close partnerships.',
      inheritsLagnaTraitHi: 'निकट संबंधों में अपनी गरिमा और आचरण का संतुलन बनाए रखना।',
      inheritsMoonTraitEn: 'Seeks emotional sanctuary, empathetic understanding, and mutual respect in bonding.',
      inheritsMoonTraitHi: 'रिश्तों में भावनात्मक सुरक्षा, आत्मीयता और पारस्परिक सम्मान की अपेक्षा।',
      inheritsDominantEngineTraitEn: 'Infuses relationships with positive executive strength and shared life vision.',
      inheritsDominantEngineTraitHi: 'साझेदारी में सकारात्मक ऊर्जा और साझा जीवन मूल्यों का संचार।',
      appliesWeakestVulnerabilityEn: 'Prevents resentment by practicing timely boundary setting and transparent communication.',
      appliesWeakestVulnerabilityHi: 'ससमय संवाद और स्पष्ट सीमाओं से संबंधों में कड़वाहट या गलतफहमी से बचाव।'
    }
  },
  progeny: {
    area: 'progeny',
    nameEn: 'Progeny, Lineage & Children',
    nameHi: 'संतान, कुल वृद्धि एवं संतति सुख',
    primaryBhavas: [5],
    secondaryBhavas: [9, 2],
    primaryKarakas: ['Jupiter'],
    secondaryKarakas: ['Sun', 'Mars', 'Venus'],
    relevantDivisionalCharts: ['D7'],
    relevantYogaGroups: ['Putra Yoga', 'Putrakaraka Alignment', 'Beeja/Kshetra Sphuta Bal'],
    timingFactors: {
      dashaKeyHouses: [5, 9, 2],
      dashaKeyPlanets: ['Jupiter'],
      transitTriggers: ['Jupiter transiting 5H or 9H', '5th Lord dasha activation']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Brings patience and parental grounding to family nurturing and lineage continuation.',
      inheritsLagnaTraitHi: 'पारिवारिक पालन-पोषण और संतान संस्कारों में धैर्य और स्थायित्व।',
      inheritsMoonTraitEn: 'Nurtures children with deep emotional attunement and Nakshatra wisdom.',
      inheritsMoonTraitHi: 'संतान के प्रति स्वाभाविक वात्सल्य, संवेदनशीलता और नैतिक मार्गदर्शन।',
      inheritsDominantEngineTraitEn: 'Provides structured guidance, educational encouragement, and enduring values.',
      inheritsDominantEngineTraitHi: 'संतान को अनुशासित मार्गदर्शन, सुदृढ़ शिक्षा और उच्च संस्कार प्रदान करना।',
      appliesWeakestVulnerabilityEn: 'Avoids projecting personal anxieties or rigid expectations onto progeny.',
      appliesWeakestVulnerabilityHi: 'संतान पर अपनी अनावश्यक अपेक्षाएं या चिंताएं थोपने से बचाव।'
    }
  },
  health: {
    area: 'health',
    nameEn: 'Vitality, Health Balance & Physical Well-Being',
    nameHi: 'स्वास्थ्य, शारीरिक ऊर्जा एवं दीर्घायु',
    primaryBhavas: [1, 6],
    secondaryBhavas: [8, 12],
    primaryKarakas: ['Sun', 'Mars', 'Saturn'],
    secondaryKarakas: ['Moon', 'Mercury'],
    relevantDivisionalCharts: ['D30'],
    relevantYogaGroups: ['Ayush Yoga', 'Arishta Yoga', 'Badhaka Configuration', 'Deha Bala'],
    timingFactors: {
      dashaKeyHouses: [1, 6, 8, 12],
      dashaKeyPlanets: ['Sun', 'Mars', 'Saturn'],
      transitTriggers: ['Saturn Ashtama/Kantaka Shani', 'Rahu-Ketu nodal return across 1H/7H or 6H/12H']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Anchors physical stamina, constitution resilience, and bodily vessel vitality.',
      inheritsLagnaTraitHi: 'शारीरिक गठन, रोग प्रतिरोधक क्षमता और स्वाभाविक जीवन शक्ति।',
      inheritsMoonTraitEn: 'Maintains nervous system balance and psychosomatic harmony.',
      inheritsMoonTraitHi: 'मानसिक शांति, संतुलित निद्रा और स्नायु तंत्र का सामंजस्य।',
      inheritsDominantEngineTraitEn: 'Channels vital physical drive into healthy circadian routines and regular discipline.',
      inheritsDominantEngineTraitHi: 'शारीरिक ऊर्जा को अनुशासित दिनचर्या और नियमित व्यायाम में नियोजित करना।',
      appliesWeakestVulnerabilityEn: 'Directs preventive care toward organs and body systems governed by the weakest planet.',
      appliesWeakestVulnerabilityHi: 'कमजोर ग्रह से संबंधित शारीरिक अंगों और आदतों पर विशेष सावधानी बरतना।'
    }
  },
  property: {
    area: 'property',
    nameEn: 'Property, Real Estate & Fixed Assets',
    nameHi: 'संपत्ति, भूमि, भवन एवं वाहन सुख',
    primaryBhavas: [4],
    secondaryBhavas: [11, 12],
    primaryKarakas: ['Mars', 'Venus', 'Saturn'],
    secondaryKarakas: ['Mercury', 'Jupiter'],
    relevantDivisionalCharts: ['D4'],
    relevantYogaGroups: ['Bhumi Yoga', 'Vahana Yoga', 'Griha Pravesha Yogas', 'Chaturthamsha Bal'],
    timingFactors: {
      dashaKeyHouses: [4, 11],
      dashaKeyPlanets: ['Mars', 'Venus'],
      transitTriggers: ['Jupiter aspecting 4H', 'Mars transiting 4H or 10H']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Approaches real estate and physical dwelling investments with methodical groundedness.',
      inheritsLagnaTraitHi: 'भूमि और अचल संपत्ति के क्रय-विक्रय में व्यावहारिक सूझबूझ।',
      inheritsMoonTraitEn: 'Selects living spaces that offer serene emotional sanctuary and psychological comfort.',
      inheritsMoonTraitHi: 'शांतिदायक, सुरक्षित और मनोनुकूल निवास स्थान का चयन।',
      inheritsDominantEngineTraitEn: 'Builds enduring family property assets that compound in security and aesthetic worth.',
      inheritsDominantEngineTraitHi: 'स्थायी और मूल्यवान परिसंपत्तियों का निर्माण जो पारिवारिक सुरक्षा को बढ़ाएं।',
      appliesWeakestVulnerabilityEn: 'Thoroughly scrutinizes legal deeds, land titles, and municipal permits to eliminate oversights.',
      appliesWeakestVulnerabilityHi: 'संपत्ति के दस्तावेजों, स्वामित्व और कानूनी औपचारिकताओं में पूरी सतर्कता।'
    }
  },
  education: {
    area: 'education',
    nameEn: 'Education, Higher Learning & Academic Mastery',
    nameHi: 'शिक्षा, उच्च विद्या एवं बौद्धिक विकास',
    primaryBhavas: [4, 5],
    secondaryBhavas: [9, 2],
    primaryKarakas: ['Mercury', 'Jupiter'],
    secondaryKarakas: ['Sun', 'Venus'],
    relevantDivisionalCharts: ['D24'],
    relevantYogaGroups: ['Saraswati Yoga', 'Budhaditya Yoga', 'Vidya Yoga', 'Chaturvimshamsha Bal'],
    timingFactors: {
      dashaKeyHouses: [4, 5, 9],
      dashaKeyPlanets: ['Mercury', 'Jupiter'],
      transitTriggers: ['Jupiter transiting 5H or 9H', 'Mercury solar conjunctions/returns']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Applies steady focus and disciplined study habits to foundational academics.',
      inheritsLagnaTraitHi: 'अध्ययन में नियमितता, एकाग्रता और सतत अभ्यास का दृष्टिकोण।',
      inheritsMoonTraitEn: 'Absorbs complex knowledge through intuitive comprehension and Nakshatra intellectual wiring.',
      inheritsMoonTraitHi: 'विषयों की गहन समझ, स्मृति और बौद्धिक जिज्ञासा का समुचित उपयोग।',
      inheritsDominantEngineTraitEn: 'Transforms theoretical learning into practical mastery, credentials, and real-world skills.',
      inheritsDominantEngineTraitHi: 'ज्ञान को केवल सैद्धांतिक न रखकर व्यावहारिक कौशल और प्रामाणिक योग्यता में बदलना।',
      appliesWeakestVulnerabilityEn: 'Avoids cognitive burnout, superficial skimming, or exam anxiety during peak evaluation cycles.',
      appliesWeakestVulnerabilityHi: 'परीक्षाओं के समय मानसिक तनाव या जल्दबाजी में बारीक गलतियों से बचाव।'
    }
  },
  foreignTravel: {
    area: 'foreignTravel',
    nameEn: 'Foreign Travel, Relocation & Distant Horizons',
    nameHi: 'विदेश यात्रा, दूरस्थ प्रवास एवं अंतरराष्ट्रीय अवसर',
    primaryBhavas: [9, 12],
    secondaryBhavas: [3, 7],
    primaryKarakas: ['Rahu', 'Moon', 'Saturn'],
    secondaryKarakas: ['Jupiter', 'Venus'],
    relevantDivisionalCharts: ['D9', 'D12'],
    relevantYogaGroups: ['Pravasa Yoga', 'Jala Yatra Yoga', 'Foreign Settlement Alignments'],
    timingFactors: {
      dashaKeyHouses: [9, 12, 3, 7],
      dashaKeyPlanets: ['Rahu', 'Moon'],
      transitTriggers: ['Rahu transiting 9H or 12H', 'Jupiter transiting 12H']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Adapts physical presence and routines gracefully when navigating foreign cultures and geographies.',
      inheritsLagnaTraitHi: 'नए वातावरण और विदेशी परिवेश में सहजता से सामंजस्य स्थापित करना।',
      inheritsMoonTraitEn: 'Balances natural attachment to home with the adventurous curiosity for distant horizons.',
      inheritsMoonTraitHi: 'जन्मभूमि के प्रति अपनत्व के साथ-साथ दूरस्थ स्थानों में नई संभावनाओं की खोज।',
      inheritsDominantEngineTraitEn: 'Turns overseas assignments and journeys into strategic avenues for worldly growth.',
      inheritsDominantEngineTraitHi: 'विदेशी यात्राओं और अवसरों को अपने समग्र विकास का माध्यम बनाना।',
      appliesWeakestVulnerabilityEn: 'Maintains rigorous attention to travel documentation, visas, and legal compliance.',
      appliesWeakestVulnerabilityHi: 'यात्रा दस्तावेजों, वीजा और स्थानीय नियमों के पालन में पूर्ण सावधानी।'
    }
  },
  spirituality: {
    area: 'spirituality',
    nameEn: 'Spirituality, Dharma & Inner Awakening',
    nameHi: 'अध्यात्म, धर्म एवं आत्म-जागृति',
    primaryBhavas: [9, 12],
    secondaryBhavas: [5, 8, 1],
    primaryKarakas: ['Jupiter', 'Ketu', 'Sun'],
    secondaryKarakas: ['Saturn', 'Moon'],
    relevantDivisionalCharts: ['D20', 'D9'],
    relevantYogaGroups: ['Dharma-Karmadhipati Yoga', 'Pravrajya Yoga', 'Moksha Trikona Alignment'],
    timingFactors: {
      dashaKeyHouses: [9, 12, 5, 8],
      dashaKeyPlanets: ['Jupiter', 'Ketu'],
      transitTriggers: ['Saturn transiting 12H (Vairagya trigger)', 'Jupiter transiting 9H or 5H']
    },
    personalitySynthesisFocus: {
      inheritsLagnaTraitEn: 'Anchors spiritual seekers into grounded ethics, non-dogmatic practice, and daily integrity.',
      inheritsLagnaTraitHi: 'अध्यात्म को आडंबर से दूर रखकर नैतिक जीवन और व्यावहारिक सत्यनिष्ठा पर आधारित करना।',
      inheritsMoonTraitEn: 'Experiences deep contemplation, devotional feeling, and meditation through intuitive peace.',
      inheritsMoonTraitHi: 'ध्यान, अंतर्मुखी चिंतन और मानसिक शांति का गहन अनुभव।',
      inheritsDominantEngineTraitEn: 'Integrates philosophical wisdom with everyday compassionate service to society.',
      inheritsDominantEngineTraitHi: 'उच्च आध्यात्मिक ज्ञान को समाज सेवा और परोपकार में रूपांतरित करना।',
      appliesWeakestVulnerabilityEn: 'Prevents spiritual escapism or abandoning worldly duties under emotional fatigue.',
      appliesWeakestVulnerabilityHi: 'कर्तव्यों से पलायन के बजाय संतुलन और कर्मयोग का मार्ग अपनाना।'
    }
  }
};

export function getLifeAreaFactorMap(area: LifeArea): LifeAreaFactorMap {
  return LIFE_AREA_FACTOR_MAPS[area] || LIFE_AREA_FACTOR_MAPS.career;
}

export function extractLifeAreaFactors(
  area: LifeArea,
  personality: PersonalitySignature,
  kundali: CompleteKundali
): ExtractedLifeAreaFactors {
  const factorMap = getLifeAreaFactorMap(area);
  const houses = kundali.houses || [];
  const planets = kundali.planets || [];
  const savBindus = kundali.ashtakvarga?.sarvashtakvarga || [];
  const factors = extractAndWeightFactors(kundali);
  const shadbala = kundali.shadbala || calculateShadbala(planets, houses, {
    birthDate: getBirthUtcDate(kundali.birthDetails),
    latitude: kundali.birthDetails.latitude,
    longitude: kundali.birthDetails.longitude
  });

  const mapHouseData = (hNum: number) => {
    const house = houses.find(h => h.houseNumber === hNum) || {
      houseNumber: hNum,
      sign: 'Aries',
      signLord: 'Mars'
    };
    const occupants = planets
      .filter(p => p.house === hNum)
      .map(p => p.name);
    const bindus = getSavByBhava(kundali.ashtakvarga, hNum, kundali.houses);
    return {
      houseNumber: hNum,
      sign: house.sign,
      signLord: house.signLord,
      savBindus: bindus,
      occupants
    };
  };

  const primaryHousesData = factorMap.primaryBhavas.map(mapHouseData);
  const secondaryHousesData = factorMap.secondaryBhavas.map(mapHouseData);

  const karakaStrengths = factorMap.primaryKarakas.map(pName => {
    const planetShad = shadbala.planets.find(sb => sb.planet.toLowerCase() === pName.toLowerCase());
    const rupas = planetShad?.totalRupas ?? 6.0;
    return {
      planet: pName,
      rupas,
      isStrong: rupas >= 6.0
    };
  });

  const mahadashaLord = factors.activeMahadasha || 'Jupiter';
  const antardashaLord = factors.activeAntardasha;
  const isPrimaryKaraka = factorMap.primaryKarakas.some(
    k => k.toLowerCase() === mahadashaLord.toLowerCase()
  );

  const activatedHouses: number[] = [];
  const allDomainHouses = [...factorMap.primaryBhavas, ...factorMap.secondaryBhavas];
  for (const hNum of allDomainHouses) {
    const hData = houses.find(h => h.houseNumber === hNum);
    if (hData && hData.signLord.toLowerCase() === mahadashaLord.toLowerCase()) {
      activatedHouses.push(hNum);
    }
  }

  return {
    area,
    factorMap,
    primaryHousesData,
    secondaryHousesData,
    karakaStrengths,
    activeDashaRelation: {
      mahadashaLord,
      antardashaLord,
      isPrimaryKaraka,
      activatesHouse: activatedHouses.length > 0,
      activatedHouseNumbers: activatedHouses
    },
    inheritedPersonalityContext: {
      lagnaSign: personality.lagnaArchetype.sign,
      lagnaLord: personality.lagnaArchetype.lord,
      moonSign: personality.emotionalCore.moonSign,
      moonNakshatra: personality.mindArchetype.nakshatra,
      dominantPlanet: personality.dominantExecutiveEngine.planet,
      weakestPlanet: personality.fundamentalVulnerability.planet,
      vulnerabilityFocusEn: factorMap.personalitySynthesisFocus.appliesWeakestVulnerabilityEn,
      vulnerabilityFocusHi: factorMap.personalitySynthesisFocus.appliesWeakestVulnerabilityHi
    }
  };
}

// ----------------------------------------------------------------------------
// Child Desh-Kaal-Patra Area Titles & Reinterpretations (v5.14 Step 3)
// ----------------------------------------------------------------------------

export const CHILD_LIFE_AREA_TITLES: Record<LifeArea, { nameEn: string; nameHi: string }> = {
  career: {
    nameEn: 'Talents & Aptitudes',
    nameHi: 'स्वाभाविक प्रतिभा एवं अभिरुचि'
  },
  wealth: {
    nameEn: 'Value Habits & Stewardship',
    nameHi: 'सद्गुण, संस्कार एवं साधनों का सदुपयोग'
  },
  marriage: {
    nameEn: 'Social & Peer Harmonization',
    nameHi: 'मित्रता, सामाजिक सामंजस्य एवं सहपाठी संबंध'
  },
  property: {
    nameEn: 'Sense of Home & Security',
    nameHi: 'गृह परिवेश, भावनात्मक सुरक्षा एवं सहज वातावरण'
  },
  health: {
    nameEn: 'Healthy Routines & Well-being',
    nameHi: 'दैनिक दिनचर्या, शारीरिक ऊर्जा एवं स्वास्थ्य संतुलन'
  },
  education: {
    nameEn: 'Learning & Curiosity',
    nameHi: 'जिज्ञासा, प्राथमिक शिक्षा एवं बौद्धिक विकास'
  },
  foreignTravel: {
    nameEn: 'Exploration & Adaptability',
    nameHi: 'नवीन अनुभव, यात्राएं एवं अनुकूलन क्षमता'
  },
  spirituality: {
    nameEn: 'Values, Wonder & Inner Awareness',
    nameHi: 'नैतिक मूल्य, जिज्ञासा एवं आत्मिक संस्कार'
  },
  progeny: {
    nameEn: 'Family Bonds & Nurturing',
    nameHi: 'पारिवारिक जुड़ाव, स्नेह एवं वात्सल्य'
  }
};

// ----------------------------------------------------------------------------
// Life Area Deep Dive Synthesis Engine (v5.14 Step 3 with Secondary Houses & Child Gating)
// ----------------------------------------------------------------------------

export function generateLifeAreaDeepDive(
  area: LifeArea,
  personality: PersonalitySignature,
  kundali: CompleteKundali,
  language: string = 'en'
): LifeAreaDeepDiveResult {
  const isHi = language === 'hi';
  const isChild = personality.isYoungChild;
  const childAge = personality.nativeAge ?? 6;
  const extracted = extractLifeAreaFactors(area, personality, kundali);
  const factorMap = extracted.factorMap;
  const factors = extractAndWeightFactors(kundali);

  const primaryH = extracted.primaryHousesData[0] || {
    houseNumber: 10,
    sign: 'Aries',
    signLord: 'Mars',
    savBindus: 28,
    occupants: []
  };

  const primaryH2 = extracted.primaryHousesData[1];
  const primaryLord = primaryH.signLord;
  const houseSAV = primaryH.savBindus;
  const primaryKaraka = factorMap.primaryKarakas[0] || 'Jupiter';
  const secH1 = extracted.secondaryHousesData[0];
  const secH2 = extracted.secondaryHousesData[1];
  const weakP = personality.fundamentalVulnerability.planet;

  // Title selection (Standard Adult vs. Developmental Child)
  const title = isChild 
    ? (isHi ? CHILD_LIFE_AREA_TITLES[area].nameHi : CHILD_LIFE_AREA_TITLES[area].nameEn)
    : (isHi ? factorMap.nameHi : factorMap.nameEn);

  // 1. Astrological Lens (Inherited from PersonalitySignature)
  let astLensEn = "";
  let astLensHi = "";

  if (isChild) {
    astLensEn = `Developmentally evaluated for childhood (${childAge} yrs) through the native's innate ${personality.lagnaArchetype.sign} (${personality.lagnaArchetype.archetypeEn}) foundation, guided by ${personality.mindArchetype.nakshatra} learning curiosity and nurtured by active ${personality.dominantExecutiveEngine.planet} growth energy.`;
    astLensHi = `बाल्यावस्था (${childAge} वर्ष) के विकास हेतु आपके बालक/बालिका के स्वाभाविक ${personality.lagnaArchetype.sign} (${personality.lagnaArchetype.archetypeHi}) स्वभाव, ${personality.mindArchetype.nakshatra} सीखने की जिज्ञासा एवं ${personality.dominantExecutiveEngine.planet} ऊर्जा के आधार पर विश्लेषित।`;
  } else {
    astLensEn = `Evaluated through your innate ${personality.lagnaArchetype.sign} (${personality.lagnaArchetype.archetypeEn}) foundation, guided by ${personality.mindArchetype.nakshatra} intellectual instincts and powered by an active ${personality.dominantExecutiveEngine.planet} executive engine.`;
    astLensHi = `आपके स्वाभाविक ${personality.lagnaArchetype.sign} (${personality.lagnaArchetype.archetypeHi}) आधार, ${personality.mindArchetype.nakshatra} बौद्धिक संस्कारों तथा प्रमुख ${personality.dominantExecutiveEngine.planet} ऊर्जा के दृष्टिकोण से विश्लेषित।`;
  }

  // 2. Baseline Capacity Synthesis (Synthesizing Primary + Secondary Bhavas, SAV, Karakas & Personality)
  let baselineEn = "";
  let baselineHi = "";

  if (isChild) {
    // ------------------------------------------------------------------------
    // Child Desh-Kaal-Patra Developmental Synthesis (No Adult Pressure/Outcomes)
    // ------------------------------------------------------------------------
    if (area === 'career') {
      baselineEn = `At this developmental stage (${childAge} years), the child's natural talents center on ${personality.mindArchetype.cognitiveStyleEn}. Governed by the 10th House (${primaryH.sign}, ruled by ${primaryLord}) alongside the 6th House of problem-solving (${secH1?.sign || 'Virgo'}) and 11th House of peer achievements (${secH2?.sign || 'Aquarius'}), the child thrives through hands-on creative experiments and joyful discovery rather than competitive pressure.`;
      baselineHi = `वर्तमान बाल्यावस्था (${childAge} वर्ष) में बच्चे की स्वाभाविक प्रतिभा ${personality.mindArchetype.cognitiveStyleHi} से प्रेरित है। दशम भाव (${primaryH.sign}, स्वामी: ${primaryLord}) के साथ षष्ठ भाव (${secH1?.sign || 'कन्या'}) और एकादश भाव (${secH2?.sign || 'कुंभ'}) का प्रभाव यह दर्शाता है कि किसी भी प्रकार के दबाव के बजाय खेल-खेल में समस्याओं को सुलझाना इनकी आंतरिक प्रतिभा को निखारेगा।`;
    } else if (area === 'wealth') {
      baselineEn = `Childhood foundation across the 2nd House of speech/habits (${primaryH.sign}, ${houseSAV} SAV bindus) and 11th House of sharing (${secH1?.sign || 'Pisces'}) focuses on building healthy stewardship. Supported by ${primaryKaraka}, the child naturally learns appreciation for family efforts, caring for toys and books, and cultivating cooperative gratitude.`;
      baselineHi = `द्वितीय भाव (${primaryH.sign}, ${houseSAV} बिंदु) एवं एकादश भाव (${secH1?.sign || 'मीन'}) के प्रभाव से बच्चे में साधनों के सदुपयोग और आदर के संस्कार विकसित होते हैं। ${primaryKaraka} के प्रभाव से बच्चा खिलौनों, पुस्तकों की संभाल और परिवार के परिश्रम का सम्मान करना स्वाभाविक रूप से सीखता है।`;
    } else if (area === 'marriage') {
      baselineEn = `Peer interaction and kindergarten/school bonding are shaped by the 7th House (${primaryH.sign}, ruled by ${primaryLord}) and 2nd House of family integration (${secH1?.sign || 'Taurus'}). The child seeks sincere friendships, fair play, and emotional safety in peer groups, guided by their ${personality.emotionalCore.moonSign} sensitivity.`;
      baselineHi = `सहपाठियों और मित्रों के साथ जुड़ाव सप्तम भाव (${primaryH.sign}, स्वामी: ${primaryLord}) एवं द्वितीय भाव (${secH1?.sign || 'वृषभ'}) से संचालित है। बच्चा अपने मित्र मंडली में निष्कपट खेल, बराबरी और भावनात्मक सुरक्षा पसंद करता है, जो उसके ${personality.emotionalCore.moonSign} चंद्र स्वभाव से पोषित है।`;
    } else if (area === 'property') {
      baselineEn = `The 4th House of home environment (${primaryH.sign}, ${houseSAV} SAV bindus) and 11th House of comfort (${secH1?.sign || 'Aries'}) highlight the child's need for a stable, peaceful domestic sanctuary. A clutter-free bedroom, predictable sleep schedule, and warm parental presence ground their emotional well-being.`;
      baselineHi = `चतुर्थ भाव (${primaryH.sign}, ${houseSAV} बिंदु) एवं एकादश भाव (${secH1?.sign || 'मेष'}) बच्चे के लिए शांत और सुरक्षित घरेलू परिवेश की आवश्यकता को रेखांकित करते हैं। एक व्यवस्थित कमरा, नियमित सोने का समय और माता-पिता का स्नेही सान्निध्य ही बच्चे को मानसिक शांति देता है।`;
    } else if (area === 'health') {
      baselineEn = `Physical vitality across the 1st House (${primaryH.sign}, ${houseSAV} SAV bindus) and 6th House of daily wellness (${secH1?.sign || 'Virgo'}) thrives on regular outdoor play, wholesome nutrition, and screen-time moderation. Balancing mental enthusiasm with restorative sleep keeps their constitution resilient.`;
      baselineHi = `शारीरिक ऊर्जा प्रथम भाव (${primaryH.sign}, ${houseSAV} बिंदु) एवं षष्ठ स्वास्थ्य भाव (${secH1?.sign || 'कन्या'}) से संचालित है। नियमित खेलकूद, पौष्टिक आहार और स्क्रीन से उचित दूरी इनके स्वास्थ्य को स्वाभाविक रूप से मजबूत बनाए रखेगी।`;
    } else if (area === 'education') {
      baselineEn = `Early learning across the 4th House of comprehension (${primaryH.sign}, ${houseSAV} SAV bindus) and 5th House of creative curiosity (${secH1?.sign || 'Leo'}, ruled by ${secH1?.signLord || 'Sun'}) is enhanced by ${primaryKaraka}. Supported by ${personality.mindArchetype.nakshatra} instincts, the child absorbs concepts rapidly through storytelling, visual diagrams, and affectionate encouragement.`;
      baselineHi = `प्राथमिक शिक्षा चतुर्थ भाव (${primaryH.sign}, ${houseSAV} बिंदु) एवं पंचम जिज्ञासा भाव (${secH1?.sign || 'सिंह'}) से संचालित है, जिसमें ${primaryKaraka} मुख्य कारक हैं। ${personality.mindArchetype.nakshatra} संस्कारों के कारण बच्चा कहानियों, चित्रों और आत्मीय प्रोत्साहन से विषयों को बहुत तेजी से सीखता है।`;
    } else if (area === 'foreignTravel') {
      baselineEn = `The 9th and 12th Bhavas (${primaryH.sign} and ${secH1?.sign || 'Pisces'}) indicate wonderful adaptability during family vacations and exposure to nature. The child's observant ${personality.lagnaArchetype.sign} nature embraces novel sights, languages, and surroundings with cheerful curiosity.`;
      baselineHi = `नवम एवं द्वादश भाव (${primaryH.sign} व ${secH1?.sign || 'मीन'}) पारिवारिक यात्राओं और नए स्थानों में बच्चे की सहज अनुकूलन क्षमता को दर्शाते हैं। बालक/बालिका का जिज्ञासु स्वभाव नई जगहों और दृश्यों को आनंद के साथ स्वीकार करता है।`;
    } else if (area === 'spirituality') {
      baselineEn = `Early moral awareness across the 9th House (${primaryH.sign}) and 5th House (${secH1?.sign || 'Leo'}) unfolds naturally through inspiring stories, empathy for animals, and family rituals. Nurturing simple values of truthfulness and compassion builds a lasting ethical foundation.`;
      baselineHi = `नैतिक संस्कार नवम भाव (${primaryH.sign}) और पंचम भाव (${secH1?.sign || 'सिंह'}) से सहज रूप से विकसित होते हैं। नीतिपरक कहानियों, जीवों के प्रति दया और पारिवारिक प्रार्थनाओं से बच्चे के मन में सत्य और करुणा के बीज अंकुरित होते हैं।`;
    } else {
      baselineEn = `Family harmony and sibling affection across the 5th House (${primaryH.sign}) and 2nd House of kutumbha (${secH1?.sign || 'Taurus'}) nurture deep generational security. Surrounded by gentle encouragement, the child develops heartwarming empathy and familial loyalty.`;
      baselineHi = `पारिवारिक सामंजस्य और भाई-बहनों का स्नेह पंचम भाव (${primaryH.sign}) एवं द्वितीय कुटुंब भाव (${secH1?.sign || 'वृषभ'}) से गहरा होता है। स्नेही परिवेश में बच्चा पारिवारिक मूल्यों और अपनत्व को आत्मसात करता है।`;
    }
  } else {
    // ------------------------------------------------------------------------
    // Adult Multi-House Astrological Synthesis (Primary + Secondary Bhavas)
    // ------------------------------------------------------------------------
    if (area === 'career') {
      const sec6TextEn = secH1 ? `, complemented by the 6th House of daily discipline and problem resolution in ${secH1.sign} (${secH1.savBindus} SAV bindus, ruled by ${secH1.signLord})` : '';
      const sec11TextEn = secH2 ? ` and the 11th House of milestone realization in ${secH2.sign} (${secH2.savBindus} SAV bindus)` : '';
      const sec6TextHi = secH1 ? `, षष्ठ भाव (${secH1.sign}, स्वामी: ${secH1.signLord}, अष्टकवर्ग: ${secH1.savBindus} बिंदु) की कार्यकुशलता` : '';
      const sec11TextHi = secH2 ? ` तथा एकादश भाव (${secH2.sign}, ${secH2.savBindus} बिंदु) की उपलब्धियों` : '';

      baselineEn = `Your vocational baseline is anchored in the 10th House (${primaryH.sign}, ruled by ${primaryLord}, ${houseSAV} SAV bindus)${sec6TextEn}${sec11TextEn}. Supported by natural career karaka ${primaryKaraka}, your ${personality.lagnaArchetype.element} outer presence and ${personality.dominantExecutiveEngine.planet} executive engine excel in structured, high-accountability leadership rather than speculative shortcuts.`;
      baselineHi = `आपका आजीविका तंत्र दशम कर्म भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${sec6TextHi}${sec11TextHi} से समन्वित है। मुख्य कारक ${primaryKaraka} की अनुकूलता, आपके ${personality.lagnaArchetype.element} तत्व के धैर्य और ${personality.dominantExecutiveEngine.planet} की कार्यकारी ऊर्जा के कारण आप दीर्घकालिक रणनीतिक उत्तरदायित्वों में श्रेष्ठ प्रदर्शन करते हैं।`;
    } else if (area === 'wealth') {
      const p2TextEn = primaryH2 ? ` and 11th House of liquid cashflow in ${primaryH2.sign} (${primaryH2.savBindus} SAV bindus)` : '';
      const sec5TextEn = secH1 ? `, fortified by the 5th House of strategic intellect in ${secH1.sign}` : '';
      const p2TextHi = primaryH2 ? ` तथा एकादश लाभ भाव (${primaryH2.sign}, ${primaryH2.savBindus} बिंदु)` : '';
      const sec5TextHi = secH1 ? `, जिसे पंचम लक्ष्मी स्थान (${secH1.sign}) का संबल प्राप्त है` : '';

      baselineEn = `Your financial architecture spans the 2nd House of accumulated assets (${primaryH.sign}, ruled by ${primaryLord}, ${houseSAV} SAV bindus)${p2TextEn}${sec5TextEn}, with ${primaryKaraka} acting as primary wealth karaka. Your natural ${personality.lagnaArchetype.sign} stability ensures compound capital preservation, while ${personality.dominantExecutiveEngine.planet} fuels systematic revenue expansion.`;
      baselineHi = `आपकी आर्थिक संरचना द्वितीय धन भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${p2TextHi}${sec5TextHi} पर आधारित है, जिसमें ${primaryKaraka} प्रमुख धनकारक हैं। आपकी स्वाभाविक ${personality.lagnaArchetype.sign} गंभीरता पूंजी सुरक्षा प्रदान करती है, जबकि ${personality.dominantExecutiveEngine.planet} आय स्रोतों के सतत विस्तार में सहायक है।`;
    } else if (area === 'marriage') {
      const sec2TextEn = secH1 ? `, supported by the 2nd House of family integration in ${secH1.sign} (${secH1.savBindus} SAV bindus)` : '';
      const sec8TextEn = secH2 ? ` and the 8th House of marital durability in ${secH2.sign}` : '';
      const sec2TextHi = secH1 ? `, द्वितीय कुटुंब भाव (${secH1.sign}, ${secH1.savBindus} बिंदु) के पारिवारिक सहयोग` : '';
      const sec8TextHi = secH2 ? ` तथा अष्टम भाव (${secH2.sign}) के वैवाहिक स्थायित्व` : '';

      baselineEn = `Your 7th House of partnership in ${primaryH.sign} (ruled by ${primaryLord}, ${houseSAV} SAV bindus)${sec2TextEn}${sec8TextEn} is filtered through your ${personality.emotionalCore.moonSign} emotional center. Supported by ${primaryKaraka}, you seek genuine reliability, mutual respect, and emotional sanctuary, harmonizing outer ${personality.lagnaArchetype.presenceEn} with authentic intimacy.`;
      baselineHi = `आपका सप्तम दांपत्य भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${sec2TextHi}${sec8TextHi} से युक्त होकर आपके ${personality.emotionalCore.moonSign} चंद्र मन से जुड़ा है। कारक ${primaryKaraka} के प्रभाव से आप संबंधों में निष्कपट विश्वसनीयता, गरिमा और भावनात्मक शांति को सर्वोच्च प्राथमिकता देते हैं।`;
    } else if (area === 'progeny') {
      const sec9TextEn = secH1 ? `, supported by the 9th House of generational dharma in ${secH1.sign} (${secH1.savBindus} SAV bindus)` : '';
      const sec2TextEn = secH2 ? ` and 2nd House of lineage in ${secH2.sign}` : '';
      const sec9TextHi = secH1 ? `, नवम धर्म भाव (${secH1.sign}, ${secH1.savBindus} बिंदु)` : '';
      const sec2TextHi = secH2 ? ` एवं द्वितीय कुल भाव (${secH2.sign}) के सहयोग` : '';

      baselineEn = `Your 5th House of progeny and lineage in ${primaryH.sign} (ruled by ${primaryLord}, ${houseSAV} SAV bindus)${sec9TextEn}${sec2TextEn} is energized by ${primaryKaraka} as natural Putra Karaka. Your parental nature brings steady warmth, intellectual encouragement, and generational grounding.`;
      baselineHi = `आपका पंचम संतान भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${sec9TextHi}${sec2TextHi} से पुष्ट होकर गुरु ${primaryKaraka} के प्रभाव में है। आपका वात्सल्य स्वाभाविक धैर्य, नैतिक मार्गदर्शन और बौद्धिक संस्कारों से परिपूर्ण है।`;
    } else if (area === 'health') {
      const sec8TextEn = secH1 ? `, balanced by the 8th House of longevity in ${secH1.sign} (${secH1.savBindus} SAV bindus)` : '';
      const sec12TextEn = secH2 ? ` and 12th House of restorative sleep in ${secH2.sign}` : '';
      const sec8TextHi = secH1 ? `, अष्टम आयु भाव (${secH1.sign}, ${secH1.savBindus} बिंदु)` : '';
      const sec12TextHi = secH2 ? ` एवं द्वादश विश्राम भाव (${secH2.sign})` : '';

      baselineEn = `Your bodily vessel is anchored across the 1st House of vitality (${primaryH.sign}, ruled by ${primaryLord}, ${houseSAV} SAV bindus) and 6th House of immunity/routine${sec8TextEn}${sec12TextEn}, overseen by ${primaryKaraka}. Your baseline constitution thrives on predictable circadian rhythms, balanced nutrition, and avoiding mental overstimulation from ${personality.dominantExecutiveEngine.planet} urgency.`;
      baselineHi = `आपकी शारीरिक ऊर्जा प्रथम देह भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु) एवं षष्ठ स्वास्थ्य भाव${sec8TextHi}${sec12TextHi} से संचालित है, जिसमें ${primaryKaraka} मुख्य कारक हैं। आपका स्वास्थ्य नियमित दिनचर्या, संतुलित खानपान और ${personality.dominantExecutiveEngine.planet} के अति-उत्साह पर नियंत्रण रखने से उत्तम बना रहता है।`;
    } else if (area === 'property') {
      const sec11TextEn = secH1 ? `, complemented by the 11th House of asset gains in ${secH1.sign} (${secH1.savBindus} SAV bindus)` : '';
      const sec12TextEn = secH2 ? ` and 12th House of peaceful residence in ${secH2.sign}` : '';
      const sec11TextHi = secH1 ? `, एकादश लाभ भाव (${secH1.sign}, ${secH1.savBindus} बिंदु)` : '';
      const sec12TextHi = secH2 ? ` एवं द्वादश निवास भाव (${secH2.sign})` : '';

      baselineEn = `Your 4th House of fixed assets, land, and vehicles in ${primaryH.sign} (ruled by ${primaryLord}, ${houseSAV} SAV bindus)${sec11TextEn}${sec12TextEn} is catalyzed by ${primaryKaraka} as property karaka. Your methodical ${personality.lagnaArchetype.sign} approach favors solid, legally verified, and appreciating physical holdings.`;
      baselineHi = `आपका चतुर्थ भूमि, भवन एवं वाहन भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${sec11TextHi}${sec12TextHi} से युक्त होकर भूमि कारक ${primaryKaraka} से संवर्धित है। आपका व्यावहारिक दृष्टिकोण सुरक्षित, कानूनी रूप से पुष्ट और मूल्यवान अचल संपत्तियों के निर्माण को प्राथमिकता देता है।`;
    } else if (area === 'education') {
      const p5TextEn = primaryH2 ? ` and 5th House of analytical intellect in ${primaryH2.sign} (${primaryH2.savBindus} SAV bindus)` : '';
      const sec9TextEn = secH1 ? `, elevated by the 9th House of higher scholarship in ${secH1.sign}` : '';
      const p5TextHi = primaryH2 ? ` तथा पंचम बुद्धि भाव (${primaryH2.sign}, ${primaryH2.savBindus} बिंदु)` : '';
      const sec9TextHi = secH1 ? `, जिसे नवम उच्च विद्या स्थान (${secH1.sign}) का मार्गदर्शन प्राप्त है` : '';

      baselineEn = `Your intellectual foundation spans the 4th House of comprehension in ${primaryH.sign} (ruled by ${primaryLord}, ${houseSAV} SAV bindus)${p5TextEn}${sec9TextEn}, guided by ${primaryKaraka}. Grounded by ${personality.mindArchetype.nakshatra} focus, you absorb complex concepts systematically and convert theoretical study into practical mastery.`;
      baselineHi = `आपकी शैक्षणिक नींव चतुर्थ विद्या भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${p5TextHi}${sec9TextHi} पर टिकी है, जिसमें ${primaryKaraka} मुख्य ज्ञानकारक हैं। ${personality.mindArchetype.nakshatra} संस्कारों से युक्त होकर आप जटिल विषयों को सुव्यवस्थित रूप से सीखकर व्यावहारिक दक्षता हासिल करते हैं।`;
    } else if (area === 'foreignTravel') {
      const p12TextEn = primaryH2 ? ` and 12th House of distant relocation in ${primaryH2.sign} (${primaryH2.savBindus} SAV bindus)` : '';
      const sec3TextEn = secH1 ? `, supported by the 3rd House of journey initiative in ${secH1.sign}` : '';
      const p12TextHi = primaryH2 ? ` तथा द्वादश विदेश प्रवास भाव (${primaryH2.sign}, ${primaryH2.savBindus} बिंदु)` : '';
      const sec3TextHi = secH1 ? `, जिसे तृतीय यात्रा भाव (${secH1.sign}) का संबल प्राप्त है` : '';

      baselineEn = `Your distant horizons and foreign opportunities span the 9th House of international travel (${primaryH.sign}, ruled by ${primaryLord}, ${houseSAV} SAV bindus)${p12TextEn}${sec3TextEn}, catalyzed by ${primaryKaraka}. Your adaptive ${personality.lagnaArchetype.sign} presence navigates unfamiliar cultures with composed self-assurance.`;
      baselineHi = `दूरस्थ यात्राओं और विदेशी अवसरों का नवम भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${p12TextHi}${sec3TextHi} से समन्वित है, जिसमें ${primaryKaraka} मुख्य कारक हैं। आपका संतुलित ${personality.lagnaArchetype.sign} स्वभाव नए परिवेश और संस्कृतियों में सहज सामंजस्य बनाता है।`;
    } else {
      const p12TextEn = primaryH2 ? ` and 12th House of Moksha and inner surrender in ${primaryH2.sign} (${primaryH2.savBindus} SAV bindus)` : '';
      const sec5TextEn = secH1 ? `, deepened by the 5th House of spiritual discernment in ${secH1.sign}` : '';
      const p12TextHi = primaryH2 ? ` तथा द्वादश मोक्ष एवं ध्यान भाव (${primaryH2.sign}, ${primaryH2.savBindus} बिंदु)` : '';
      const sec5TextHi = secH1 ? `, जिसे पंचम पूर्व-पुण्य भाव (${secH1.sign}) की गहराई प्राप्त है` : '';

      baselineEn = `Your Dharmic and spiritual evolution spans the 9th House of philosophy in ${primaryH.sign} (ruled by ${primaryLord}, ${houseSAV} SAV bindus)${p12TextEn}${sec5TextEn}, guided by ${primaryKaraka}. Influenced by ${personality.mindArchetype.nakshatra} wisdom, your highest growth unfolds through principled mentorship, ethical integrity, and continuous self-mastery.`;
      baselineHi = `आपका आध्यात्मिक विकास नवम धर्म भाव (${primaryH.sign}, स्वामी: ${primaryLord}, ${houseSAV} बिंदु)${p12TextHi}${sec5TextHi} से आलोकित है, जिसमें ${primaryKaraka} प्रमुख मार्गदर्शक हैं। ${personality.mindArchetype.nakshatra} संस्कारों से प्रेरित होकर, आपका आत्मिक विकास नैतिक आचरण, ज्ञान संवर्धन और आत्म-संयम से निरंतर प्रदीप्त होता है।`;
    }
  }

  // 3. Active Timing Synthesis (Strict separation: Inherent capacity vs. Vimshottari temporal window)
  let timingEn = "";
  let timingHi = "";

  const dashaRel = extracted.activeDashaRelation;
  const activatedHNames = dashaRel.activatedHouseNumbers.length > 0 
    ? (isHi ? `भाव ${dashaRel.activatedHouseNumbers.join(', ')}` : `Houses ${dashaRel.activatedHouseNumbers.join(', ')}`)
    : (isHi ? `${primaryH.sign} क्षेत्र` : `your ${primaryH.sign} domain`);

  if (isChild) {
    timingEn = `Currently navigating the ${factors.activeMahadasha} Mahadasha ${factors.activeAntardasha ? `(Antardasha: ${factors.activeAntardasha})` : ''}. In childhood, this planetary cycle gently shapes the child's developmental rhythm, school adjustment, and social curiosity without imposing adult benchmarks.`;
    timingHi = `वर्तमान में बालक/बालिका ${factors.activeMahadasha} महादशा ${factors.activeAntardasha ? `(अंतर्दशा: ${factors.activeAntardasha})` : ''} के प्रभाव में हैं। बाल्यावस्था में यह दशा चक्र बिना किसी दबाव के बच्चे के सीखने की गति, स्कूल परिवेश और स्वाभाविक विकास को संवारता है।`;
  } else {
    timingEn = `Currently navigating the ${factors.activeMahadasha} Mahadasha ${factors.activeAntardasha ? `(Antardasha: ${factors.activeAntardasha})` : ''}. This planetary period actively stimulates ${activatedHNames}${dashaRel.isPrimaryKaraka ? ` while resonating with natural ${factors.activeMahadasha} domain significations` : ''}, creating favorable timing windows for structured milestone realization when aligned with disciplined effort.`;
    timingHi = `वर्तमान में आप ${factors.activeMahadasha} महादशा ${factors.activeAntardasha ? `(अंतर्दशा: ${factors.activeAntardasha})` : ''} के प्रभाव में हैं। यह समयावधि विशेष रूप से ${activatedHNames} को सक्रिय करती है${dashaRel.isPrimaryKaraka ? ` तथा ${factors.activeMahadasha} के स्वाभाविक कारकत्व को बल देती है` : ''}, जिससे सुविचारित और निरंतर प्रयासों द्वारा महत्वपूर्ण उपलब्धियां प्राप्त की जा सकती हैं।`;
  }

  // 4. Risk Mitigation (Applying weakest planet vulnerability)
  let riskEn = "";
  let riskHi = "";

  if (isChild) {
    if (weakP === 'Mars') {
      riskEn = `Gently encourage patience during playful challenges; avoid sudden frustration when physical tasks require repeated attempts.`;
      riskHi = `खेलकूद या नए कामों में बच्चे को धैर्य रखने के लिए प्यार से प्रेरित करें; किसी काम में समय लगने पर डांटने के बजाय संबल दें।`;
    } else if (weakP === 'Mercury') {
      riskEn = `Support unhurried verbal expression; give the child ample time to share their thoughts without finishing their sentences.`;
      riskHi = `बच्चे को अपनी बात पूरी कहने का पूरा समय दें; बातचीत में जल्दबाजी या बीच में टोकने से बचें ताकि उनका आत्मविश्वास बढ़े।`;
    } else if (weakP === 'Saturn') {
      riskEn = `Avoid imposing rigid time benchmarks on learning; unhurried exploration and positive reinforcement yield the best results.`;
      riskHi = `सीखने में बहुत सख्त समय सीमा न बनाएं; सहज गति और सकारात्मक प्रोत्साहन से ही बच्चा सबसे अच्छा सीखता है।`;
    } else if (weakP === 'Sun') {
      riskEn = `Build quiet self-worth through sincere appreciation of effort rather than comparing the child with peers.`;
      riskHi = `अन्य बच्चों से तुलना करने के बजाय बच्चे के व्यक्तिगत प्रयासों की सराहना करके उसका आत्मसम्मान मजबूत करें।`;
    } else {
      riskEn = `Ensure predictable daily sleep and quiet downtime away from overstimulating screens to protect emotional calm.`;
      riskHi = `शांत नींद और अत्यधिक डिजिटल स्क्रीन से दूरी बनाकर बच्चे के संवेदनशील मन को तरोताजा और शांत रखें।`;
    }
  } else {
    if (weakP === 'Mars') {
      riskEn = `Watch for hesitation in boundary enforcement or delaying necessary professional/financial confrontation. Do not concede vital terms simply to avoid temporary discomfort.`;
      riskHi = `समय पर अपनी सीमाएं स्पष्ट न करने या टकराव टालने के लिए जरूरी बातों में ढील देने से बचें। अपने हितों की रक्षा में संकोच न करें।`;
    } else if (weakP === 'Mercury') {
      riskEn = `Guard against ambiguous verbal commitments or detail fatigue in contractual fine print. Insist on clear written documentation before proceeding.`;
      riskHi = `अस्पष्ट मौखिक आश्वासनों या कागजी औपचारिकताओं में जल्दबाजी से बचें। सभी महत्वपूर्ण निर्णयों को लिखित रूप में ही अंतिम रूप दें।`;
    } else if (weakP === 'Saturn') {
      riskEn = `Prevent impatience or frustration during slow, repetitive administrative groundwork. Consistent daily compound progress is your true safeguard.`;
      riskHi = `धीमी और नीरस प्रक्रियाओं में धैर्य न खोएं। प्रतिदिन का निरंतर अभ्यास ही आपको स्थायी परिणाम देगा।`;
    } else if (weakP === 'Sun') {
      riskEn = `Avoid undervaluing your contributions or hesitating to claim rightful visibility before senior stakeholders.`;
      riskHi = `अपनी योग्यता और योगदान को कम आंकने से बचें। महत्वपूर्ण अवसरों पर आत्मविश्वास के साथ अपनी बात रखें।`;
    } else {
      riskEn = `Maintain disciplined self-care and avoid over-extending personal resources to satisfy external expectations.`;
      riskHi = `दूसरों की अपेक्षाओं को पूरा करने के चक्कर में अपनी ऊर्जा और प्राथमिकताओं को संकट में न डालें।`;
    }
  }

  // 5. Action Roadmap: 3 Directives + Remedial Focus
  let dir1 = "";
  let dir2 = "";
  let dir3 = "";
  let remedialFocus = "";

  if (isChild) {
    dir1 = isHi ? 'रचनात्मक खेल, चित्रकारी और जिज्ञासा-भरे प्रश्नों को दैनिक रूप से प्रोत्साहित करें।' : 'Encourage open-ended curiosity, creative play, and hands-on discovery daily.';
    dir2 = isHi ? `कमजोर ${weakP} के प्रभाव को संतुलित करने के लिए स्नेही और धैर्यवान मार्गदर्शन दें।` : `Provide warm, patient support to balance ${weakP} developmental sensitivity.`;
    dir3 = isHi ? 'परिणाम के बजाय बच्चे के सीखने के प्रयास और सीखने के आनंद की सराहना करें।' : 'Celebrate incremental effort, good habits, and the joy of learning over rigid perfection.';
    remedialFocus = isHi 
      ? 'माता-पिता का स्नेही सान्निध्य, नियमित दिनचर्या और शांत पारिवारिक वातावरण ही बच्चे का सर्वोत्तम संबल है।'
      : 'A warm, affectionate home environment, consistent sleep routines, and joyful play are completely sufficient.';
  } else {
    dir1 = isHi ? 'दैनिक प्राथमिकताओं को स्पष्ट और लिखित रूप में व्यवस्थित करें।' : 'Anchor key domain objectives into clear, written daily milestones.';
    dir2 = isHi ? `कमजोर ${weakP} के प्रभाव को संतुलित करने के लिए सक्रिय सतर्कता बरतें।` : `Apply targeted vigilance to mitigate ${weakP} blind spots in this life area.`;
    dir3 = isHi ? `सक्रिय ${factors.activeMahadasha} दशा की ऊर्जा का लाभ उठाकर दीर्घकालिक निर्माण करें।` : `Leverage active ${factors.activeMahadasha} dasha momentum for compounding long-term growth.`;
    remedialFocus = isHi 
      ? 'दैनिक अनुशासन, संतुलित दिनचर्या और नैतिक स्पष्टता ही आपका सर्वोत्तम उपाय है।'
      : 'Maintaining structured daily routines, ethical clarity, and mindful focus is completely sufficient.';
  }

  return {
    area,
    title,
    astrologicalLens: isHi ? astLensHi : astLensEn,
    baselineCapacity: baselineHi || baselineEn,
    activeTimingInfluence: isHi ? timingHi : timingEn,
    riskMitigation: isHi ? riskHi : riskEn,
    actionRoadmap: {
      directives: [dir1, dir2, dir3],
      remedialFocus
    },
    extractedFactors: extracted
  };
}
